import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    Copy,
    Clock3,
    Download,
    Play,
    ChevronDown,
    ChevronUp,
    Loader,
    CheckCircle,
    AlertCircle,
    FileCode,
    Zap,
    Filter,
    BookOpen,
    X,
    Lightbulb,
    AlertTriangle,
    Pencil,
    History,
    Save,
    Github,
    ExternalLink,
    Maximize2,
    Image as ImageIcon,
    Video,
    Link2,
    List,
    Plus,
    RefreshCw,
    RotateCcw,
    Trash2,
    Wand2,
    ArrowLeft
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import { cancelJob, loadJobResult, retryJob, submitJobOperation, waitForJobResult } from '../../services/jobs';
import { CodeEditorPreview } from '../shared/CodeEditorPreview';
import { GithubExportModal } from '../modals/GithubExportModal';
import { getScriptStageTheme } from '../../config/scriptStageTheme';

const VERSIONING_ENABLED = false;
const MAX_SAVED_VERSIONS = 1;
const MAX_SCRIPT_GENERATION_CASES = 10;
const ASSOCIATED_POMS_REQUEST_TIMEOUT_MS = 30000;
const ASSOCIATED_POMS_RICH_PREVIEW_CHAR_LIMIT = 15000;
const ASSOCIATED_POMS_RICH_PREVIEW_LINE_LIMIT = 350;
const PLAYWRIGHT_EXPORT_ROOT = 'playwright-automation';
const PLAYWRIGHT_EXPORT_README = `# Playwright Automation

This project was exported from generated test scripts.

## Run

\`\`\`bash
npm install
npm test
\`\`\`

## Structure

- \`tests/ui\` contains UI Playwright specs.
- \`tests/api\` contains API Playwright specs.
- \`pages\` contains exported Page Object Model files when available.
- \`data\` contains test data, run metadata, and export metadata.
`;
const PLAYWRIGHT_EXPORT_GITIGNORE = `node_modules/
playwright-report/
test-results/
.env
`;
const PLAYWRIGHT_EXPORT_PACKAGE_JSON = `{
  "name": "exported-playwright-automation",
  "version": "1.0.0",
  "private": true,
  "scripts": {
    "test": "playwright test",
    "test:headed": "playwright test --headed",
    "report": "playwright show-report"
  },
  "devDependencies": {
    "@playwright/test": "latest"
  }
}
`;
const PLAYWRIGHT_EXPORT_CONFIG_TS = `import { defineConfig, devices } from '@playwright/test';

export default defineConfig({
  testDir: './tests',
  fullyParallel: false,
  reporter: [['html'], ['list']],
  use: {
    trace: 'on-first-retry',
    screenshot: 'only-on-failure',
    video: 'retain-on-failure',
  },
  projects: [
    {
      name: 'chromium',
      use: { ...devices['Desktop Chrome'] },
    },
  ],
});
`;
const TESTSUITE_HOOKS_TS = `import { test } from '@playwright/test';

const __testsuiteStartScreenshotCaptured = new WeakSet();
const __testsuiteStartScreenshotActions = new Set(['goto', 'reload', 'goBack', 'goForward']);

const __testsuiteWaitForScreenshotReady = async (page) => {
  if (!page || page.isClosed?.()) return false;
  const url = String(page.url?.() || '');
  if (!url || url === 'about:blank') return false;
  await page.waitForLoadState('domcontentloaded', { timeout: 5000 }).catch(() => null);
  await page.waitForLoadState('networkidle', { timeout: 2000 }).catch(() => null);
  await page.locator('body').waitFor({ state: 'visible', timeout: 5000 }).catch(() => null);
  await page.evaluate(() => new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  })).catch(() => null);
  return true;
};

const __testsuiteHasScreenshotContent = async (page) => {
  if (!(await __testsuiteWaitForScreenshotReady(page))) return false;
  return await page.evaluate(() => {
    const body = document.body;
    if (!body) return false;
    const bodyStyle = window.getComputedStyle(body);
    if (bodyStyle.visibility === 'hidden' || bodyStyle.display === 'none' || Number(bodyStyle.opacity) === 0) {
      return false;
    }
    const hasText = Boolean((body.innerText || '').trim());
    const visibleElementSelector = 'img,svg,canvas,video,input,button,select,textarea,a,[role],table';
    const hasVisibleElement = Array.from(document.querySelectorAll(visibleElementSelector)).some((element) => {
      const rect = element.getBoundingClientRect();
      const elementStyle = window.getComputedStyle(element);
      return rect.width > 0
        && rect.height > 0
        && elementStyle.visibility !== 'hidden'
        && elementStyle.display !== 'none'
        && Number(elementStyle.opacity) !== 0;
    });
    return hasText || hasVisibleElement;
  }).catch(() => true);
};

const __captureScreen = async (page, testInfo, phase) => {
  if (!(await __testsuiteHasScreenshotContent(page))) return false;
  const safeTitle = String(testInfo?.title || 'test').replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 80);
  const fileName = \`\${safeTitle}-\${phase}-\${Date.now()}.png\`;
  const screenshotPath = testInfo.outputPath(fileName);
  await page.screenshot({ path: screenshotPath, fullPage: true });
  await testInfo.attach(\`screen-\${phase}\`, { path: screenshotPath, contentType: 'image/png' });
  return true;
};

const __captureStartAfterAction = async (page, testInfo, method) => {
  if (!testInfo || __testsuiteStartScreenshotCaptured.has(page)) return;
  if (!__testsuiteStartScreenshotActions.has(method)) return;
  const captured = await __captureScreen(page, testInfo, 'start');
  if (captured) __testsuiteStartScreenshotCaptured.add(page);
};

globalThis.__testsuiteCaptureStartAfterAction = __captureStartAfterAction;

const __looksLikeAuthUrl = (value) => {
  const text = String(value || '').toLowerCase();
  return /(^|[\\/#?&])(login|log-in|signin|sign-in|auth|authenticate|session-expired|session_expired)([\\/#?&=]|$)/.test(text);
};

const __looksLikeAuthText = (value) => {
  const text = String(value || '').toLowerCase();
  return /(unauthorized|forbidden|session expired|session timeout|token expired|jwt expired|invalid token|invalid jwt|please sign in|login required|sign in to continue)/.test(text);
};

test.beforeEach(async ({ page }, testInfo) => {
  page.on('response', async (response) => {
    const status = response.status();
    if (status === 401 || status === 403) {
      console.log('__PW_AUTH_SIGNAL__ ' + JSON.stringify({ type: 'http-status', status, url: response.url() }));
    }
  });
  const captured = await __captureScreen(page, testInfo, 'start');
  if (captured) __testsuiteStartScreenshotCaptured.add(page);
});

test.afterEach(async ({ page }, testInfo) => {
  const status = testInfo?.status || 'unknown';
  let finalTitle = '';
  try {
    finalTitle = await page.title();
  } catch (_err) {
    finalTitle = '';
  }
  const finalUrl = page.url();
  console.log('__PW_FINAL_STATE__ ' + JSON.stringify({ url: finalUrl, title: finalTitle, status }));
  if (__looksLikeAuthUrl(finalUrl) || __looksLikeAuthText(finalTitle)) {
    console.log('__PW_AUTH_SIGNAL__ ' + JSON.stringify({ type: 'final-state', url: finalUrl, title: finalTitle, status }));
  }
  await __captureScreen(page, testInfo, \`end-\${status}\`);
});

const __testsuiteActionDelayMs = Math.max(
  0,
  Number.parseInt(String(process.env.TESTSUITE_ACTION_DELAY_MS || '1000'), 10) || 0,
);

const __testsuiteActionDelay = async () => {
  if (__testsuiteActionDelayMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, __testsuiteActionDelayMs));
  }
};

const __patchDelayedAction = (sample, method, captureStartAfterAction = false) => {
  const proto = sample ? Object.getPrototypeOf(sample) : null;
  if (!proto || typeof proto[method] !== 'function') return;
  const original = proto[method];
  if (original.__testsuiteActionDelayWrapped) return;
  const wrapped = async function (...args) {
    const result = await original.apply(this, args);
    if (captureStartAfterAction) {
      const captureStart = globalThis.__testsuiteCaptureStartAfterAction;
      if (typeof captureStart === 'function') {
        await captureStart(this, this.__testsuiteActionDelayTestInfo, method);
      }
    }
    await __testsuiteActionDelay();
    return result;
  };
  wrapped.__testsuiteActionDelayWrapped = true;
  proto[method] = wrapped;
};

const __installActionDelayHooks = (page) => {
  const locator = page.locator('body');
  [
    'click', 'dblclick', 'fill', 'clear', 'check', 'uncheck',
    'selectOption', 'setInputFiles', 'press', 'type', 'hover',
    'tap', 'dragTo'
  ].forEach((method) => __patchDelayedAction(locator, method));
  [
    'goto', 'reload', 'goBack', 'goForward', 'click', 'dblclick',
    'fill', 'check', 'uncheck', 'selectOption', 'setInputFiles',
    'press', 'type', 'hover'
  ].forEach((method) => __patchDelayedAction(page, method, true));
  if (page.keyboard) ['press', 'type', 'insertText'].forEach((method) => __patchDelayedAction(page.keyboard, method));
  if (page.mouse) ['click', 'dblclick', 'down', 'up', 'move', 'wheel'].forEach((method) => __patchDelayedAction(page.mouse, method));
  if (page.touchscreen) ['tap'].forEach((method) => __patchDelayedAction(page.touchscreen, method));
};

test.beforeEach(async ({ page }, testInfo) => {
  page.__testsuiteActionDelayTestInfo = testInfo;
  __installActionDelayHooks(page);
});
`;

const textEncoder = new TextEncoder();
const crcTable = (() => {
    const table = new Uint32Array(256);
    for (let i = 0; i < 256; i += 1) {
        let c = i;
        for (let k = 0; k < 8; k += 1) {
            c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
        }
        table[i] = c >>> 0;
    }
    return table;
})();

const crc32 = (bytes) => {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i += 1) {
        crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
};

const writeUInt16 = (buffer, offset, value) => {
    buffer[offset] = value & 0xff;
    buffer[offset + 1] = (value >>> 8) & 0xff;
};

const writeUInt32 = (buffer, offset, value) => {
    buffer[offset] = value & 0xff;
    buffer[offset + 1] = (value >>> 8) & 0xff;
    buffer[offset + 2] = (value >>> 16) & 0xff;
    buffer[offset + 3] = (value >>> 24) & 0xff;
};

const concatUint8Arrays = (parts) => {
    const totalLength = parts.reduce((sum, part) => sum + part.length, 0);
    const output = new Uint8Array(totalLength);
    let offset = 0;
    parts.forEach((part) => {
        output.set(part, offset);
        offset += part.length;
    });
    return output;
};

const createZipBlob = (files = []) => {
    const now = new Date();
    const dosTime = ((now.getHours() & 0x1f) << 11) | ((now.getMinutes() & 0x3f) << 5) | ((Math.floor(now.getSeconds() / 2)) & 0x1f);
    const dosDate = (((now.getFullYear() - 1980) & 0x7f) << 9) | (((now.getMonth() + 1) & 0x0f) << 5) | (now.getDate() & 0x1f);
    const localParts = [];
    const centralParts = [];
    let offset = 0;

    files.forEach((file) => {
        const nameBytes = textEncoder.encode(file.path);
        const dataBytes = file.content instanceof Uint8Array ? file.content : textEncoder.encode(String(file.content ?? ''));
        const checksum = crc32(dataBytes);

        const localHeader = new Uint8Array(30 + nameBytes.length);
        writeUInt32(localHeader, 0, 0x04034b50);
        writeUInt16(localHeader, 4, 20);
        writeUInt16(localHeader, 6, 0x0800);
        writeUInt16(localHeader, 8, 0);
        writeUInt16(localHeader, 10, dosTime);
        writeUInt16(localHeader, 12, dosDate);
        writeUInt32(localHeader, 14, checksum);
        writeUInt32(localHeader, 18, dataBytes.length);
        writeUInt32(localHeader, 22, dataBytes.length);
        writeUInt16(localHeader, 26, nameBytes.length);
        writeUInt16(localHeader, 28, 0);
        localHeader.set(nameBytes, 30);
        localParts.push(localHeader, dataBytes);

        const centralHeader = new Uint8Array(46 + nameBytes.length);
        writeUInt32(centralHeader, 0, 0x02014b50);
        writeUInt16(centralHeader, 4, 20);
        writeUInt16(centralHeader, 6, 20);
        writeUInt16(centralHeader, 8, 0x0800);
        writeUInt16(centralHeader, 10, 0);
        writeUInt16(centralHeader, 12, dosTime);
        writeUInt16(centralHeader, 14, dosDate);
        writeUInt32(centralHeader, 16, checksum);
        writeUInt32(centralHeader, 20, dataBytes.length);
        writeUInt32(centralHeader, 24, dataBytes.length);
        writeUInt16(centralHeader, 28, nameBytes.length);
        writeUInt16(centralHeader, 30, 0);
        writeUInt16(centralHeader, 32, 0);
        writeUInt16(centralHeader, 34, 0);
        writeUInt16(centralHeader, 36, 0);
        writeUInt32(centralHeader, 38, 0);
        writeUInt32(centralHeader, 42, offset);
        centralHeader.set(nameBytes, 46);
        centralParts.push(centralHeader);

        offset += localHeader.length + dataBytes.length;
    });

    const centralDirectory = concatUint8Arrays(centralParts);
    const endRecord = new Uint8Array(22);
    writeUInt32(endRecord, 0, 0x06054b50);
    writeUInt16(endRecord, 4, 0);
    writeUInt16(endRecord, 6, 0);
    writeUInt16(endRecord, 8, files.length);
    writeUInt16(endRecord, 10, files.length);
    writeUInt32(endRecord, 12, centralDirectory.length);
    writeUInt32(endRecord, 16, offset);
    writeUInt16(endRecord, 20, 0);

    return new Blob([...localParts, centralDirectory, endRecord], { type: 'application/zip' });
};

const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const element = document.createElement('a');
    element.href = url;
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const normalizeExportFileSegment = (value = '', fallback = 'file') => {
    const normalized = String(value || '')
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .replace(/[^a-zA-Z0-9._-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();
    return normalized || fallback;
};

const rewriteExportedPomImports = (script = '', poms = []) => {
    const targets = new Map();
    (Array.isArray(poms) ? poms : []).forEach((pom, index) => {
        const pageName = String(pom?.pageName || '').trim();
        if (!pageName) return;
        const pomBaseName = normalizeExportFileSegment(pageName, `pom-${index + 1}`);
        const pomScopePrefix = pom?.scope && pom.scope !== 'local' ? `${pom.scope}-` : '';
        const target = `../../pages/${pomScopePrefix}${pomBaseName}`;
        targets.set(pageName.toLocaleLowerCase(), target);
        targets.set(pomBaseName.toLocaleLowerCase(), target);
    });

    if (!targets.size) return String(script || '');

    return String(script || '').replace(
        /(\bfrom\s+['"])([^'"]+)(['"]\s*;?)/g,
        (match, prefix, importPath, suffix) => {
            const fileName = String(importPath || '').split('/').pop() || '';
            const importName = fileName.replace(/\.(?:pom|ts|tsx|js|jsx)$/i, '');
            const target = targets.get(importName.toLocaleLowerCase());
            return target ? `${prefix}${target}${suffix}` : match;
        }
    );
};

const buildCaseExportName = (testCase = {}, index = 0) => {
    const id = String(testCase.test_case_no || testCase.testcase_no || testCase.id || `case-${index + 1}`).trim();
    const title = String(testCase.test_description || testCase.test_name || testCase.scenario || testCase.title || '').trim();
    const prefix = normalizeExportFileSegment(id, `case-${index + 1}`);
    const suffix = normalizeExportFileSegment(title, '');
    return suffix ? `${prefix}-${suffix}`.slice(0, 96) : prefix.slice(0, 96);
};

const lineCountForText = (value = '') => Math.max(1, String(value || '').split('\n').length);

const shouldUsePlainAssociatedPomPreview = (value = '') => {
    const text = String(value || '');
    return (
        text.length > ASSOCIATED_POMS_RICH_PREVIEW_CHAR_LIMIT ||
        lineCountForText(text) > ASSOCIATED_POMS_RICH_PREVIEW_LINE_LIMIT
    );
};

/**
 * Missing POM elements for one test case.
 *
 * Rendered when strict POM generation refuses to write a script because a step
 * has no Page Object member. It names the element and the step that needs it,
 * so the user knows exactly what to scan or add before generating again.
 */
const MissingPomElementsPanel = ({ report, onRecheck, isChecking }) => {
    if (!report || report.ready !== false) return null;
    const missing = Array.isArray(report.missing_elements) ? report.missing_elements : [];
    if (!missing.length) return null;

    return (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
            <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-amber-900">
                        {missing.length} element{missing.length === 1 ? '' : 's'} missing from the selected POMs
                    </p>
                    <p className="mt-1 text-xs text-amber-800">
                        No script was generated. Scan the page with the POM Scanner extension, or add
                        these elements manually, then generate again.
                    </p>
                    <ul className="mt-2 space-y-2">
                        {missing.map((item, index) => (
                            <li
                                key={`${item.suggested_name || item.element || 'missing'}-${index}`}
                                className="rounded border border-amber-200 bg-white p-2"
                            >
                                <p className="text-sm font-medium text-slate-900">
                                    {item.element || 'Unnamed element'}
                                    {item.element_type && item.element_type !== 'other' && (
                                        <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                                            {item.element_type}
                                        </span>
                                    )}
                                </p>
                                {item.step && (
                                    <p className="mt-1 text-xs text-slate-600">Needed by: {item.step}</p>
                                )}
                                {item.reason && (
                                    <p className="mt-1 text-xs text-slate-500">{item.reason}</p>
                                )}
                                {item.suggested_name && (
                                    <p className="mt-1 font-mono text-[11px] text-slate-500">
                                        suggested name: {item.suggested_name}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                    {onRecheck && (
                        <button
                            type="button"
                            onClick={onRecheck}
                            disabled={isChecking}
                            className="mt-3 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                        >
                            {isChecking ? 'Re-checking...' : 'Re-check coverage'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const AssociatedPomCodePreview = ({
    value = '',
    language = 'typescript',
    minHeight = 240,
    maxHeight = 520,
}) => {
    const code = String(value || '');
    if (!shouldUsePlainAssociatedPomPreview(code)) {
        return (
            <CodeEditorPreview
                value={code}
                language={language}
                minHeight={minHeight}
                maxHeight={maxHeight}
            />
        );
    }

    return (
        <textarea
            readOnly
            value={code}
            spellCheck={false}
            aria-label="Associated POM file preview"
            className="w-full resize-none rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 font-mono text-xs leading-5 text-slate-100 shadow-inner outline-none"
            style={{
                minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
                height: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
                maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
            }}
        />
    );
};

const CollapsibleAssociatedPomTypeScript = ({
    isOpen,
    isLoading,
    error,
    code = '',
    minHeight = 240,
    maxHeight = 520,
    onToggle,
}) => (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <button
            type="button"
            onClick={onToggle}
            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-600 hover:bg-slate-50"
            aria-expanded={isOpen}
        >
            <span className="inline-flex items-center gap-2">
                {isLoading && isOpen ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <FileCode className="h-3.5 w-3.5" />}
                TypeScript
            </span>
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {isOpen && (
            <div className="border-t border-slate-200 p-3">
                {isLoading ? (
                    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                        <Loader className="h-4 w-4 animate-spin" />
                        Loading TypeScript...
                    </div>
                ) : error ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                        {error}
                    </div>
                ) : !String(code || '').trim() ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
                        POM TypeScript file is missing.
                    </div>
                ) : (
                    <AssociatedPomCodePreview
                        value={code}
                        minHeight={minHeight}
                        maxHeight={maxHeight}
                    />
                )}
            </div>
        )}
    </div>
);

const stringifyAssociatedPomConfig = (config) => {
    try {
        return JSON.stringify(config && typeof config === 'object' ? config : {}, null, 2);
    } catch (_err) {
        return '{}';
    }
};

const normalizeAssociatedPomsPayload = (payload) => {
    const source = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
    const poms = Array.isArray(source.poms) ? source.poms : [];
    const warnings = Array.isArray(source.warnings) ? source.warnings : [];

    return {
        ...source,
        poms: poms
            .filter((pom) => pom && typeof pom === 'object' && !Array.isArray(pom))
            .map((pom) => {
                const config = pom.config && typeof pom.config === 'object' && !Array.isArray(pom.config) ? pom.config : {};
                return {
                    ...pom,
                    scope: String(pom.scope || 'local').trim() || 'local',
                    pageName: String(pom.pageName || '').trim(),
                    pageUrl: String(pom.pageUrl || '').trim(),
                    file_exists: Boolean(pom.file_exists),
                    config_exists: Boolean(pom.config_exists),
                    config,
                    configText: stringifyAssociatedPomConfig(config),
                    typescriptCode: typeof pom.typescriptCode === 'string' ? pom.typescriptCode : '',
                };
            }),
        warnings: warnings.map((warning) => String(warning || '').trim()).filter(Boolean),
    };
};

const normalizeAssociatedPomFilesPayload = (payload, fallbackPageName = '') => {
    const source = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
    const pageName = String(source.pageName || fallbackPageName || '').trim();
    const files = (Array.isArray(source.files) ? source.files : [])
        .filter((file) => file && typeof file === 'object' && !Array.isArray(file))
        .map((file) => ({
            name: String(file.name || '').trim(),
            language: String(file.language || '').trim() || (String(file.name || '').endsWith('.json') ? 'json' : 'typescript'),
            content: typeof file.content === 'string' ? file.content : '',
            sourceOfTruth: String(file.sourceOfTruth || '').trim(),
        }))
        .filter((file) => file.name);
    return {
        ...source,
        pageName,
        pageUrl: String(source.pageUrl || '').trim(),
        files,
        selectedFileName: files.find((file) => file.language === 'typescript')?.name || files[0]?.name || '',
        typescript_edit_mode: String(source.typescript_edit_mode || '').trim(),
        custom_typescript: Boolean(source.custom_typescript),
        custom_typescript_updated_at: source.custom_typescript_updated_at || null,
        validation_warnings: (Array.isArray(source.validation_warnings) ? source.validation_warnings : [])
            .map((warning) => String(warning || '').trim())
            .filter(Boolean),
    };
};

const isAbsoluteHttpUrl = (value = '') => {
    try {
        const parsed = new URL(String(value || '').trim());
        return ['http:', 'https:'].includes(parsed.protocol) && Boolean(parsed.host);
    } catch (_err) {
        return false;
    }
};

function normalizeUserInputEntry(item = {}) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return { field: '', value: '' };
    }
    return {
        ...item,
        field: String(item.field || item.name || item.label || '').trim(),
        value: item.value === null || item.value === undefined ? '' : String(item.value),
    };
}

const MAX_INPUT_SETS_PER_CASE = 3;

function normalizeUserInputList(items = []) {
    return (Array.isArray(items) ? items : [])
        .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
        .map((item) => normalizeUserInputEntry(item));
}

function resolveUserInputSets(testCase = {}) {
    const rawSets = Array.isArray(testCase?.user_input_sets) ? testCase.user_input_sets : [];
    const normalizedSets = rawSets
        .map((setItem, index) => {
            if (!setItem || typeof setItem !== 'object' || Array.isArray(setItem)) return null;
            return {
                ...setItem,
                set_name: String(setItem.set_name || setItem.name || `Set ${index + 1}`).trim() || `Set ${index + 1}`,
                set_type: String(setItem.set_type || setItem.scenario_type || '').trim(),
                user_inputs: normalizeUserInputList(setItem.user_inputs || setItem.inputs || setItem.values || []),
            };
        })
        .filter(Boolean)
        .filter((setItem) => Array.isArray(setItem.user_inputs));

    if (normalizedSets.length > 0) return normalizedSets.slice(0, 3);

    const fallbackInputs = normalizeUserInputList(testCase?.user_inputs || []);
    if (fallbackInputs.length === 0) return [];

    return [
        {
            set_name: 'Set 1',
            set_type: 'primary',
            user_inputs: fallbackInputs,
        },
    ];
}

function toUserInputPlaceholder(fieldName = '') {
    const normalized = String(fieldName || '').trim();
    if (!normalized) return '';
    return `{{USER_INPUT:${normalized}}}`;
}

function asInputMapFromPairs(pairs = []) {
    return normalizeUserInputList(pairs).reduce((acc, item) => {
        const key = String(item?.field || '').trim();
        if (!key) return acc;
        acc[key] = typeof item?.value === 'string' ? item.value : '';
        return acc;
    }, {});
}

function getMergedInputMapAcrossSets(testCase = {}) {
    const resolvedSets = resolveUserInputSets(testCase);
    if (!resolvedSets.length) {
        return asInputMapFromPairs(testCase?.user_inputs || []);
    }

    return resolvedSets.reduce((acc, setItem) => {
        const currentMap = asInputMapFromPairs(setItem?.user_inputs || []);
        Object.entries(currentMap).forEach(([key, value]) => {
            if (!Object.prototype.hasOwnProperty.call(acc, key)) {
                acc[key] = value;
            }
        });
        return acc;
    }, {});
}

function sanitizeTestCaseForGeneration(testCase) {
    if (!testCase || typeof testCase !== 'object') return {};
    const sanitized = Object.entries(testCase).reduce((acc, [key, value]) => {
        if (key.startsWith('_') || key === 'source_type' || key === 'active_input_set_index') return acc;
        acc[key] = value;
        return acc;
    }, {});

    const resolvedSets = resolveUserInputSets(testCase).map((setItem, index) => ({
        set_name: String(setItem?.set_name || `Set ${index + 1}`).trim() || `Set ${index + 1}`,
        set_type: String(setItem?.set_type || '').trim(),
        user_inputs: normalizeUserInputList(setItem?.user_inputs || []).map((item) => ({
            field: String(item?.field || '').trim(),
            value: toUserInputPlaceholder(item?.field),
        })),
    }));

    const canonicalFieldMap = new Map();
    resolvedSets.forEach((setItem) => {
        (setItem?.user_inputs || []).forEach((item) => {
            const field = String(item?.field || '').trim();
            if (!field || canonicalFieldMap.has(field)) return;
            canonicalFieldMap.set(field, toUserInputPlaceholder(field));
        });
    });

    if (!canonicalFieldMap.size) {
        normalizeUserInputList(testCase?.user_inputs || []).forEach((item) => {
            const field = String(item?.field || '').trim();
            if (!field || canonicalFieldMap.has(field)) return;
            canonicalFieldMap.set(field, toUserInputPlaceholder(field));
        });
    }

    sanitized.user_input_sets = resolvedSets;
    sanitized.user_inputs = Array.from(canonicalFieldMap.entries()).map(([field, value]) => ({
        field,
        value,
    }));
    return sanitized;
}

function buildDraftSaveTestCase(testCase, fallbackSectionNumber = null) {
    if (!testCase || typeof testCase !== 'object') return {};
    return {
        test_case_no: testCase.test_case_no || testCase.testcase_no || '',
        testcase_no: testCase.testcase_no || testCase.test_case_no || '',
        section_number: testCase.section_number || fallbackSectionNumber || null,
        module: testCase.module || testCase.section_title || testCase.sectionTitle || '',
        test_description: testCase.test_description || '',
    };
}

function getHtmlReportUrl(report = {}) {
    if (!report || typeof report !== 'object') return '';
    return String(report.viewer_url || report.report_url || report.presigned_url || '').trim();
}

function resolveRunHtmlReport(runItem = {}) {
    if (runItem?.html_report && typeof runItem.html_report === 'object') {
        return runItem.html_report;
    }
    if (runItem?.report_url) {
        return { viewer_url: runItem.report_url };
    }
    return null;
}

function formatExpiryDuration(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0) return '';
    if (seconds < 60) return `${seconds}s`;

    const minutes = Math.floor(seconds / 60);
    const remSeconds = seconds % 60;
    if (minutes < 60) return remSeconds ? `${minutes}m ${remSeconds}s` : `${minutes}m`;

    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`;
}

const RunScreenshotCard = ({
    shot,
    index,
    idPrefix,
    copiedId,
    copyToClipboard,
    compact = false,
}) => {
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const screenshotUrl = String(shot?.viewer_url || shot?.presigned_url || '').trim();
    const label = String(shot?.file_name || shot?.relative_path || `screenshot-${index + 1}`);
    const expiresIn = Number(shot?.presigned_expires_in_seconds || 0);
    const expiryLabel = shot?.viewer_url ? '' : formatExpiryDuration(expiresIn);

    useEffect(() => {
        if (!isPreviewOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setIsPreviewOpen(false);
        };

        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isPreviewOpen]);

    if (!screenshotUrl) {
        return (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-[11px] uppercase tracking-wide font-semibold text-amber-700">
                    Screenshot {index + 1}
                </p>
                <p className="mt-1 text-xs font-semibold text-slate-800 break-words">{label}</p>
                <p className="mt-2 text-[11px] font-semibold text-amber-700">URL unavailable</p>
            </div>
        );
    }

    return (
        <>
            <div className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-md">
            <button
                type="button"
                onClick={() => setIsPreviewOpen(true)}
                className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500"
                title={`Preview ${label}`}
                aria-label={`Preview screenshot ${index + 1}: ${label}`}
            >
                <div className={`relative overflow-hidden bg-slate-950 ${compact ? 'h-36' : 'h-48 sm:h-52'}`}>
                    <img
                        src={screenshotUrl}
                        alt={`Screenshot ${index + 1}: ${label}`}
                        loading="lazy"
                        className="h-full w-full object-contain transition duration-300 group-hover:scale-[1.02]"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-slate-950/0 transition group-hover:bg-slate-950/20" />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-slate-950/90 via-slate-950/45 to-transparent px-3 pb-2.5 pt-10">
                        <span className="min-w-0 truncate text-xs font-semibold text-white">
                            Screenshot {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/20 bg-slate-950/35 px-2.5 py-1 text-[10px] font-semibold text-white backdrop-blur-sm">
                            <Maximize2 className="h-3 w-3" />
                            Preview
                        </span>
                    </div>
                </div>
            </button>
            <div className="flex items-center gap-2 border-t border-slate-100 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-slate-800" title={label}>{label}</p>
                    {expiryLabel ? (
                        <p className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700">
                            <Clock3 className="h-3 w-3" />
                            Expires in {expiryLabel}
                        </p>
                    ) : null}
                </div>
                <button
                    type="button"
                    onClick={() => copyToClipboard(screenshotUrl, `${idPrefix}-shot-url-${index}`)}
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                >
                    <Copy className="h-3.5 w-3.5" />
                    {copiedId === `${idPrefix}-shot-url-${index}` ? 'Copied' : 'Copy link'}
                </button>
            </div>
            </div>
            {isPreviewOpen && ReactDOM.createPortal(
                <div
                className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm sm:p-8"
                role="dialog"
                aria-modal="true"
                aria-label={`Screenshot ${index + 1} preview`}
                onMouseDown={(event) => {
                    if (event.target === event.currentTarget) setIsPreviewOpen(false);
                }}
            >
                <div className="flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-slate-900 shadow-2xl">
                    <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3 sm:px-5">
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-white">Screenshot {index + 1}</p>
                            <p className="mt-0.5 truncate text-xs text-slate-400" title={label}>{label}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => copyToClipboard(screenshotUrl, `${idPrefix}-shot-url-${index}`)}
                            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-white/15 px-3 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
                        >
                            <Copy className="h-3.5 w-3.5" />
                            {copiedId === `${idPrefix}-shot-url-${index}` ? 'Copied' : 'Copy link'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsPreviewOpen(false)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-300 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                            aria-label="Close screenshot preview"
                            autoFocus
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="min-h-0 flex-1 overflow-auto bg-slate-950 p-3 sm:p-5">
                        <img
                            src={screenshotUrl}
                            alt={`Screenshot ${index + 1}: ${label}`}
                            className="mx-auto max-h-[calc(100vh-10rem)] max-w-full rounded-lg object-contain shadow-lg"
                        />
                    </div>
                </div>
                </div>,
                document.body
            )}
        </>
    );
};

const RunVideoCard = ({ video, index }) => {
    const videoUrl = String(video?.viewer_url || video?.presigned_url || '').trim();
    const label = String(video?.file_name || video?.relative_path || `recording-${index + 1}`);
    if (!videoUrl) return null;

    return (
        <div className="overflow-hidden rounded-xl border border-indigo-200 bg-white shadow-sm">
            <div className="aspect-video bg-slate-950">
                <video
                    src={videoUrl}
                    controls
                    preload="metadata"
                    className="h-full w-full object-contain"
                    aria-label={`Playwright recording ${index + 1}: ${label}`}
                />
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-3 py-2.5">
                <p className="min-w-0 truncate text-xs font-semibold text-slate-800" title={label}>{label}</p>
                <a
                    href={videoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-indigo-700"
                >
                    <ExternalLink className="h-3 w-3" />
                    Open
                </a>
            </div>
        </div>
    );
};

const AUTO_POM_MIN_SCORE = 0.35;
const MAX_AUTO_SELECTED_POMS = 3;
function resolveQueuedBulkJobId(payload = {}) {
    return String(payload?.bulk_job?.id || payload?.bulk_job_id || '').trim();
}

async function waitForBulkWorkerResult(bulkJobId, fallbackMessage = 'The request did not complete successfully', options = {}) {
    const normalizedJobId = String(bulkJobId || '').trim();
    if (!normalizedJobId) {
        throw new Error('The request did not start. Please try again.');
    }

    const snapshot = await waitForJobResult(
        { id: normalizedJobId, adapter: 'bulk' },
        { signal: options.signal, timeoutMs: options.timeoutMs }
    );
    const item = snapshot.items.find((candidate) => String(candidate?.status || '').toLowerCase() === 'completed')
        || snapshot.items[0];
    if (String(item?.status || '').toLowerCase() !== 'completed') {
        throw new Error(item?.error || fallbackMessage);
    }
    return item?.result_payload || item?.result_summary || {};
}

async function queueWorkerRequest(endpoint, payload, fallbackMessage, options = {}) {
    const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: options.signal,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(resolveApiErrorMessage(errorData, fallbackMessage));
    }

    const queued = await response.json();
    if (queued?.job?.id) {
        const snapshot = await waitForJobResult(queued.job, {
            acceptFailedExecution: true,
            signal: options.signal,
            timeoutMs: options.timeoutMs,
            onUpdate: options.onUpdate,
        });
        if (snapshot.job?.type === 'playwright_script_generation'
            || snapshot.job?.type === 'playwright_automation'
            || String(snapshot.job?.type || '').endsWith('_execution')) {
            return loadJobResult(snapshot.job, { signal: options.signal });
        }
        return snapshot.job?.result_summary || {};
    }
    return waitForBulkWorkerResult(resolveQueuedBulkJobId(queued), fallbackMessage, options);
}

function buildPomSelectionKeyFromPom(pom = {}) {
    return `${pom.scope || 'local'}::${pom.pageName || ''}`;
}

function buildAssociatedPomViewerKey(caseKey, pom = {}, pomIndex = 0) {
    const pomKey = buildPomSelectionKeyFromPom(pom);
    return `${caseKey || 'case'}::${pomKey || `pom-${pomIndex}`}`;
}

function buildAssociatedPomFileEditorKey(caseKey, pom = {}, pomIndex = 0) {
    return `${buildAssociatedPomViewerKey(caseKey, pom, pomIndex)}::file-editor`;
}

// A POM lives in one of three catalogs: this source, the application it belongs
// to, or the deployment-wide publish target.
const POM_SCOPES = new Set(['local', 'application', 'global']);

const POM_SCOPE_LABELS = {
    local: 'This source',
    application: 'Application',
    global: 'Global',
};

const POM_SCOPE_ORDER = { application: 0, local: 1, global: 2 };

/** Prefix a POM name with its catalog, so two same-named POMs stay tellable apart. */
const pomScopePrefixLabel = (pom = {}) => {
    if (pom.scope === 'global') return 'Global ';
    if (pom.scope === 'application') return 'Application ';
    return '';
};

function normalizePomSelectionItem(pom = {}) {
    if (!pom || typeof pom !== 'object') return null;
    const pageName = String(pom.pageName || pom.page_name || '').trim();
    if (!pageName) return null;
    const normalized = {
        ...pom,
        pageName,
        pageUrl: String(pom.pageUrl || pom.page_url || '/').trim() || '/',
        scope: POM_SCOPES.has(pom.scope) ? pom.scope : 'local',
    };
    if (pom.score !== null && pom.score !== undefined && Number.isFinite(Number(pom.score))) {
        normalized.score = Number(pom.score);
    }
    return normalized;
}

function normalizePomSelectionList(poms = []) {
    const seen = new Set();
    return (Array.isArray(poms) ? poms : [])
        .map((pom) => normalizePomSelectionItem(pom))
        .filter(Boolean)
        .filter((pom) => {
            const key = buildPomSelectionKeyFromPom(pom);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
}

function normalizeApplicationPomCatalogItem(pom = {}) {
    if (!pom || typeof pom !== 'object') return null;
    const pageName = String(pom.pageName || pom.page_name || '').trim();
    if (!pageName) return null;
    const actionCount = Number(pom.actionCount ?? pom.action_count ?? 0);
    const assertionCount = Number(pom.assertionCount ?? pom.assertion_count ?? 0);
    const functionCount = Number(
        pom.functions
        ?? pom.functionCount
        ?? pom.function_count
        ?? pom.methodCount
        ?? pom.method_count
        ?? (actionCount + assertionCount)
        ?? 0
    );

    return normalizePomSelectionItem({
        ...pom,
        scope: 'application',
        pageName,
        pageUrl: String(pom.pageUrl || pom.page_url || '').trim(),
        locators: Number(pom.locators ?? pom.locatorCount ?? pom.locator_count ?? 0),
        functions: Number.isFinite(functionCount) ? functionCount : 0,
    });
}

function normalizeFeaturePomSelectionList(featurePom = {}) {
    const source = featurePom && typeof featurePom === 'object' && !Array.isArray(featurePom)
        ? featurePom
        : {};
    const pages = Array.isArray(source.pages) ? source.pages : [];
    const fallbackPageName = String(source.page_name || source.pageName || 'CapturedFeaturePage').trim() || 'CapturedFeaturePage';
    const fallbackPageUrl = String(source.page_url || source.pageUrl || '').trim();
    const fallbackTypeScript = String(source.typescript || source.playwright || '').trim();
    const candidates = pages.length > 0
        ? pages
        : [{
            page_name: fallbackPageName,
            page_url: fallbackPageUrl,
            locator_count: source.locator_count || 0,
            function_count: source.function_count || 0,
            typescript: fallbackTypeScript,
        }];

    return normalizePomSelectionList(candidates.map((page, index) => {
        const pageName = String(
            page.pageName
            || page.page_name
            || page.name
            || (index === 0 ? fallbackPageName : `CapturedFeaturePage${index + 1}`)
        ).trim() || `CapturedFeaturePage${index + 1}`;
        const pageUrl = String(page.pageUrl || page.page_url || fallbackPageUrl || '/').trim() || '/';
        const typescriptCode = String(page.typescript || page.typescriptCode || fallbackTypeScript || '').trim();
        return {
            ...page,
            scope: 'local',
            pageName,
            pageUrl,
            locators: Number(page.locators || page.locator_count || 0),
            functions: Number(page.functions || page.function_count || 0),
            reason: 'Captured feature POM is attached automatically.',
            file_exists: Boolean(typescriptCode),
            config_exists: true,
            config: page.config || {},
            configText: stringifyAssociatedPomConfig(page.config || {}),
            typescriptCode,
        };
    }));
}

function mergePomSelectionLists(...pomLists) {
    return normalizePomSelectionList(
        pomLists.flatMap((poms) => (Array.isArray(poms) ? poms : []))
    );
}

function normalizePomSelectionRecord(record = {}) {
    const rawPoms = Array.isArray(record) ? record : record?.poms;
    const mode = Array.isArray(record)
        ? 'manual'
        : String(record?.mode || 'manual').trim().toLowerCase();
    return {
        mode: ['auto', 'manual', 'manual_all'].includes(mode) ? mode : 'manual',
        poms: normalizePomSelectionList(rawPoms || []),
        updated_at: record?.updated_at || null,
        updated_by: record?.updated_by || null,
    };
}

function filterPomSelectionListByCatalog(poms = [], catalogPoms = [], options = {}) {
    const availableKeys = new Set(
        normalizePomSelectionList(catalogPoms).map((pom) => buildPomSelectionKeyFromPom(pom))
    );
    if (availableKeys.size === 0) {
        return options?.strict ? [] : normalizePomSelectionList(poms);
    }
    return normalizePomSelectionList(poms).filter((pom) => availableKeys.has(buildPomSelectionKeyFromPom(pom)));
}

function filterPomSelectionRecordByCatalog(record = {}, catalogPoms = [], options = {}) {
    const normalizedRecord = normalizePomSelectionRecord(record);
    return {
        ...normalizedRecord,
        poms: filterPomSelectionListByCatalog(normalizedRecord.poms, catalogPoms, options),
    };
}

function splitPomsByScope(poms = []) {
    const normalized = normalizePomSelectionList(poms);
    const namesForScope = (scope) => normalized
        .filter((pom) => pom.scope === scope)
        .map((pom) => pom.pageName);
    return {
        localNames: namesForScope('local'),
        applicationNames: namesForScope('application'),
        globalNames: namesForScope('global'),
    };
}

function isAbortLikeError(error) {
    const name = String(error?.name || '').toLowerCase();
    const message = String(error?.message || '').toLowerCase();
    return name === 'aborterror' || message.includes('aborted') || message.includes('abort');
}

function createRequestToken() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isInternalActorId(value = '') {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return false;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(normalizedValue)) {
        return true;
    }
    if (/^[0-9a-f]{24}$/i.test(normalizedValue)) {
        return true;
    }
    return false;
}

function normalizeActorCandidate(candidate) {
    if (candidate === null || candidate === undefined) return '';

    if (
        typeof candidate === 'string'
        || typeof candidate === 'number'
        || typeof candidate === 'boolean'
    ) {
        return String(candidate).trim();
    }

    if (Array.isArray(candidate)) {
        for (const item of candidate) {
            const resolved = normalizeActorCandidate(item);
            if (resolved) return resolved;
        }
        return '';
    }

    if (typeof candidate === 'object') {
        const preferredKeys = [
            'name',
            'display_name',
            'full_name',
            'username',
            'user_name',
            'email',
            'label',
            'value',
            'id',
            'user_id',
        ];
        for (const key of preferredKeys) {
            const resolved = normalizeActorCandidate(candidate?.[key]);
            if (resolved) return resolved;
        }
    }

    return '';
}

function resolveActorLabel(...values) {
    for (const candidate of values) {
        const normalizedValue = normalizeActorCandidate(candidate);
        if (!normalizedValue) continue;
        if (isInternalActorId(normalizedValue)) continue;
        return normalizedValue;
    }
    return null;
}

function resolveScriptEditorMeta(source = {}, fallback = {}) {
    const requestPayload = source?.request_payload && typeof source.request_payload === 'object'
        ? source.request_payload
        : {};
    const payload = source?.payload && typeof source.payload === 'object'
        ? source.payload
        : {};
    const fallbackMeta = fallback && typeof fallback === 'object' ? fallback : {};
    const editedBy = resolveActorLabel(
        source?.edited_by_name,
        source?.edited_by_email,
        source?.edited_by,
        requestPayload?.edited_by_name,
        requestPayload?.edited_by_email,
        requestPayload?.edited_by,
        payload?.edited_by_name,
        payload?.edited_by_email,
        payload?.edited_by,
        fallbackMeta?.edited_by
    );
    const editedAt =
        source?.updated_at ||
        requestPayload?.edited_at ||
        fallbackMeta?.edited_at ||
        null;
    return {
        edited_by: editedBy || null,
        edited_at: editedAt,
    };
}

function normalizeErrorDetail(detail) {
    if (detail === null || detail === undefined) return '';

    if (
        typeof detail === 'string'
        || typeof detail === 'number'
        || typeof detail === 'boolean'
    ) {
        const normalized = String(detail).trim();
        if (!normalized || normalized === '[object Object]') return '';
        return normalized;
    }

    if (Array.isArray(detail)) {
        const parts = detail
            .map((item) => normalizeErrorDetail(item))
            .filter(Boolean);
        return parts.join(' | ');
    }

    if (typeof detail === 'object') {
        const locParts = Array.isArray(detail?.loc)
            ? detail.loc
                .map((item) => String(item || '').trim())
                .filter(Boolean)
            : [];
        const msgText = normalizeErrorDetail(detail?.msg);
        if (locParts.length > 0 && msgText) {
            return `${locParts.join('.')}: ${msgText}`;
        }

        const preferredParts = [
            detail?.message,
            detail?.detail,
            detail?.reason,
            detail?.error,
            detail?.title,
            detail?.description,
            detail?.msg,
        ];
        for (const part of preferredParts) {
            const normalized = normalizeErrorDetail(part);
            if (normalized) return normalized;
        }

        try {
            const serialized = JSON.stringify(detail);
            return serialized && serialized !== '{}' ? serialized : '';
        } catch {
            return '';
        }
    }

    return '';
}

function resolveApiErrorMessage(errorData, fallback) {
    const normalized = normalizeErrorDetail(
        errorData?.detail ?? errorData?.error ?? errorData?.message ?? errorData
    );
    if (/\b(queue|queued|worker|bulk)\b/i.test(normalized)) {
        return fallback;
    }
    return normalized || fallback;
}

function resolveCaughtErrorMessage(error, fallback) {
    const normalized = normalizeErrorDetail(error?.message ?? error);
    if (/\b(queue|queued|worker|bulk)\b/i.test(normalized)) {
        return fallback;
    }
    return normalized || fallback;
}

function resolveExitCode(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 1;
}

function buildRunOutputFromRuns(perSetRuns = [], options = {}) {
    const runs = (Array.isArray(perSetRuns) ? perSetRuns : [])
        .filter(Boolean)
        .map((runItem, index) => {
            const parsedIndex = Number(runItem?.set_index);
            const setName = String(runItem?.set_name || runItem?.setName || `Run ${index + 1}`).trim() || `Run ${index + 1}`;
            const setType = String(runItem?.set_type || '').trim();
            const setLabel = String(runItem?.set_label || '').trim() || (setType ? `${setName} (${setType})` : setName);
            return {
                ...runItem,
                set_index: Number.isFinite(parsedIndex) ? parsedIndex : index,
                set_name: setName,
                set_type: setType,
                set_label: setLabel,
            };
        });
    if (!runs.length) return null;

    const passedRuns = runs.filter((runItem) => resolveExitCode(runItem?.exit_code) === 0).length;
    const failedRuns = runs.length - passedRuns;
    const totalDurationMs = runs.reduce(
        (sum, runItem) => sum + Number(runItem?.duration_ms || 0),
        0
    );
    const mergedStdout = runs
        .filter((runItem) => String(runItem?.stdout || '').trim())
        .map((runItem) => `[${runItem.set_label}]${'\n'}${runItem.stdout}`)
        .join('\n\n');
    const mergedStderr = runs
        .filter((runItem) => String(runItem?.stderr || '').trim())
        .map((runItem) => `[${runItem.set_label}]${'\n'}${runItem.stderr}`)
        .join('\n\n');
    const mergedScreenshots = runs
        .flatMap((runItem) => (Array.isArray(runItem?.screenshots) ? runItem.screenshots : []))
        .filter((item) => item && typeof item === 'object');
    const mergedVideos = runs
        .flatMap((runItem) => (Array.isArray(runItem?.videos) ? runItem.videos : []))
        .filter((item) => item && typeof item === 'object');
    const htmlReports = runs
        .map((runItem) => resolveRunHtmlReport(runItem))
        .filter((item) => item && getHtmlReportUrl(item));
    const authStateUsed = runs.some((runItem) => Boolean(runItem?.auth_state_used));
    const authFailureDetected = runs.some((runItem) => Boolean(runItem?.auth_failure_detected));
    const authRetryAttempted = runs.some((runItem) => Boolean(runItem?.auth_retry_attempted));
    const authStateRefreshed = runs.some((runItem) => Boolean(runItem?.auth_state_refreshed));
    const authRetrySucceeded = runs.some((runItem) => Boolean(runItem?.auth_retry_succeeded));
    const authRefreshErrors = runs
        .map((runItem) => String(runItem?.auth_refresh_error || '').trim())
        .filter(Boolean);
    const authRetrySkippedReasons = runs
        .map((runItem) => String(runItem?.auth_retry_skipped_reason || '').trim())
        .filter(Boolean);

    return {
        runs,
        total_sets: runs.length,
        passed_sets: passedRuns,
        failed_sets: failedRuns,
        exit_code: failedRuns === 0 ? 0 : 1,
        duration_ms: totalDurationMs,
        stdout: mergedStdout,
        stderr: mergedStderr,
        auth_state_used: authStateUsed,
        auth_failure_detected: authFailureDetected,
        auth_retry_attempted: authRetryAttempted,
        auth_state_refreshed: authStateRefreshed,
        auth_retry_succeeded: authRetrySucceeded,
        auth_refresh_error: authRefreshErrors.join('\n'),
        auth_retry_skipped_reason: authRetrySkippedReasons.join('\n'),
        screenshots: mergedScreenshots,
        videos: mergedVideos,
        html_reports: htmlReports,
        html_report: htmlReports[0] || null,
        report_url: getHtmlReportUrl(htmlReports[0] || {}) || null,
        loaded_from_history: Boolean(options.loadedFromHistory),
    };
}

export const GenerateScriptsStep = ({
    editedTestCases,
    pdfId,
    applicationId = '',
    automationAuthPdfId = '',
    processingResult,
    prefilledScriptsByCaseId = {},
    automationMode = 'pdf',
    featureSessionId = '',
    featurePom = null,
    featureScripts = null,
    featureScriptNotes = '',
    onFeatureScriptNotesChange = null,
    onFeatureSessionChange = null,
    scriptFlow = 'ui',
    theme,
    onBack,
    onComplete,
    onSaveTestCases,
    onRunResultsChange,
    showBackButton = true
}) => {
    const isFeatureMode = automationMode === 'feature';
    const normalizedApplicationId = String(applicationId || '').trim();
    const featurePomSelections = useMemo(
        () => (isFeatureMode ? normalizeFeaturePomSelectionList(featurePom || {}) : []),
        [featurePom, isFeatureMode]
    );
    const isApiScriptFlow = String(scriptFlow || '').trim().toLowerCase() === 'api';
    const scriptTheme = getScriptStageTheme(
        theme || (isFeatureMode ? 'capture' : isApiScriptFlow ? 'api' : 'brd')
    );
    // Shared by every bordered/neutral action button so the hover accent stays
    // in one place instead of being repeated at each call site.
    const secondaryButtonClass = `inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 transition ${scriptTheme.secondaryHover} disabled:cursor-not-allowed disabled:opacity-50`;
    const [expandedCase, setExpandedCase] = useState(null);
    const [activeInspectorTab, setActiveInspectorTab] = useState('script');
    const scriptType = 'playwright';
    const [generatedScripts, setGeneratedScripts] = useState({});
    const [loadingCases, setLoadingCases] = useState(new Set());
    const [copiedId, setCopiedId] = useState(null);
    const [generateAllStatus, setGenerateAllStatus] = useState(null);
    const [scriptBatchJob, setScriptBatchJob] = useState(null);
    const [scriptBatchItems, setScriptBatchItems] = useState([]);
    const [executionJob, setExecutionJob] = useState(null);
    const [executionItems, setExecutionItems] = useState([]);
    const [errors, setErrors] = useState({});
    const [runningCases, setRunningCases] = useState(new Set());
    const [runResults, setRunResults] = useState({});
    const [runErrors, setRunErrors] = useState({});
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [selectedCases, setSelectedCases] = useState(new Set());
    const [runAllStatus, setRunAllStatus] = useState(null);
    const [actionDelayEnabled, setActionDelayEnabled] = useState(true);
    const [actionDelayMs, setActionDelayMs] = useState(1000);
    const [editModes, setEditModes] = useState(new Set());
    const [scriptInputValues, setScriptInputValues] = useState({});
    const [workingTestCases, setWorkingTestCases] = useState(editedTestCases);
    const [caseInputDrafts, setCaseInputDrafts] = useState({});
    const [caseInputDirtyKeys, setCaseInputDirtyKeys] = useState(new Set());
    const [savingCaseInputKeys, setSavingCaseInputKeys] = useState(new Set());
    const [caseInputSaveErrors, setCaseInputSaveErrors] = useState({});
    const [caseInputSaveMeta, setCaseInputSaveMeta] = useState({});

    // Assumptions and Warnings State
    const [scriptAssumptions, setScriptAssumptions] = useState({});
    const [scriptWarnings, setScriptWarnings] = useState({});
    const [expandedDetails, setExpandedDetails] = useState(null); // Track which details section is expanded
    const [expandedScreenshotPanels, setExpandedScreenshotPanels] = useState(new Set());
    const [refreshingReportKeys, setRefreshingReportKeys] = useState(new Set());
    const [scriptModels, setScriptModels] = useState({});
    const [scriptVersionsByCaseId, setScriptVersionsByCaseId] = useState({});
    const [versionPanels, setVersionPanels] = useState(new Set());
    const [dirtyScriptKeys, setDirtyScriptKeys] = useState(new Set());
    const [savingScriptKeys, setSavingScriptKeys] = useState(new Set());
    const [scriptSaveErrors, setScriptSaveErrors] = useState({});
    const [scriptSaveMeta, setScriptSaveMeta] = useState({});
    const getScriptStaleMeta = useCallback((caseKey) => {
        const meta = scriptSaveMeta?.[caseKey] || {};
        const isStale = String(meta?.status || '').trim().toLowerCase() === 'stale'
            || Boolean(meta?.requires_regeneration);
        if (!isStale) {
            return { isStale: false, message: '', missingPoms: [] };
        }
        const missingPoms = Array.isArray(meta?.missing_poms) ? meta.missing_poms : [];
        const missingNames = missingPoms
            .map((pom) => String(pom?.pageName || pom?.page_name || '').trim())
            .filter(Boolean);
        const message = meta?.stale_reason
            || (
                missingNames.length
                    ? `Selected POM ${missingNames.join(', ')} is no longer available. Regenerate this script.`
                    : 'Selected POMs changed. Regenerate this script before running.'
            );
        return { isStale: true, message, missingPoms };
    }, [scriptSaveMeta]);
    const [pendingScriptSave, setPendingScriptSave] = useState(null);
    const generatedScriptsRef = useRef({});
    const [associatedPomPanels, setAssociatedPomPanels] = useState(new Set());
    const [associatedPomsByCaseKey, setAssociatedPomsByCaseKey] = useState({});
    const [openAssociatedPomTypeScriptPanels, setOpenAssociatedPomTypeScriptPanels] = useState(new Set());
    const [loadingAssociatedPoms, setLoadingAssociatedPoms] = useState(new Set());
    const [associatedPomErrors, setAssociatedPomErrors] = useState({});
    const [loadingAssociatedPomFileKeys, setLoadingAssociatedPomFileKeys] = useState(new Set());
    const [editingAssociatedPomKeys, setEditingAssociatedPomKeys] = useState(new Set());
    const [associatedPomEditDrafts, setAssociatedPomEditDrafts] = useState({});
    const [savingAssociatedPomKeys, setSavingAssociatedPomKeys] = useState(new Set());
    const [associatedPomEditErrors, setAssociatedPomEditErrors] = useState({});
    const [associatedPomEditStatus, setAssociatedPomEditStatus] = useState({});
    const [showGithubExportModal, setShowGithubExportModal] = useState(false);
    const [projectExportLoading, setProjectExportLoading] = useState(false);
    const [projectExportStatus, setProjectExportStatus] = useState(null);
    const [projectExportError, setProjectExportError] = useState(null);
    const [automationAuthState, setAutomationAuthState] = useState(null);

    // POM Selection State
    const [showPomModal, setShowPomModal] = useState(false);
    const [pomList, setPomList] = useState([]);
    const [loadingPoms, setLoadingPoms] = useState(false);
    const [pomError, setPomError] = useState(null);
    const [pomSelectionsByCaseKey, setPomSelectionsByCaseKey] = useState({});
    const [commonPomSelections, setCommonPomSelections] = useState([]);
    const [pomSelectionError, setPomSelectionError] = useState(null);
    const [pomSelectionStatus, setPomSelectionStatus] = useState(null);
    const [loadingPomRecommendations, setLoadingPomRecommendations] = useState(new Set());
    const [pomRecommendationCancelling, setPomRecommendationCancelling] = useState(false);
    const [pomSelectionsLoaded, setPomSelectionsLoaded] = useState(false);
    const [showPomModeModal, setShowPomModeModal] = useState(false);
    const [pomModePromptSeen, setPomModePromptSeen] = useState(false);
    const [pomModalTarget, setPomModalTarget] = useState(null);
    const [modalSelectedPoms, setModalSelectedPoms] = useState([]);
    const [pomSearchTerm, setPomSearchTerm] = useState('');
    // Strict POM mode: the generator may only use members of the selected POMs.
    // A step it cannot map is reported as a missing element instead of becoming
    // an invented selector. Off by default so existing flows are unchanged.
    const [strictPomMode, setStrictPomMode] = useState(false);
    const [pomCoverageByCaseKey, setPomCoverageByCaseKey] = useState({});
    const [checkingPomCoverage, setCheckingPomCoverage] = useState(new Set());
    const pomRecommendationAbortRef = useRef(null);
    const pomRecommendationRequestRef = useRef(null);
    const associatedPomRequestControllersRef = useRef(new Map());
    const buildPomSelectionKey = buildPomSelectionKeyFromPom;

    useEffect(() => {
        if (onRunResultsChange) {
            onRunResultsChange(runResults);
        }
    }, [runResults, onRunResultsChange]);

    useEffect(() => {
        setWorkingTestCases(editedTestCases);
        setCaseInputDrafts({});
        setCaseInputDirtyKeys(new Set());
        setCaseInputSaveErrors({});
        setCaseInputSaveMeta({});
    }, [editedTestCases]);

    useEffect(() => {
        generatedScriptsRef.current = generatedScripts;
    }, [generatedScripts]);

    useEffect(() => {
        if (isApiScriptFlow && activeInspectorTab === 'poms') {
            setActiveInspectorTab('script');
        }
    }, [activeInspectorTab, isApiScriptFlow]);

    const fetchAutomationAuthState = useCallback(async () => {
        const normalizedPdfId = String(automationAuthPdfId || pdfId || '').trim();
        if (!normalizedPdfId) {
            setAutomationAuthState(null);
            return null;
        }
        try {
            const response = await apiFetch(API_ENDPOINTS.PLAYWRIGHT_AUTH_STATE(normalizedPdfId));
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(resolveApiErrorMessage(errorData, 'Failed to load automation auth state'));
            }
            const payload = await response.json().catch(() => ({}));
            setAutomationAuthState(payload);
            return payload;
        } catch (_err) {
            setAutomationAuthState(null);
            return null;
        }
    }, [automationAuthPdfId, pdfId]);

    useEffect(() => {
        fetchAutomationAuthState();
    }, [fetchAutomationAuthState]);

    useEffect(() => () => {
        associatedPomRequestControllersRef.current.forEach((controller) => controller.abort());
        associatedPomRequestControllersRef.current.clear();
    }, []);

    useEffect(() => {
        if (!pendingScriptSave) return undefined;

        const handleEscape = (event) => {
            if (event.key !== 'Escape') return;
            if (savingScriptKeys.has(pendingScriptSave.caseKey)) return;
            setPendingScriptSave(null);
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [pendingScriptSave, savingScriptKeys]);

    useEffect(() => {
        setPomSelectionsLoaded(false);
        setPomSelectionsByCaseKey({});
        setCommonPomSelections([]);
        setPomModePromptSeen(false);
        setShowPomModeModal(false);
    }, [pdfId, processingResult?.section_number]);

    // Fetch POMs list
    const fetchPOMsList = useCallback(async () => {
        if (isFeatureMode) {
            setPomList(featurePomSelections);
            setPomError(null);
            return;
        }
        if (!pdfId) {
            setPomError('Missing pdf_id. Please upload/select a PDF first.');
            setPomList([]);
            return;
        }

        setLoadingPoms(true);
        setPomError(null);

        try {
            if (normalizedApplicationId) {
                const response = await apiFetch(API_ENDPOINTS.POM_SCANNER_APPLICATION_POMS(normalizedApplicationId, {
                    adopt: false,
                    fast: true,
                    includeFeatureCapture: false,
                }), {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                });
                if (!response.ok) throw new Error('Failed to fetch application POMs');

                const payload = await response.json();
                const applicationPages = normalizePomSelectionList(
                    (payload?.poms || []).map((pom) => normalizeApplicationPomCatalogItem(pom)).filter(Boolean)
                ).sort((a, b) => String(a.pageName || '').localeCompare(String(b.pageName || '')));
                const filterOptions = { strict: true };
                setPomList(applicationPages);
                setCommonPomSelections((prev) => filterPomSelectionListByCatalog(prev, applicationPages, filterOptions));
                setPomSelectionsByCaseKey((prev) => {
                    const next = {};
                    Object.entries(prev || {}).forEach(([caseKey, record]) => {
                        const filteredRecord = filterPomSelectionRecordByCatalog(record, applicationPages, filterOptions);
                        if (filteredRecord.poms.length > 0) {
                            next[caseKey] = filteredRecord;
                        }
                    });
                    return next;
                });
                setModalSelectedPoms((prev) => filterPomSelectionListByCatalog(prev, applicationPages, filterOptions));
                return;
            }

            const localResponse = await apiFetch(`${API_ENDPOINTS.POMS}?pdf_id=${encodeURIComponent(pdfId)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!localResponse.ok) throw new Error('Failed to fetch PDF POMs');

            const localResult = await localResponse.json();
            // The source listing already carries the application's shared POMs,
            // tagged with their own scope. Global POMs are legacy and are no
            // longer offered in new script selections.
            const localPages = (localResult.pages || []).map((pom) => ({
                ...pom,
                scope: POM_SCOPES.has(pom.scope) ? pom.scope : 'local',
            }));
            const merged = localPages.sort((a, b) => {
                const scopeA = POM_SCOPE_ORDER[a.scope] ?? 1;
                const scopeB = POM_SCOPE_ORDER[b.scope] ?? 1;
                if (scopeA !== scopeB) return scopeA - scopeB;
                return String(a.pageName || '').localeCompare(String(b.pageName || ''));
            });
            const filterOptions = { strict: true };
            setPomList(merged);
            setCommonPomSelections((prev) => filterPomSelectionListByCatalog(prev, merged, filterOptions));
            setPomSelectionsByCaseKey((prev) => {
                const next = {};
                Object.entries(prev || {}).forEach(([caseKey, record]) => {
                    const filteredRecord = filterPomSelectionRecordByCatalog(record, merged, filterOptions);
                    if (filteredRecord.poms.length > 0) {
                        next[caseKey] = filteredRecord;
                    }
                });
                return next;
            });
            setModalSelectedPoms((prev) => filterPomSelectionListByCatalog(prev, merged, filterOptions));
        } catch (err) {
            setPomError(resolveCaughtErrorMessage(err, 'Failed to fetch POMs'));
        } finally {
            setLoadingPoms(false);
        }
    }, [featurePomSelections, isFeatureMode, normalizedApplicationId, pdfId]);

    // Filter POMs based on search term
	    const filteredPoms = pomList.filter(pom =>
	        String(pom.pageName || '').toLowerCase().includes(pomSearchTerm.toLowerCase()) ||
	        String(pom.pageUrl || '').toLowerCase().includes(pomSearchTerm.toLowerCase())
	    );
	    const modalSelectedPomKeySet = new Set(modalSelectedPoms.map((pom) => buildPomSelectionKey(pom)));

	    useEffect(() => {
	        if (!pomSelectionsLoaded || !normalizedApplicationId || isFeatureMode || isApiScriptFlow) return;
	        fetchPOMsList();
	    }, [
	        fetchPOMsList,
	        isApiScriptFlow,
	        isFeatureMode,
	        normalizedApplicationId,
	        pomSelectionsLoaded,
	    ]);

	    /**
     * Normalize test cases to handle both formats:
     * 1. Array format: editedTestCases = [...]
     * 2. Nested format: editedTestCases = { positive: [...], negative: [...] }
     */
    const getNormalizedTestCases = useCallback(() => {
        if (!workingTestCases) return [];

        if (Array.isArray(workingTestCases)) {
            return workingTestCases.map((tc, idx) => ({
                ...tc,
                _category: 'all',
                _case_key: `all::${tc.test_case_no || 'case'}::${idx}`
            }));
        }

        if (typeof workingTestCases === 'object') {
            const cases = [];

            if (workingTestCases.positive && Array.isArray(workingTestCases.positive)) {
                cases.push(...workingTestCases.positive.map((tc, idx) => ({
                    ...tc,
                    _category: 'positive',
                    _case_key: `positive::${tc.test_case_no || 'case'}::${idx}`
                })));
            }

            if (workingTestCases.negative && Array.isArray(workingTestCases.negative)) {
                cases.push(...workingTestCases.negative.map((tc, idx) => ({
                    ...tc,
                    _category: 'negative',
                    _case_key: `negative::${tc.test_case_no || 'case'}::${idx}`
                })));
            }

            return cases;
        }

        return [];
    }, [workingTestCases]);

    /**
     * Get test cases filtered by category
     */
    const getFilteredTestCases = (category) => {
        const allCases = getNormalizedTestCases();

        if (category === 'all') {
            return allCases;
        }

        if (category === 'manual_positive') {
            return allCases.filter(tc => tc._category === 'positive' && tc.source_type === 'manual_upload');
        }

        if (category === 'manual_negative') {
            return allCases.filter(tc => tc._category === 'negative' && tc.source_type === 'manual_upload');
        }

        return allCases.filter(tc => tc._category === category);
    };

    const getUserInputsMapFromTestCase = (testCase = {}) => {
        return getMergedInputMapAcrossSets(testCase);
    };

    const getResolvedTestCaseId = (testCase = {}) =>
        String(testCase?.test_case_no || testCase?.testcase_no || testCase?.id || '').trim();

    useEffect(() => {
        const featureRunSourceId = pdfId
            || processingResult?.automation_source_id
            || processingResult?.pdf_id
            || featureSessionId;
        const runSourceId = String(isFeatureMode ? featureRunSourceId : pdfId || '').trim();
        if (!runSourceId) return undefined;

        const normalizedCases = getNormalizedTestCases();
        const caseEntries = normalizedCases
            .map((testCase) => ({
                caseKey: testCase?._case_key || testCase?.test_case_no,
                caseId: getResolvedTestCaseId(testCase),
            }))
            .filter((entry) => entry.caseKey && entry.caseId);
        if (!caseEntries.length) return undefined;

        const caseKeyById = new Map();
        caseEntries.forEach((entry) => {
            if (!caseKeyById.has(entry.caseId)) {
                caseKeyById.set(entry.caseId, entry.caseKey);
            }
        });

        let cancelled = false;
        const controller = new AbortController();
        const sectionNumber = isFeatureMode ? 1 : processingResult?.section_number;

        const loadSavedRunHistory = async () => {
            try {
                const response = await apiFetch(
                    API_ENDPOINTS.PLAYWRIGHT_RUNS(runSourceId, {
                        testCaseIds: Array.from(caseKeyById.keys()),
                        sectionNumber,
                        limitPerCase: 5,
                    }),
                    {
                        method: 'GET',
                        headers: { Accept: 'application/json' },
                        signal: controller.signal,
                    }
                );

                if (!response.ok) {
                    if (response.status === 404 && isFeatureMode) return;
                    return;
                }

                const payload = await response.json().catch(() => ({}));
                if (cancelled) return;
                const byTestCase = payload?.by_test_case && typeof payload.by_test_case === 'object'
                    ? payload.by_test_case
                    : {};
                const nextHistoryByCaseKey = {};
                caseKeyById.forEach((caseKey, caseId) => {
                    const runs = Array.isArray(byTestCase[caseId]) ? byTestCase[caseId] : [];
                    const output = buildRunOutputFromRuns(runs, { loadedFromHistory: true });
                    if (output) {
                        nextHistoryByCaseKey[caseKey] = output;
                    }
                });
                if (!Object.keys(nextHistoryByCaseKey).length) return;

                setRunResults((prev) => {
                    let changed = false;
                    const next = { ...prev };
                    Object.entries(nextHistoryByCaseKey).forEach(([caseKey, output]) => {
                        if (next[caseKey] && !next[caseKey].loaded_from_history) return;
                        next[caseKey] = output;
                        changed = true;
                    });
                    return changed ? next : prev;
                });
            } catch (err) {
                if (controller.signal.aborted || isAbortLikeError(err)) return;
            }
        };

        loadSavedRunHistory();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [
        featureSessionId,
        getNormalizedTestCases,
        isFeatureMode,
        pdfId,
        processingResult?.automation_source_id,
        processingResult?.pdf_id,
        processingResult?.section_number,
    ]);

    const getEditableInputSetsForCase = (testCase = {}) => (
        resolveUserInputSets(testCase).map((setItem, setIndex) => ({
            set_name: String(setItem?.set_name || `Set ${setIndex + 1}`).trim() || `Set ${setIndex + 1}`,
            set_type: String(setItem?.set_type || '').trim(),
            user_inputs: normalizeUserInputList(setItem?.user_inputs || []).map((inputItem) => ({
                field: String(inputItem?.field || '').trim(),
                value: inputItem?.value === null || inputItem?.value === undefined ? '' : String(inputItem.value),
            })).filter((inputItem) => inputItem.field),
        })).filter((setItem) => setItem.user_inputs.length > 0)
    );

    const buildCanonicalInputsFromSets = (inputSets = []) => {
        const seen = new Set();
        const canonical = [];
        (Array.isArray(inputSets) ? inputSets : []).forEach((setItem) => {
            normalizeUserInputList(setItem?.user_inputs || []).forEach((inputItem) => {
                const field = String(inputItem?.field || '').trim();
                const fieldKey = field.toLowerCase();
                if (!field || seen.has(fieldKey)) return;
                seen.add(fieldKey);
                canonical.push({
                    field,
                    value: inputItem?.value === null || inputItem?.value === undefined ? '' : String(inputItem.value),
                });
            });
        });
        return canonical;
    };

    const stripTestCaseUiFields = (testCase = {}) => (
        Object.entries(testCase || {}).reduce((acc, [key, value]) => {
            if (key.startsWith('_')) return acc;
            acc[key] = value;
            return acc;
        }, {})
    );

    const parseCaseKeyParts = (caseKey = '') => {
        const parts = String(caseKey || '').split('::');
        const index = Number(parts[parts.length - 1]);
        return {
            category: parts[0] || '',
            index: Number.isFinite(index) ? index : -1,
        };
    };

    const replaceTestCaseInCollection = (collection, targetCase, replacementCase) => {
        const targetId = getResolvedTestCaseId(targetCase);
        const targetKeyParts = parseCaseKeyParts(targetCase?._case_key);
        const hasTargetKey = Boolean(targetKeyParts.category && targetKeyParts.index >= 0);
        const replacement = stripTestCaseUiFields(replacementCase);
        const matches = (row, index, category) => {
            if (hasTargetKey) {
                return category === targetKeyParts.category && index === targetKeyParts.index;
            }
            const rowId = getResolvedTestCaseId(row);
            if (targetId && rowId === targetId) return true;
            return false;
        };

        if (Array.isArray(collection)) {
            return collection.map((row, index) => (
                matches(row, index, 'all') ? { ...row, ...replacement } : row
            ));
        }
        if (collection && typeof collection === 'object') {
            return {
                ...collection,
                positive: Array.isArray(collection.positive)
                    ? collection.positive.map((row, index) => (
                        matches(row, index, 'positive') ? { ...row, ...replacement } : row
                    ))
                    : collection.positive,
                negative: Array.isArray(collection.negative)
                    ? collection.negative.map((row, index) => (
                        matches(row, index, 'negative') ? { ...row, ...replacement } : row
                    ))
                    : collection.negative,
            };
        }
        return collection;
    };

    const getCaseInputDraft = (testCase = {}) => {
        const caseKey = testCase?._case_key || getResolvedTestCaseId(testCase);
        if (caseInputDrafts[caseKey]) return caseInputDrafts[caseKey];
        const existingSets = getEditableInputSetsForCase(testCase);
        if (existingSets.length > 0) return existingSets;
        const requiredInputs = getRequiredInputsFromScript(generatedScripts[caseKey] || '');
        if (!requiredInputs.length) return [];
        return [
            {
                set_name: 'Set 1',
                set_type: 'primary',
                user_inputs: requiredInputs.map((field) => ({ field, value: '' })),
            },
        ];
    };

    const updateCaseInputDraftValue = (testCase, setIndex, inputIndex, value) => {
        const caseKey = testCase?._case_key || getResolvedTestCaseId(testCase);
        if (!caseKey) return;
        setCaseInputDrafts((prev) => {
            const currentDraft = prev[caseKey] || getEditableInputSetsForCase(testCase);
            const nextDraft = currentDraft.map((setItem, currentSetIndex) => {
                if (currentSetIndex !== setIndex) return setItem;
                return {
                    ...setItem,
                    user_inputs: (setItem.user_inputs || []).map((inputItem, currentInputIndex) => (
                        currentInputIndex === inputIndex
                            ? { ...inputItem, value }
                            : inputItem
                    )),
                };
            });
            return { ...prev, [caseKey]: nextDraft };
        });
        setCaseInputDirtyKeys((prev) => new Set([...prev, caseKey]));
        setCaseInputSaveErrors((prev) => ({ ...prev, [caseKey]: null }));
    };

    const updateCaseInputDraftField = (testCase, setIndex, inputIndex, fieldName) => {
        const caseKey = testCase?._case_key || getResolvedTestCaseId(testCase);
        if (!caseKey) return;
        setCaseInputDrafts((prev) => {
            const currentDraft = prev[caseKey] || getEditableInputSetsForCase(testCase);
            const nextDraft = currentDraft.map((setItem, currentSetIndex) => {
                if (currentSetIndex !== setIndex) return setItem;
                return {
                    ...setItem,
                    user_inputs: (setItem.user_inputs || []).map((inputItem, currentInputIndex) => (
                        currentInputIndex === inputIndex
                            ? { ...inputItem, field: fieldName }
                            : inputItem
                    )),
                };
            });
            return { ...prev, [caseKey]: nextDraft };
        });
        setCaseInputDirtyKeys((prev) => new Set([...prev, caseKey]));
        setCaseInputSaveErrors((prev) => ({ ...prev, [caseKey]: null }));
    };

    const addInputSetForCase = (testCase = {}) => {
        const caseKey = testCase?._case_key || getResolvedTestCaseId(testCase);
        if (!caseKey) return;
        setCaseInputDrafts((prev) => {
            const currentDraft = prev[caseKey] || getCaseInputDraft(testCase);
            if (currentDraft.length >= MAX_INPUT_SETS_PER_CASE) return prev;
            const templateRows = (currentDraft[0]?.user_inputs || []).map((row) => ({
                field: row.field,
                value: '',
            }));
            const nextSet = {
                set_name: `Set ${currentDraft.length + 1}`,
                set_type: '',
                user_inputs: templateRows.length ? templateRows : [{ field: '', value: '' }],
            };
            return { ...prev, [caseKey]: [...currentDraft, nextSet] };
        });
        setCaseInputDirtyKeys((prev) => new Set([...prev, caseKey]));
        setCaseInputSaveErrors((prev) => ({ ...prev, [caseKey]: null }));
    };

    const removeInputSetForCase = (testCase = {}, setIndex) => {
        const caseKey = testCase?._case_key || getResolvedTestCaseId(testCase);
        if (!caseKey) return;
        setCaseInputDrafts((prev) => {
            const currentDraft = prev[caseKey] || getCaseInputDraft(testCase);
            if (currentDraft.length <= 1) return prev;
            return { ...prev, [caseKey]: currentDraft.filter((_, index) => index !== setIndex) };
        });
        setCaseInputDirtyKeys((prev) => new Set([...prev, caseKey]));
        setCaseInputSaveErrors((prev) => ({ ...prev, [caseKey]: null }));
    };

    const addInputRowToDraft = (testCase, setIndex) => {
        const caseKey = testCase?._case_key || getResolvedTestCaseId(testCase);
        if (!caseKey) return;
        setCaseInputDrafts((prev) => {
            const currentDraft = prev[caseKey] || getCaseInputDraft(testCase);
            const nextDraft = currentDraft.map((setItem, currentSetIndex) => (
                currentSetIndex === setIndex
                    ? { ...setItem, user_inputs: [...(setItem.user_inputs || []), { field: '', value: '' }] }
                    : setItem
            ));
            return { ...prev, [caseKey]: nextDraft };
        });
        setCaseInputDirtyKeys((prev) => new Set([...prev, caseKey]));
        setCaseInputSaveErrors((prev) => ({ ...prev, [caseKey]: null }));
    };

    const removeInputRowFromDraft = (testCase, setIndex, inputIndex) => {
        const caseKey = testCase?._case_key || getResolvedTestCaseId(testCase);
        if (!caseKey) return;
        setCaseInputDrafts((prev) => {
            const currentDraft = prev[caseKey] || getCaseInputDraft(testCase);
            const nextDraft = currentDraft.map((setItem, currentSetIndex) => (
                currentSetIndex === setIndex
                    ? { ...setItem, user_inputs: (setItem.user_inputs || []).filter((_, index) => index !== inputIndex) }
                    : setItem
            ));
            return { ...prev, [caseKey]: nextDraft };
        });
        setCaseInputDirtyKeys((prev) => new Set([...prev, caseKey]));
        setCaseInputSaveErrors((prev) => ({ ...prev, [caseKey]: null }));
    };

    const resetCaseInputDraft = (testCase = {}) => {
        const caseKey = testCase?._case_key || getResolvedTestCaseId(testCase);
        if (!caseKey) return;
        setCaseInputDrafts((prev) => {
            const next = { ...prev };
            delete next[caseKey];
            return next;
        });
        setCaseInputDirtyKeys((prev) => {
            const next = new Set(prev);
            next.delete(caseKey);
            return next;
        });
        setCaseInputSaveErrors((prev) => ({ ...prev, [caseKey]: null }));
    };

    const saveCaseInputValues = async (testCase = {}, options = {}) => {
        const caseKey = testCase?._case_key || getResolvedTestCaseId(testCase);
        const testCaseId = getResolvedTestCaseId(testCase);
        if (!caseKey || !testCaseId) return;

        const inputSets = getCaseInputDraft(testCase);
        const canonicalInputs = buildCanonicalInputsFromSets(inputSets);
        setSavingCaseInputKeys((prev) => new Set([...prev, caseKey]));
        setCaseInputSaveErrors((prev) => ({ ...prev, [caseKey]: null }));
        try {
            if (!isFeatureMode) {
                if (!onSaveTestCases) {
                    throw new Error('Input edits can be saved after this workflow is loaded from saved test cases.');
                }
                const updatedCase = {
                    ...testCase,
                    user_input_sets: inputSets,
                    user_inputs: canonicalInputs,
                };
                const nextTestCases = replaceTestCaseInCollection(workingTestCases, testCase, updatedCase);
                await onSaveTestCases(nextTestCases, null, {
                    autosave: true,
                    manualImportMeta: processingResult?.manual_import || null,
                    keepalive: Boolean(options?.keepalive),
                });
                setWorkingTestCases(nextTestCases);
                setCaseInputDrafts((prev) => ({
                    ...prev,
                    [caseKey]: getEditableInputSetsForCase(updatedCase),
                }));
                setCaseInputDirtyKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(caseKey);
                    return next;
                });
                setCaseInputSaveMeta((prev) => ({
                    ...prev,
                    [caseKey]: {
                        saved_at: new Date().toISOString(),
                    },
                }));
                const nextInputMap = getMergedInputMapAcrossSets(updatedCase);
                setScriptInputValues((prev) => ({
                    ...prev,
                    [caseKey]: {
                        ...(prev[caseKey] || {}),
                        ...nextInputMap,
                    },
                }));
                return;
            }

            if (!featureSessionId) {
                throw new Error('Input edits can be saved after this feature flow is loaded from the application dashboard.');
            }

            const response = await apiFetch(API_ENDPOINTS.FEATURE_CAPTURE_TEST_CASE_INPUTS(featureSessionId, testCaseId), {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_input_sets: inputSets,
                    user_inputs: canonicalInputs,
                }),
                keepalive: Boolean(options?.keepalive),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(resolveApiErrorMessage(payload, 'Failed to save input values'));
            }

            const savedCase = (Array.isArray(payload?.test_cases) ? payload.test_cases : [])
                .find((row) => getResolvedTestCaseId(row) === testCaseId);
            const updatedCase = savedCase || {
                ...testCase,
                user_input_sets: inputSets,
                user_inputs: canonicalInputs,
            };
            setWorkingTestCases((current) => replaceTestCaseInCollection(current, testCase, updatedCase));
            setCaseInputDrafts((prev) => ({
                ...prev,
                [caseKey]: getEditableInputSetsForCase(updatedCase),
            }));
            setCaseInputDirtyKeys((prev) => {
                const next = new Set(prev);
                next.delete(caseKey);
                return next;
            });
            setCaseInputSaveMeta((prev) => ({
                ...prev,
                [caseKey]: {
                    saved_at: payload?.test_case_input_review?.updated_at || new Date().toISOString(),
                },
            }));
            const nextInputMap = getMergedInputMapAcrossSets(updatedCase);
            setScriptInputValues((prev) => ({
                ...prev,
                [caseKey]: {
                    ...(prev[caseKey] || {}),
                    ...nextInputMap,
                },
            }));
            if (onFeatureSessionChange) {
                onFeatureSessionChange(payload, { message: 'Test case inputs updated.' });
            }
        } catch (err) {
            setCaseInputSaveErrors((prev) => ({
                ...prev,
                [caseKey]: resolveCaughtErrorMessage(err, 'Failed to save input values'),
            }));
        } finally {
            setSavingCaseInputKeys((prev) => {
                const next = new Set(prev);
                next.delete(caseKey);
                return next;
            });
        }
    };

    const getCaseSpecificPomSelectionForCaseKey = useCallback((caseKey) => (
        normalizePomSelectionRecord(pomSelectionsByCaseKey[caseKey] || { mode: 'manual', poms: [] })
    ), [pomSelectionsByCaseKey]);

    const getPomSelectionForCaseKey = useCallback((caseKey) => {
        const caseSelection = getCaseSpecificPomSelectionForCaseKey(caseKey);
        return {
            ...caseSelection,
            commonPoms: commonPomSelections,
            poms: mergePomSelectionLists(caseSelection.poms, commonPomSelections),
        };
    }, [commonPomSelections, getCaseSpecificPomSelectionForCaseKey]);

    const getSelectedPomsForCaseKey = useCallback((caseKey) => (
        getPomSelectionForCaseKey(caseKey).poms
    ), [getPomSelectionForCaseKey]);

    const persistPomSelections = useCallback(async (
        nextSelectionsByCaseKey,
        signal = undefined,
        nextCommonPomSelections = commonPomSelections
    ) => {
        if (isFeatureMode) {
            return {
                status: 'skipped',
                reason: 'Feature capture uses automatically attached POMs.',
                selections: nextSelectionsByCaseKey || {},
                common_poms: normalizePomSelectionList(nextCommonPomSelections),
            };
        }
        if (isApiScriptFlow) {
            return {
                status: 'skipped',
                reason: 'API script flow does not use POM selections.',
                selections: {},
                common_poms: [],
            };
        }
        const normalizedCases = getNormalizedTestCases();
        const selections = {};
        normalizedCases.forEach((testCase) => {
            const testCaseId = getResolvedTestCaseId(testCase);
            if (!testCaseId) return;
            const record = normalizePomSelectionRecord(nextSelectionsByCaseKey[testCase._case_key]);
            if (!record.poms.length) return;
            selections[testCaseId] = record;
        });

        const response = await apiFetch(API_ENDPOINTS.POM_SELECTIONS, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pdf_id: pdfId,
                section_number: processingResult?.section_number,
                selections,
                common_poms: normalizePomSelectionList(nextCommonPomSelections),
            }),
            signal,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(resolveApiErrorMessage(errorData, 'Failed to save POM selections'));
        }
        return response.json();
    }, [commonPomSelections, getNormalizedTestCases, isApiScriptFlow, isFeatureMode, pdfId, processingResult?.section_number]);

    const updateAndPersistPomSelections = useCallback(async (nextSelectionsByCaseKey, statusMessage = null, options = {}) => {
        const signal = options?.signal;
        const optimistic = options?.optimistic !== false;
        const hasCommonPomsOption = Object.prototype.hasOwnProperty.call(options || {}, 'commonPoms');
        const nextCommonPoms = hasCommonPomsOption
            ? normalizePomSelectionList(options.commonPoms)
            : commonPomSelections;
        if (optimistic) {
            setPomSelectionsByCaseKey(nextSelectionsByCaseKey);
            if (hasCommonPomsOption) {
                setCommonPomSelections(nextCommonPoms);
            }
        }
        try {
            await persistPomSelections(nextSelectionsByCaseKey, signal, nextCommonPoms);
            if (signal?.aborted) return false;
            if (!optimistic) {
                setPomSelectionsByCaseKey(nextSelectionsByCaseKey);
                if (hasCommonPomsOption) {
                    setCommonPomSelections(nextCommonPoms);
                }
            }
            setPomSelectionError(null);
            if (statusMessage) {
                setPomSelectionStatus(statusMessage);
                window.setTimeout(() => setPomSelectionStatus(null), 3500);
            }
            return true;
        } catch (err) {
            if (signal?.aborted || isAbortLikeError(err)) {
                return false;
            }
            setPomSelectionError(resolveCaughtErrorMessage(err, 'Failed to save POM selections'));
            return false;
        }
    }, [commonPomSelections, persistPomSelections]);

    const mapSavedPomSelectionsToCaseKeys = useCallback((savedByCaseId = {}) => {
        const normalizedCases = getNormalizedTestCases();
        const nextSelections = {};
        normalizedCases.forEach((testCase) => {
            const testCaseId = getResolvedTestCaseId(testCase);
            if (!testCaseId || !savedByCaseId[testCaseId]) return;
            const record = normalizePomSelectionRecord(savedByCaseId[testCaseId]);
            if (record.poms.length) {
                nextSelections[testCase._case_key] = record;
            }
        });
        return nextSelections;
    }, [getNormalizedTestCases]);

    useEffect(() => {
        if (!pdfId) {
            setPomSelectionsLoaded(true);
            setCommonPomSelections(isFeatureMode ? featurePomSelections : []);
            setPomSelectionsByCaseKey({});
            setPomSelectionError(null);
            if (isFeatureMode) {
                setShowPomModeModal(false);
                setPomModePromptSeen(true);
                setPomList(featurePomSelections);
            }
            return undefined;
        }
        if (isFeatureMode) {
            setPomSelectionsLoaded(true);
            setPomSelectionsByCaseKey({});
            setCommonPomSelections(featurePomSelections);
            setPomSelectionError(null);
            setShowPomModeModal(false);
            setPomModePromptSeen(true);
            setPomList(featurePomSelections);
            return undefined;
        }
        if (isApiScriptFlow) {
            setPomSelectionsLoaded(true);
            setPomSelectionsByCaseKey({});
            setCommonPomSelections([]);
            setPomSelectionError(null);
            return undefined;
        }
        let cancelled = false;
        setPomSelectionsLoaded(false);

        const loadSelections = async () => {
            try {
                const params = new URLSearchParams({ pdf_id: pdfId });
                if (processingResult?.section_number) {
                    params.set('section_number', processingResult.section_number);
                }
                const response = await apiFetch(`${API_ENDPOINTS.POM_SELECTIONS}?${params.toString()}`);
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(resolveApiErrorMessage(errorData, 'Failed to load saved POM selections'));
                }
                const payload = await response.json();
                const savedByCaseId = payload?.selections || {};
                const nextSelections = mapSavedPomSelectionsToCaseKeys(savedByCaseId);
                if (!cancelled) {
                    setPomSelectionsByCaseKey(nextSelections);
                    const savedCommonPoms = normalizePomSelectionList(payload?.common_poms || []);
                    setCommonPomSelections(
                        isFeatureMode && Object.keys(nextSelections).length === 0 && savedCommonPoms.length === 0
                            ? featurePomSelections
                            : savedCommonPoms
                    );
                    if (isFeatureMode) {
                        setShowPomModeModal(false);
                        setPomModePromptSeen(true);
                        setPomList(featurePomSelections);
                    }
                    setPomSelectionError(null);
                }
            } catch (err) {
                if (!cancelled) {
                    if (isFeatureMode && featurePomSelections.length > 0) {
                        setPomSelectionsByCaseKey({});
                        setCommonPomSelections(featurePomSelections);
                        setPomList(featurePomSelections);
                        setPomSelectionError(null);
                    } else {
                        setPomSelectionError(resolveCaughtErrorMessage(err, 'Failed to load saved POM selections'));
                    }
                }
            } finally {
                if (!cancelled) {
                    setPomSelectionsLoaded(true);
                }
            }
        };

        loadSelections();
        return () => {
            cancelled = true;
        };
    }, [featurePomSelections, isApiScriptFlow, isFeatureMode, mapSavedPomSelectionsToCaseKeys, pdfId, processingResult?.section_number]);

    const autoSelectPomsForCases = useCallback(async (cases, options = {}) => {
        if (isFeatureMode) {
            setCommonPomSelections(featurePomSelections);
            setPomSelectionsByCaseKey({});
            setPomSelectionError(null);
            setPomSelectionStatus('Captured page objects are attached automatically.');
            window.setTimeout(() => setPomSelectionStatus(null), 3000);
            return;
        }
        const targetCases = (Array.isArray(cases) ? cases : [])
            .filter(Boolean)
            .filter((testCase) => {
                if (!options.preserveManual) return true;
                const selection = getCaseSpecificPomSelectionForCaseKey(testCase._case_key);
                if (!selection.poms.length) return true;
                const mode = selection.mode;
                return mode !== 'manual' && mode !== 'manual_all';
            });

        if (!targetCases.length) {
            setPomSelectionStatus('No auto-managed test cases to refresh.');
            window.setTimeout(() => setPomSelectionStatus(null), 3000);
            return;
        }

        const targetKeys = targetCases.map((testCase) => testCase._case_key);
        if (pomRecommendationAbortRef.current && !pomRecommendationAbortRef.current.signal.aborted) {
            pomRecommendationAbortRef.current.abort();
        }
        const controller = new AbortController();
        const cancelToken = createRequestToken();
        pomRecommendationAbortRef.current = controller;
        pomRecommendationRequestRef.current = {
            cancelToken,
            pdfId,
            sectionNumber: processingResult?.section_number,
            controller,
        };
        setPomRecommendationCancelling(false);
        setLoadingPomRecommendations((prev) => new Set([...prev, ...targetKeys]));
        setPomSelectionError(null);
        setErrors((prev) => ({ ...prev, bulk: null }));

        try {
            const payload = await queueWorkerRequest(
                API_ENDPOINTS.POM_AUTO_SELECT_QUEUE,
                {
                    pdf_id: pdfId,
                    section_number: processingResult?.section_number,
                    case_ids: targetCases.map((testCase) => getResolvedTestCaseId(testCase)).filter(Boolean),
                    preserve_manual: Boolean(options.preserveManual),
                    top_k: 5,
                    include_manual: true,
                    include_global: false,
                    min_score: AUTO_POM_MIN_SCORE,
                    max_selected: MAX_AUTO_SELECTED_POMS,
                    cancel_token: cancelToken,
                },
                'Failed to auto-select POMs',
                { signal: controller.signal }
            );

            if (controller.signal.aborted) {
                setPomSelectionStatus('POM selection cancelled. Existing selections were not changed.');
                window.setTimeout(() => setPomSelectionStatus(null), 3500);
                return;
            }

            if (payload?.cancelled) {
                setPomSelectionStatus('POM selection cancelled. Existing selections were not changed.');
                window.setTimeout(() => setPomSelectionStatus(null), 3500);
                return;
            }
            const nextSelections = mapSavedPomSelectionsToCaseKeys(payload?.selections || {});
            const matchedCount = Number(payload?.matched_count || 0);

            setPomSelectionsByCaseKey(nextSelections);
            setPomSelectionError(null);
            setPomSelectionStatus(
                matchedCount > 0
                    ? `Auto-selected POMs for ${matchedCount} test case${matchedCount === 1 ? '' : 's'}.`
                    : 'No confident POM matches found. Select common POMs manually or refresh after updating POMs.'
            );
            window.setTimeout(() => setPomSelectionStatus(null), 3500);
        } catch (err) {
            if (controller.signal.aborted || isAbortLikeError(err)) {
                setPomSelectionStatus('POM selection cancelled. Existing selections were not changed.');
                window.setTimeout(() => setPomSelectionStatus(null), 3500);
                return;
            }
            setPomSelectionError(resolveCaughtErrorMessage(err, 'Failed to auto-select POMs'));
        } finally {
            setLoadingPomRecommendations((prev) => {
                const next = new Set(prev);
                targetKeys.forEach((key) => next.delete(key));
                return next;
            });
            if (pomRecommendationAbortRef.current === controller) {
                pomRecommendationAbortRef.current = null;
            }
            if (pomRecommendationRequestRef.current?.controller === controller) {
                pomRecommendationRequestRef.current = null;
            }
            setPomRecommendationCancelling(false);
        }
    }, [
        getCaseSpecificPomSelectionForCaseKey,
        featurePomSelections,
        isFeatureMode,
        mapSavedPomSelectionsToCaseKeys,
        pdfId,
        processingResult?.section_number,
    ]);

    const autoSelectPomsForCase = useCallback(async (testCase) => {
        await autoSelectPomsForCases([testCase], { preserveManual: false });
    }, [autoSelectPomsForCases]);

    const cancelPomRecommendation = useCallback(() => {
        const requestInfo = pomRecommendationRequestRef.current;
        const controller = requestInfo?.controller || pomRecommendationAbortRef.current;
        if (!requestInfo && (!controller || controller.signal.aborted)) return;
        setPomRecommendationCancelling(true);
        setPomSelectionStatus('Cancelling POM selection...');
        if (requestInfo?.cancelToken) {
            apiFetch(API_ENDPOINTS.POM_AUTO_SELECT_CANCEL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pdf_id: requestInfo.pdfId,
                    section_number: requestInfo.sectionNumber,
                    cancel_token: requestInfo.cancelToken,
                }),
            }).catch((err) => {
                if (!isAbortLikeError(err)) {
                    console.warn('Failed to request POM auto-selection cancellation:', err);
                }
            });
        }
        if (controller && !controller.signal.aborted) {
            controller.abort();
        }
    }, []);

    const handleOpenPomModal = useCallback((target = { type: 'all' }) => {
        if (isFeatureMode) {
            setPomSelectionStatus('Captured page objects are attached automatically for feature captures.');
            window.setTimeout(() => setPomSelectionStatus(null), 3000);
            return;
        }
        const normalizedTarget = target || { type: 'all' };
        setPomModalTarget(normalizedTarget);
        if (normalizedTarget.type === 'case' && normalizedTarget.caseKey) {
            setModalSelectedPoms(getCaseSpecificPomSelectionForCaseKey(normalizedTarget.caseKey).poms);
        } else {
            setModalSelectedPoms(commonPomSelections);
        }
        fetchPOMsList();
        setShowPomModal(true);
    }, [commonPomSelections, fetchPOMsList, getCaseSpecificPomSelectionForCaseKey, isFeatureMode]);

    const confirmPomSelection = useCallback(async () => {
        const selected = normalizePomSelectionList(modalSelectedPoms);
        const invalidSelected = selected.filter((pom) => !isAbsoluteHttpUrl(pom.pageUrl));
        if (invalidSelected.length > 0) {
            setPomSelectionError(
                `Add a full Page URL for ${invalidSelected.map((pom) => pom.pageName).join(', ')} before saving the POM selection.`
            );
            return;
        }
        const target = pomModalTarget || { type: 'all' };
        const nextSelections = { ...pomSelectionsByCaseKey };

        if (target.type === 'case' && target.caseKey) {
            if (!selected.length) return;
            nextSelections[target.caseKey] = {
                mode: 'manual',
                poms: selected,
                updated_at: new Date().toISOString(),
            };
            await updateAndPersistPomSelections(nextSelections, 'POM selection updated.');
        } else {
            await updateAndPersistPomSelections(
                nextSelections,
                selected.length ? 'Common POMs applied to all test cases.' : 'Common POMs cleared.',
                { commonPoms: selected }
            );
        }

        setShowPomModal(false);
        setPomSearchTerm('');
    }, [
        modalSelectedPoms,
        pomModalTarget,
        pomSelectionsByCaseKey,
        updateAndPersistPomSelections,
    ]);

    const resetPomSelectionForCase = useCallback(async (testCase) => {
        const nextSelections = { ...pomSelectionsByCaseKey };
        delete nextSelections[testCase._case_key];
        await updateAndPersistPomSelections(nextSelections, 'POM selection reset.');
    }, [pomSelectionsByCaseKey, updateAndPersistPomSelections]);

    const resetAllPomSelections = useCallback(async () => {
        await updateAndPersistPomSelections({}, 'All POM selections reset.', { commonPoms: [] });
    }, [updateAndPersistPomSelections]);

    const loadScriptVersions = useCallback(async (testCaseId, caseKey = null) => {
        if (isFeatureMode) return [];
        const sectionNumber = Number(processingResult?.section_number || 0);
        if (!pdfId || !sectionNumber || !testCaseId) return [];

        try {
            const params = new URLSearchParams({
                artifact_type: 'scripts',
                artifact_kind: 'playwright',
                test_case_id: testCaseId,
            });
            const response = await apiFetch(
                `${API_ENDPOINTS.SECTION_ARTIFACTS(pdfId, sectionNumber)}?${params.toString()}`
            );
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(resolveApiErrorMessage(errorData, 'Failed to load script versions'));
            }

            const payload = await response.json();
            const versions = Array.isArray(payload?.versions) ? payload.versions : [];
            const resolvedActiveVersion = payload?.active_version ?? null;
            const latestArtifact =
                versions.find((artifact) => artifact?.version === resolvedActiveVersion)
                || versions[0]
                || null;
            setScriptVersionsByCaseId((prev) => ({
                ...prev,
                [testCaseId]: versions,
            }));
            if (caseKey && latestArtifact) {
                setScriptSaveMeta((prev) => {
                    const currentMeta = prev?.[caseKey] || {};
                    const editorMeta = resolveScriptEditorMeta(latestArtifact, currentMeta);
                    return {
                        ...prev,
                        [caseKey]: {
                            ...currentMeta,
                            version: latestArtifact?.version ?? null,
                            status: latestArtifact?.status || null,
                            updated_at: latestArtifact?.updated_at || null,
                            save_mode: latestArtifact?.request_payload?.save_mode || 'history',
                            draft_updated_at: currentMeta?.draft_updated_at || null,
                            requires_regeneration: Boolean(latestArtifact?.request_payload?.requires_regeneration || latestArtifact?.status === 'stale'),
                            stale_reason: latestArtifact?.request_payload?.stale_reason || null,
                            missing_poms: Array.isArray(latestArtifact?.request_payload?.missing_poms)
                                ? latestArtifact.request_payload.missing_poms
                                : [],
                            edited_by: editorMeta.edited_by,
                            edited_at: editorMeta.edited_at || latestArtifact?.updated_at || null,
                        },
                    };
                });
            }
            return versions;
        } catch (err) {
            setScriptSaveErrors((prev) => ({
                ...prev,
                [testCaseId]: resolveCaughtErrorMessage(err, 'Failed to load script versions'),
            }));
            return [];
        }
    }, [isFeatureMode, pdfId, processingResult?.section_number]);

    const saveScriptVersion = useCallback(async (testCase, options = {}) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        const testCaseId = getResolvedTestCaseId(testCase);
        const script = String(options?.scriptOverride ?? generatedScripts[caseKey] ?? '').trim();
        if (!caseKey || !testCaseId || !script) return null;
        if (isFeatureMode && !featureSessionId) {
            throw new Error('Open this feature from its application dashboard before saving the script.');
        }
        const autosave = Boolean(options?.autosave);
        const caseSelectedPoms = getSelectedPomsForCaseKey(caseKey);
        const {
            localNames: selectedPomNames,
            globalNames: selectedGlobalPomNames,
            applicationNames: selectedApplicationPomNames,
        } = splitPomsByScope(caseSelectedPoms);

        setSavingScriptKeys((prev) => new Set([...prev, caseKey]));
        setScriptSaveErrors((prev) => ({ ...prev, [caseKey]: null, [testCaseId]: null }));
        try {
            const response = await apiFetch(
                isFeatureMode
                    ? API_ENDPOINTS.FEATURE_CAPTURE_SCRIPT(featureSessionId, testCaseId)
                    : API_ENDPOINTS.SAVE_SCRIPT,
            {
                method: isFeatureMode ? 'PATCH' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                keepalive: Boolean(options?.keepalive),
                body: JSON.stringify(isFeatureMode ? {
                    playwright_script: script,
                } : {
                    pdf_id: pdfId,
                    section_number: processingResult?.section_number,
                    test_case_id: testCaseId,
                    test_case: autosave
                        ? buildDraftSaveTestCase(testCase, processingResult?.section_number)
                        : sanitizeTestCaseForGeneration(testCase),
                    playwright_script: script,
                    assumptions: scriptAssumptions[caseKey] || [],
                    warnings: scriptWarnings[caseKey] || [],
                    model: scriptModels[caseKey] || null,
                    save_mode: autosave ? 'autosave' : 'manual',
                    pom_name: selectedPomNames[0] || null,
                    pom_names: selectedPomNames,
                    global_pom_names: selectedGlobalPomNames,
                    application_pom_names: selectedApplicationPomNames,
                    script_flow: isApiScriptFlow ? 'api' : 'ui',
                    source_type: isApiScriptFlow ? 'openapi' : undefined,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(resolveApiErrorMessage(errorData, 'Failed to save script'));
            }

            const payload = await response.json();
            const featureScriptItem = isFeatureMode
                ? (payload?.scripts?.items || []).find(
                    (item) => String(item?.test_case_id || '').trim() === testCaseId
                )
                : null;
            setScriptSaveMeta((prev) => {
                const currentMeta = prev?.[caseKey] || {};
                const sourceArtifact = isFeatureMode
                    ? featureScriptItem
                    : (autosave ? payload?.draft : payload?.artifact);
                const editorMeta = resolveScriptEditorMeta(sourceArtifact, currentMeta);
                return {
                    ...prev,
                    [caseKey]: {
                        ...currentMeta,
                        version: isFeatureMode
                            ? null
                            : autosave
                            ? (currentMeta?.version ?? null)
                            : (payload?.artifact?.version ?? null),
                        status: isFeatureMode
                            ? null
                            : autosave
                            ? (currentMeta?.status ?? null)
                            : (payload?.artifact?.status || null),
                        updated_at: isFeatureMode
                            ? (featureScriptItem?.updated_at || payload?.updated_at || new Date().toISOString())
                            : autosave
                            ? (currentMeta?.updated_at ?? null)
                            : (payload?.artifact?.updated_at || null),
                        save_mode: isFeatureMode ? 'feature_capture' : (autosave ? 'draft' : 'manual'),
                        draft_updated_at: isFeatureMode
                            ? null
                            : autosave
                            ? (payload?.draft?.updated_at || new Date().toISOString())
                            : null,
                        requires_regeneration: isFeatureMode
                            ? false
                            : autosave
                            ? Boolean(currentMeta?.requires_regeneration)
                            : Boolean(payload?.artifact?.request_payload?.requires_regeneration),
                        stale_reason: isFeatureMode
                            ? null
                            : autosave
                            ? (currentMeta?.stale_reason || null)
                            : (payload?.artifact?.request_payload?.stale_reason || null),
                        missing_poms: isFeatureMode || autosave
                            ? (Array.isArray(currentMeta?.missing_poms) ? currentMeta.missing_poms : [])
                            : (
                                Array.isArray(payload?.artifact?.request_payload?.missing_poms)
                                    ? payload.artifact.request_payload.missing_poms
                                    : []
                            ),
                        edited_by: editorMeta.edited_by,
                        edited_at: editorMeta.edited_at || currentMeta?.edited_at || null,
                    },
                };
            });
            setDirtyScriptKeys((prev) => {
                const latestScript = generatedScriptsRef.current?.[caseKey];
                const next = new Set(prev);
                if (latestScript === script) {
                    next.delete(caseKey);
                }
                return next;
            });
            if (isFeatureMode) {
                onFeatureSessionChange?.(payload, { message: null });
            } else if (!autosave) {
                await loadScriptVersions(testCaseId, caseKey);
            }
            return payload;
        } catch (err) {
            setScriptSaveErrors((prev) => ({
                ...prev,
                [caseKey]: resolveCaughtErrorMessage(err, 'Failed to save script'),
            }));
            throw err;
        } finally {
            setSavingScriptKeys((prev) => {
                const next = new Set(prev);
                next.delete(caseKey);
                return next;
            });
        }
    }, [
        generatedScripts,
        getSelectedPomsForCaseKey,
        featureSessionId,
        isFeatureMode,
        isApiScriptFlow,
        loadScriptVersions,
        onFeatureSessionChange,
        pdfId,
        processingResult?.section_number,
        scriptAssumptions,
        scriptModels,
        scriptWarnings,
    ]);

    const requestScriptSave = async (testCase) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        const currentVersion = scriptSaveMeta[caseKey]?.version ?? null;
        const currentSavedAt = scriptSaveMeta[caseKey]?.updated_at || null;
        const hasUnsavedChanges = dirtyScriptKeys.has(caseKey);

        if (!caseKey || !generatedScripts[caseKey] || savingScriptKeys.has(caseKey)) return;
        if (!hasUnsavedChanges && (currentVersion != null || currentSavedAt)) {
            setEditModes((prev) => {
                const next = new Set(prev);
                next.delete(caseKey);
                return next;
            });
            return;
        }
        try {
            await saveScriptVersion(testCase, { autosave: false });
            setEditModes((prev) => {
                const next = new Set(prev);
                next.delete(caseKey);
                return next;
            });
        } catch (err) {
            // saveScriptVersion already exposes the error beside the editor.
        }
    };

    const toggleVersionPanel = async (testCase) => {
        if (isFeatureMode) return;
        const testCaseId = getResolvedTestCaseId(testCase);
        if (!testCaseId) return;

        const caseKey = testCase?._case_key || testCase?.test_case_no;
        setVersionPanels((prev) => {
            const next = new Set(prev);
            if (next.has(caseKey)) {
                next.delete(caseKey);
            } else {
                next.add(caseKey);
            }
            return next;
        });

        if (!scriptVersionsByCaseId[testCaseId]) {
            await loadScriptVersions(testCaseId, caseKey);
        }
    };

    const toggleScreenshotPanel = (caseKey) => {
        setExpandedScreenshotPanels((prev) => {
            const next = new Set(prev);
            if (next.has(caseKey)) {
                next.delete(caseKey);
            } else {
                next.add(caseKey);
            }
            return next;
        });
    };

    const refreshHtmlReportLink = async (caseKey, runId, reportIndex = 0) => {
        if (!caseKey || !runId || !pdfId) return null;
        const refreshKey = `${caseKey}-${runId}-${reportIndex}`;
        setRefreshingReportKeys((prev) => new Set([...prev, refreshKey]));
        setRunErrors((prev) => ({ ...prev, [caseKey]: null }));

        try {
            const response = await apiFetch(API_ENDPOINTS.PLAYWRIGHT_REPORT_LINK(runId, pdfId), {
                method: 'GET',
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(resolveApiErrorMessage(errorData, 'Failed to refresh report link'));
            }

            const data = await response.json();
            const refreshedReport = data?.html_report || null;
            if (!refreshedReport) {
                throw new Error('Report link is unavailable for this run');
            }

            setRunResults((prev) => {
                const current = prev?.[caseKey];
                if (!current) return prev;
                const runs = Array.isArray(current.runs) && current.runs.length > 0
                    ? current.runs.map((item) => ({ ...item }))
                    : [{ ...current }];
                const targetIndex = runs.findIndex((item) => item?.run_id === runId);
                const resolvedIndex = targetIndex >= 0 ? targetIndex : reportIndex;
                if (!runs[resolvedIndex]) return prev;

                runs[resolvedIndex] = {
                    ...runs[resolvedIndex],
                    html_report: refreshedReport,
                    report_url: data?.report_url || refreshedReport.viewer_url || runs[resolvedIndex]?.report_url || null,
                };
                const htmlReports = runs
                    .map((item) => resolveRunHtmlReport(item))
                    .filter((item) => item && getHtmlReportUrl(item));

                return {
                    ...prev,
                    [caseKey]: {
                        ...current,
                        runs,
                        html_reports: htmlReports,
                        html_report: htmlReports[0] || null,
                        report_url: getHtmlReportUrl(htmlReports[0] || {}) || null,
                    },
                };
            });

            return refreshedReport;
        } catch (err) {
            setRunErrors((prev) => ({
                ...prev,
                [caseKey]: resolveCaughtErrorMessage(err, 'Failed to refresh report link'),
            }));
            return null;
        } finally {
            setRefreshingReportKeys((prev) => {
                const next = new Set(prev);
                next.delete(refreshKey);
                return next;
            });
        }
    };

    /**
     * Get display label for test case category
     */
    const getCategoryLabel = (category) => {
        switch (category) {
            case 'positive':
                return 'Positive Test Case';
            case 'negative':
                return 'Negative Test Case';
            default:
                return 'Test Case';
        }
    };

    /**
     * Get badge color for category
     */
    const getCategoryBadgeClass = (category) => {
        switch (category) {
            case 'positive':
                return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'negative':
                return 'bg-rose-50 text-rose-700 border-rose-200';
            default:
                return 'bg-slate-100 text-slate-700 border-slate-200';
        }
    };

    const getFilterDisplayLabel = (filterKey) => {
        switch (filterKey) {
            case 'manual_positive':
                return 'manual positive';
            case 'manual_negative':
                return 'manual negative';
            case 'positive':
                return 'positive';
            case 'negative':
                return 'negative';
            default:
                return 'test';
        }
    };

    // Handle generating script for a single test case
    const USER_INPUT_PLACEHOLDER_REGEX = /\{\{?\s*USER_INPUT\s*:\s*([^}\n]+?)\s*\}\}?/g;
    const automationAuthMetadata = automationAuthState?.metadata || {};
    const automationLoginReplayAvailable = Boolean(automationAuthMetadata?.login_replay_available);

    const canonicalizeInputKey = (value = '') => {
        let text = String(value || '')
            .trim()
            .replace(/[“”]/g, '"')
            .replace(/[‘’]/g, "'")
            .toLowerCase();
        if (!text) return '';

        const locatorArgumentMatch = text.match(
            /(?:getby(?:label|placeholder|testid|text)|locator)\(\s*['"`]([^'"`]+)['"`]/
        );
        if (locatorArgumentMatch?.[1]) {
            text = locatorArgumentMatch[1].trim().toLowerCase();
        }

        text = text
            .replace(/^\s*(?:css=|id=|name=|testid=|data-testid=)\s*/, '')
            .replace(/^['"`]+|['"`]+$/g, '')
            .trim();

        const attributeMatch = text.match(
            /\[\s*(?:id|name|data-testid|data-test|data-qa|placeholder|aria-label)\s*=\s*['"`]?([^'"`\]]+)/
        );
        if (attributeMatch?.[1]) {
            text = attributeMatch[1].trim().toLowerCase();
        } else {
            const idMatch = text.match(/#([a-z0-9_-]+)/);
            if (idMatch?.[1]) {
                text = idMatch[1].trim().toLowerCase();
            }
        }

        return text
            .replace(/^[#.@~]+/, '')
            .replace(/\s+/g, ' ')
            .trim();
    };

    const normalizeInputKey = (value = '') =>
        canonicalizeInputKey(value).replace(/[^a-z0-9]+/g, '');

    const getInputKeyCandidates = (value = '') => {
        const compact = normalizeInputKey(value);
        if (!compact) return [];
        const candidates = new Set([compact]);
        const withoutUiSuffix = compact.replace(/(?:input|field|textbox|control|locator|selector)$/g, '');
        if (withoutUiSuffix) candidates.add(withoutUiSuffix);
        return Array.from(candidates);
    };

    const resolveInputValue = (values = {}, lookupKey = '') => {
        if (!values || typeof values !== 'object') return '';
        const rawLookup = String(lookupKey || '').trim();
        if (!rawLookup) return '';

        const directValue = values[rawLookup];
        if (typeof directValue === 'string' && directValue.trim().length > 0) return directValue;

        const lookupCandidates = getInputKeyCandidates(rawLookup);
        let blankCandidate = typeof directValue === 'string' ? directValue : '';
        for (const [key, value] of Object.entries(values)) {
            const keyCandidates = getInputKeyCandidates(key);
            if (!keyCandidates.some((candidate) => lookupCandidates.includes(candidate))) continue;
            if (typeof value !== 'string') continue;
            if (value.trim().length > 0) return value;
            blankCandidate = value;
        }
        return blankCandidate;
    };

    const hasResolvedInputValue = (values = {}, lookupKey = '') =>
        String(resolveInputValue(values, lookupKey) || '').trim().length > 0;

    const isLoginCredentialInputKey = (value = '') => {
        const compact = normalizeInputKey(value);
        if (!compact) return false;
        return /(?:password|passcode|passphrase|credential|secret|otp|mfa|totp|pin|token|verificationcode|email|emailaddress|username|loginid|loginemail|userid|phone|mobile)/.test(compact);
    };

    const canUseAutomationLoginReplayForInputs = (inputs = []) => (
        automationLoginReplayAvailable
        && Array.isArray(inputs)
        && inputs.length > 0
        && inputs.every((inputKey) => isLoginCredentialInputKey(inputKey))
    );

    const buildMissingInputMessage = (inputs = []) => {
        const labels = (Array.isArray(inputs) ? inputs : [])
            .map((inputKey) => String(inputKey || '').trim())
            .filter(Boolean);
        const joined = labels.join(', ');
        if (labels.length > 0 && labels.every((inputKey) => isLoginCredentialInputKey(inputKey))) {
            return `Login credential value(s) are missing for ${joined}. Open Environment, capture and save the login state, then run again.`;
        }
        return `Missing required input value(s): ${joined}. Add values to the test case input sets before running.`;
    };

    const getRequiredInputsFromScript = (script = '') => {
        const required = new Set();
        let match;
        USER_INPUT_PLACEHOLDER_REGEX.lastIndex = 0;
        while ((match = USER_INPUT_PLACEHOLDER_REGEX.exec(script)) !== null) {
            const key = (match[1] || '').trim();
            if (key) required.add(key);
        }
        return Array.from(required);
    };

    const applyUserInputsToScript = (script = '', values = {}) => {
        return script.replace(USER_INPUT_PLACEHOLDER_REGEX, (fullMatch, key) => {
            const lookupKey = (key || '').trim();
            const value = resolveInputValue(values, lookupKey);
            if (typeof value === 'string' && value.length > 0) return value;
            return fullMatch;
        });
    };

    /**
     * Ask the backend whether the selected POMs actually cover every step of a
     * test case. Returns the report, or null when the check could not run.
     *
     * This is the gate that stops the generator from inventing a selector: when
     * a step has no POM member, generation is blocked and the user is told which
     * element to scan or add.
     */
    const checkPomCoverageForCase = useCallback(async (testCase) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        const caseSelectedPoms = getSelectedPomsForCaseKey(caseKey);
        const { localNames, globalNames, applicationNames } = splitPomsByScope(caseSelectedPoms);
        if (!localNames.length && !globalNames.length && !applicationNames.length) return null;

        setCheckingPomCoverage((prev) => new Set([...prev, caseKey]));
        try {
            const response = await apiFetch(API_ENDPOINTS.PLAYWRIGHT_POM_COVERAGE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pdf_id: pdfId,
                    section_number: processingResult?.section_number,
                    test_cases: [sanitizeTestCaseForGeneration(testCase)],
                    pom_names: localNames,
                    global_pom_names: globalNames,
                    application_pom_names: applicationNames,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(resolveApiErrorMessage(errorData, 'Failed to check POM coverage'));
            }
            const payload = await response.json();
            const report = (payload?.reports || [])[0] || { ready: true, missing_elements: [] };
            setPomCoverageByCaseKey((prev) => ({ ...prev, [caseKey]: report }));
            return report;
        } finally {
            setCheckingPomCoverage((prev) => {
                const next = new Set(prev);
                next.delete(caseKey);
                return next;
            });
        }
    }, [getSelectedPomsForCaseKey, pdfId, processingResult?.section_number]);

    const generateScriptForCase = async (testCase) => {
        const caseKey = testCase._case_key || testCase.test_case_no;
        const testCaseId = getResolvedTestCaseId(testCase);

        if (!pomSelectionsLoaded) {
            setErrors(prev => ({
                ...prev,
                [caseKey]: 'Automation setup is still loading. Please try again in a moment.'
            }));
            return;
        }

        const caseSelectedPoms = getSelectedPomsForCaseKey(caseKey);
        const {
            localNames: selectedPomNames,
            globalNames: selectedGlobalPomNames,
            applicationNames: selectedApplicationPomNames,
        } = splitPomsByScope(caseSelectedPoms);

        // Validate POM selection for UI automation. API scripts are generated from the contract.
        if (!isApiScriptFlow && caseSelectedPoms.length === 0) {
            setErrors(prev => ({
                ...prev,
                [caseKey]: 'Please select or auto-select at least one Page Object Model (POM) before generating a script.'
            }));
            return;
        }

        setLoadingCases(prev => new Set([...prev, caseKey]));
        setErrors(prev => ({ ...prev, [caseKey]: null }));

        try {
            if (strictPomMode && !isApiScriptFlow && !isFeatureMode) {
                const coverage = await checkPomCoverageForCase(testCase);
                if (coverage && coverage.ready === false) {
                    throw new Error(
                        `The selected POMs are missing ${coverage.missing_elements.length} element(s) `
                        + 'this test case needs. Scan the page or add them, then generate again.'
                    );
                }
            }

            if (isFeatureMode) {
                if (!featureSessionId) {
                    throw new Error('Missing feature capture session. Load or create a captured flow first.');
                }
                if (!testCaseId) {
                    throw new Error('Missing test case id for script generation.');
                }

                const submitted = await submitJobOperation(API_ENDPOINTS.FEATURE_CAPTURE_GENERATE_SCRIPTS_QUEUE(featureSessionId), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        test_case_ids: [testCaseId],
                        regenerate: true,
                        script_notes: featureScriptNotes,
                    }),
                }, { errorMessage: 'Failed to queue script generation' });
                setScriptBatchJob(submitted.job);
                const snapshot = await waitForJobResult(submitted.job, {
                    onUpdate: ({ job, items }) => {
                        setScriptBatchJob(job);
                        setScriptBatchItems(items || []);
                    },
                });
                setScriptBatchJob(snapshot.job);
                setScriptBatchItems(snapshot.items || []);

                const response = await apiFetch(API_ENDPOINTS.FEATURE_CAPTURE_BY_ID(featureSessionId));
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(resolveApiErrorMessage(errorData, 'Failed to reload generated script'));
                }

                const payload = await response.json();
                if (onFeatureSessionChange) {
                    onFeatureSessionChange(payload);
                }
                const scriptItems = Array.isArray(payload?.scripts?.items) ? payload.scripts.items : [];
                const savedScriptsById = new Map(scriptItems.map((scriptItem) => [
                    String(scriptItem?.test_case_id || '').trim(),
                    String(scriptItem?.playwright_script || '').trim(),
                ]));
                const currentScripts = {};
                getNormalizedTestCases().forEach((row) => {
                    const script = savedScriptsById.get(getResolvedTestCaseId(row));
                    if (script) currentScripts[row._case_key] = script;
                });
                setGeneratedScripts(currentScripts);
                const item = scriptItems.find((scriptItem) => (
                    String(scriptItem?.test_case_id || '').trim() === testCaseId
                )) || scriptItems.find((scriptItem) => scriptItem?.playwright_script);
                const generatedScript = String(item?.playwright_script || payload?.scripts?.spec || '').trim();
                if (!generatedScript) {
                    throw new Error('Feature script generation did not return a usable Playwright script.');
                }

                setScriptModels(prev => ({
                    ...prev,
                    [caseKey]: item?.model || null,
                }));
                setScriptAssumptions(prev => ({
                    ...prev,
                    [caseKey]: Array.isArray(item?.assumptions) ? item.assumptions : [],
                }));
                setScriptWarnings(prev => ({
                    ...prev,
                    [caseKey]: Array.isArray(item?.warnings) ? item.warnings : [],
                }));
                setScriptSaveMeta(prev => ({
                    ...prev,
                    [caseKey]: {
                        ...(prev?.[caseKey] || {}),
                        version: null,
                        updated_at: item?.generated_at || new Date().toISOString(),
                        save_mode: 'feature_capture',
                        draft_updated_at: null,
                    },
                }));

                const requiredInputs = getRequiredInputsFromScript(generatedScript);
                const defaultInputs = getUserInputsMapFromTestCase(testCase);
                setScriptInputValues(prev => {
                    const existing = prev[caseKey] || {};
                    const next = { ...existing };
                    requiredInputs.forEach((inputKey) => {
                        if (typeof next[inputKey] !== 'string') {
                            next[inputKey] = resolveInputValue(defaultInputs, inputKey) || '';
                        }
                    });
                    return {
                        ...prev,
                        [caseKey]: {
                            ...defaultInputs,
                            ...next
                        }
                    };
                });

                setExpandedCase(caseKey);
                setActiveInspectorTab('script');
                return true;
            }

            const payload = {
                test_case: sanitizeTestCaseForGeneration(testCase),
                script_type: scriptType,
                pdf_id: pdfId,
                section_number: processingResult?.section_number,
                test_case_id: testCaseId,
                pom_name: selectedPomNames[0] || null,
                pom_names: selectedPomNames,
                global_pom_names: selectedGlobalPomNames,
                application_pom_names: selectedApplicationPomNames,
                script_flow: isApiScriptFlow ? 'api' : 'ui',
                source_type: isApiScriptFlow ? 'openapi' : undefined,
                strict_pom: strictPomMode && !isApiScriptFlow ? true : undefined,
            };

            const data = await queueWorkerRequest(
                API_ENDPOINTS.GENERATE_SCRIPT_QUEUE,
                payload,
                'Failed to generate script'
            );

            setGeneratedScripts(prev => ({
                ...prev,
                [caseKey]: data.playwright_script || data.generated_script || '',
            }));
            setScriptModels(prev => ({
                ...prev,
                [caseKey]: data.model || null,
            }));

            const generatedScript = data.playwright_script || data.generated_script || '';
            const requiredInputs = getRequiredInputsFromScript(generatedScript);
            const defaultInputs = getUserInputsMapFromTestCase(testCase);
            setScriptInputValues(prev => {
                const existing = prev[caseKey] || {};
                const next = { ...existing };
                requiredInputs.forEach((inputKey) => {
                    if (typeof next[inputKey] !== 'string') {
                        next[inputKey] = resolveInputValue(defaultInputs, inputKey) || '';
                    }
                });
                return {
                    ...prev,
                    [caseKey]: {
                        ...defaultInputs,
                        ...next
                    }
                };
            });

            // Store assumptions and warnings
            setScriptAssumptions(prev => ({
                ...prev,
                [caseKey]: data.assumptions || []
            }));

            setScriptWarnings(prev => ({
                ...prev,
                [caseKey]: data.warnings || []
            }));

            setExpandedCase(caseKey);
            await loadScriptVersions(testCaseId, caseKey);
            return true;
        } catch (err) {
            setErrors(prev => ({
                ...prev,
                [caseKey]: resolveCaughtErrorMessage(err, 'Failed to generate script'),
            }));
            return false;
        } finally {
            setLoadingCases(prev => {
                const newSet = new Set(prev);
                newSet.delete(caseKey);
                return newSet;
            });
        }
    };

    // Handle generating scripts for selected test cases
    const generateSelectedScripts = async () => {
        if (!pomSelectionsLoaded) {
            setErrors(prev => ({
                ...prev,
                bulk: 'Automation setup is still loading. Please try again in a moment.'
            }));
            return;
        }

        const cases = getFilteredTestCases(categoryFilter)
            .filter(tc => selectedCases.has(tc._case_key));
        const casesWithoutPoms = isApiScriptFlow
            ? []
            : cases.filter((testCase) => getSelectedPomsForCaseKey(testCase._case_key).length === 0);

        if (cases.length > MAX_SCRIPT_GENERATION_CASES) {
            setErrors(prev => ({
                ...prev,
                bulk: `Select up to ${MAX_SCRIPT_GENERATION_CASES} test cases to generate scripts.`
            }));
            return;
        }

        // Validate POM selection for UI automation.
        if (casesWithoutPoms.length > 0) {
            setErrors(prev => ({
                ...prev,
                bulk: `${casesWithoutPoms.length} selected test case${casesWithoutPoms.length === 1 ? '' : 's'} need POM selection before generating scripts.`
            }));
            return;
        }

        setGenerateAllStatus('loading');
        setErrors(prev => ({ ...prev, bulk: null }));

        try {
            const endpoint = isFeatureMode
                ? API_ENDPOINTS.FEATURE_CAPTURE_GENERATE_SCRIPTS_QUEUE(featureSessionId)
                : API_ENDPOINTS.PLAYWRIGHT_GENERATE_BATCH;
            const body = isFeatureMode ? {
                test_case_ids: cases.map(getResolvedTestCaseId), regenerate: true, script_notes: featureScriptNotes,
            } : {
                pdf_id: pdfId,
                section_number: processingResult?.section_number,
                items: cases.map((testCase) => {
                    const selected = getSelectedPomsForCaseKey(testCase._case_key);
                    const split = splitPomsByScope(selected);
                    return { request: {
                        test_case: sanitizeTestCaseForGeneration(testCase), script_type: scriptType,
                        test_case_id: getResolvedTestCaseId(testCase),
                        pom_names: split.localNames, global_pom_names: split.globalNames,
                        application_pom_names: split.applicationNames,
                        script_flow: isApiScriptFlow ? 'api' : 'ui', source_type: isApiScriptFlow ? 'openapi' : undefined,
                        strict_pom: strictPomMode && !isApiScriptFlow ? true : undefined,
                    }};
                }),
            };
            const submitted = await submitJobOperation(endpoint, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
            }, { errorMessage: 'Failed to queue selected scripts' });
            setScriptBatchJob(submitted.job);
            setLoadingCases(new Set(cases.map(testCase => testCase._case_key)));
            const snapshot = await waitForJobResult(submitted.job, {
                onUpdate: ({ job, items }) => {
                    setScriptBatchJob(job);
                    setScriptBatchItems(items || []);
                    const activeIds = new Set((items || []).filter(item => ['queued', 'running'].includes(item.status)).map(item => String(item.test_case_id || item.item_key)));
                    setLoadingCases(new Set(cases.filter(testCase => activeIds.has(getResolvedTestCaseId(testCase))).map(testCase => testCase._case_key)));
                    const itemErrors = {};
                    (items || []).forEach(item => {
                        if (item.status === 'failed') {
                            const match = cases.find(testCase => getResolvedTestCaseId(testCase) === String(item.test_case_id || item.item_key));
                            if (match) itemErrors[match._case_key] = item.error?.message || 'Script generation failed.';
                        }
                    });
                    if (Object.keys(itemErrors).length) setErrors(prev => ({ ...prev, ...itemErrors }));
                },
            });
            setScriptBatchJob(snapshot.job);
            setScriptBatchItems(snapshot.items || []);
            const resultPayload = await loadJobResult(snapshot.job).catch(() => ({ items: [] }));
            const resultItems = Array.isArray(resultPayload?.items) ? resultPayload.items : [resultPayload];
            resultItems.forEach(data => {
                const testCase = cases.find(row => getResolvedTestCaseId(row) === String(data?.test_case_id || ''));
                if (!testCase || !data?.playwright_script) return;
                const key = testCase._case_key;
                setGeneratedScripts(prev => ({ ...prev, [key]: data.playwright_script }));
                setScriptModels(prev => ({ ...prev, [key]: data.model || null }));
                setScriptAssumptions(prev => ({ ...prev, [key]: data.assumptions || [] }));
                setScriptWarnings(prev => ({ ...prev, [key]: data.warnings || [] }));
            });
            setGenerateAllStatus(snapshot.job.status === 'partial_completed' ? 'partial' : 'success');
            if (isFeatureMode && onFeatureSessionChange) onFeatureSessionChange({ refresh_requested: true, session_id: featureSessionId });
        } catch (err) {
            setGenerateAllStatus('error');
            setErrors(prev => ({
                ...prev,
                bulk: resolveCaughtErrorMessage(err, 'Failed to generate selected scripts'),
            }));
        } finally {
            setLoadingCases(new Set());
        }
    };

    const cancelScriptBatch = async () => {
        if (!scriptBatchJob) return;
        try {
            setScriptBatchJob(await cancelJob(scriptBatchJob));
        } catch (err) {
            setErrors(prev => ({ ...prev, bulk: resolveCaughtErrorMessage(err, 'Unable to cancel pending scripts') }));
        }
    };

    const retryFailedScripts = async () => {
        if (!scriptBatchJob) return;
        try {
            const retried = await retryJob(scriptBatchJob);
            setScriptBatchJob(retried);
            setGenerateAllStatus('loading');
            const snapshot = await waitForJobResult(retried, {
                onUpdate: ({ job, items }) => { setScriptBatchJob(job); setScriptBatchItems(items || []); },
            });
            setScriptBatchJob(snapshot.job);
            setScriptBatchItems(snapshot.items || []);
            setGenerateAllStatus(snapshot.job.status === 'partial_completed' ? 'partial' : 'success');
        } catch (err) {
            setErrors(prev => ({ ...prev, bulk: resolveCaughtErrorMessage(err, 'Unable to retry failed scripts') }));
            setGenerateAllStatus('error');
        }
    };

    // Handle running a generated script for a single test case
    const runScriptForCase = async (testCase) => {
        const caseKey = testCase._case_key || testCase.test_case_no;
        const testCaseId = getResolvedTestCaseId(testCase);
        const script = generatedScripts[caseKey];
        if (!script) return;
        const staleMeta = getScriptStaleMeta(caseKey);
        if (staleMeta.isStale) {
            setRunErrors(prev => ({
                ...prev,
                [caseKey]: staleMeta.message || 'Regenerate this script before running.',
            }));
            setExpandedCase(caseKey);
            setActiveInspectorTab('script');
            return;
        }

        const requiredInputs = getRequiredInputsFromScript(script);
        const resolvedSets = resolveUserInputSets(testCase);
        const setsToRun = resolvedSets.length > 0
            ? resolvedSets
            : [{ set_name: 'Set 1', set_type: 'primary', user_inputs: [] }];
        const baseInputOverrides = {
            ...getUserInputsMapFromTestCase(testCase),
            ...(scriptInputValues[caseKey] || {}),
        };
        const caseSelectedPoms = getSelectedPomsForCaseKey(caseKey);
        const {
            localNames: selectedPomNames,
            globalNames: selectedGlobalPomNames,
            applicationNames: selectedApplicationPomNames,
        } = splitPomsByScope(caseSelectedPoms);

        setRunningCases(prev => new Set([...prev, caseKey]));
        setRunErrors(prev => ({ ...prev, [caseKey]: null }));
        setExpandedCase(caseKey);
        setActiveInspectorTab('run');

        try {
            const perSetRuns = [];
            const runnableSets = [];
            for (let index = 0; index < setsToRun.length; index += 1) {
                const setItem = setsToRun[index] || {};
                const setName = String(setItem?.set_name || `Set ${index + 1}`).trim() || `Set ${index + 1}`;
                const setType = String(setItem?.set_type || '').trim();
                const setLabel = setType ? `${setName} (${setType})` : setName;
                const setInputs = {
                    ...baseInputOverrides,
                    ...asInputMapFromPairs(setItem?.user_inputs || []),
                };

                const missingInputs = requiredInputs.filter((inputKey) => !hasResolvedInputValue(setInputs, inputKey));
                const missingInputsRequiringValues = missingInputs.filter(
                    (inputKey) => !canUseAutomationLoginReplayForInputs([inputKey])
                );
                if (missingInputsRequiringValues.length > 0) {
                    perSetRuns.push({
                        set_index: index,
                        set_name: setName,
                        set_type: setType,
                        set_label: setLabel,
                        run_id: null,
                        exit_code: 1,
                        duration_ms: 0,
                        stdout: '',
                        stderr: buildMissingInputMessage(missingInputsRequiringValues),
                    });
                    continue;
                }

                const resolvedScript = applyUserInputsToScript(script, setInputs);
                const unresolvedInputs = getRequiredInputsFromScript(resolvedScript);
                const unresolvedInputsRequiringValues = unresolvedInputs.filter(
                    (inputKey) => !canUseAutomationLoginReplayForInputs([inputKey])
                );
                if (unresolvedInputsRequiringValues.length > 0) {
                    perSetRuns.push({
                        set_index: index,
                        set_name: setName,
                        set_type: setType,
                        set_label: setLabel,
                        run_id: null,
                        exit_code: 1,
                        duration_ms: 0,
                        stdout: '',
                        stderr: buildMissingInputMessage(unresolvedInputsRequiringValues),
                    });
                    continue;
                }
                runnableSets.push({
                    id: setItem?.id || index,
                    input_set_id: setItem?.id || index,
                    set_index: index,
                    set_name: setName,
                    set_type: setType,
                    set_label: setLabel,
                    playwright_script: resolvedScript,
                });
            }

            if (runnableSets.length) {
                if (isFeatureMode && !featureSessionId) {
                    throw new Error('Missing feature capture session. Load or create a captured flow first.');
                }
                const payload = {
                    ...(isFeatureMode ? {} : { pdf_id: pdfId, section_number: processingResult?.section_number }),
                    test_case_id: testCaseId,
                    base_url: processingResult?.start_url || processingResult?.application_url || '',
                    playwright_script: script,
                    pom_names: selectedPomNames,
                    global_pom_names: selectedGlobalPomNames,
                    application_pom_names: selectedApplicationPomNames,
                    action_delay_ms: actionDelayEnabled ? actionDelayMs : 0,
                    input_sets: runnableSets,
                };
                const result = await queueWorkerRequest(
                    isFeatureMode
                        ? API_ENDPOINTS.FEATURE_CAPTURE_RUN_SCRIPT_QUEUE(featureSessionId)
                        : API_ENDPOINTS.RUN_PLAYWRIGHT_QUEUE,
                    payload,
                    'Failed to run script'
                );
                const completedRuns = Array.isArray(result?.items) ? result.items : [result];
                completedRuns.forEach((run, resultIndex) => {
                    const inputSetId = String(run?.input_set_id ?? '');
                    const sourceSet = runnableSets.find((setItem) => String(setItem.input_set_id) === inputSetId)
                        || runnableSets[resultIndex]
                        || {};
                    perSetRuns.push({ ...sourceSet, ...run });
                });
            }

            const nextRunOutput = buildRunOutputFromRuns(perSetRuns);

            setRunResults(prev => ({
                ...prev,
                [caseKey]: nextRunOutput || { runs: perSetRuns, exit_code: 1 }
            }));
            setRunErrors(prev => ({
                ...prev,
                [caseKey]: null,
            }));
        } catch (err) {
            setRunErrors(prev => ({
                ...prev,
                [caseKey]: resolveCaughtErrorMessage(err, 'Failed to run script'),
            }));
        } finally {
            setRunningCases(prev => {
                const newSet = new Set(prev);
                newSet.delete(caseKey);
                return newSet;
            });
        }
    };

    const runSelectedScripts = async () => {
        setRunAllStatus('loading');
        const cases = getFilteredTestCases(categoryFilter)
            .filter(tc => selectedCases.has(tc._case_key));

        try {
            const runs = [];
            const localRunsByCase = {};
            const runnableCases = cases.filter(testCase => generatedScripts[testCase._case_key] && !getScriptStaleMeta(testCase._case_key).isStale);
            runnableCases.forEach((testCase) => {
                const caseKey = testCase._case_key || testCase.test_case_no;
                const testCaseId = getResolvedTestCaseId(testCase);
                const script = generatedScripts[caseKey];
                const requiredInputs = getRequiredInputsFromScript(script);
                const resolvedSets = resolveUserInputSets(testCase);
                const setsToRun = resolvedSets.length > 0 ? resolvedSets : [{ set_name: 'Set 1', set_type: 'primary', user_inputs: [] }];
                const baseInputOverrides = { ...getUserInputsMapFromTestCase(testCase), ...(scriptInputValues[caseKey] || {}) };
                const runnableSets = [];
                const localRuns = [];
                setsToRun.forEach((setItem = {}, index) => {
                    const setName = String(setItem?.set_name || `Set ${index + 1}`).trim() || `Set ${index + 1}`;
                    const setType = String(setItem?.set_type || '').trim();
                    const setLabel = setType ? `${setName} (${setType})` : setName;
                    const setInputs = { ...baseInputOverrides, ...asInputMapFromPairs(setItem?.user_inputs || []) };
                    const resolvedScript = applyUserInputsToScript(script, setInputs);
                    const missingInputs = requiredInputs
                        .filter((inputKey) => !hasResolvedInputValue(setInputs, inputKey))
                        .concat(getRequiredInputsFromScript(resolvedScript))
                        .filter((inputKey, position, values) => values.indexOf(inputKey) === position)
                        .filter((inputKey) => !canUseAutomationLoginReplayForInputs([inputKey]));
                    if (missingInputs.length) {
                        localRuns.push({
                            set_index: index, set_name: setName, set_type: setType, set_label: setLabel,
                            run_id: null, exit_code: 1, duration_ms: 0, stdout: '',
                            stderr: buildMissingInputMessage(missingInputs),
                        });
                        return;
                    }
                    runnableSets.push({
                        id: setItem?.id || index, input_set_id: setItem?.id || index,
                        set_index: index, set_name: setName, set_type: setType,
                        set_label: setLabel, playwright_script: resolvedScript,
                    });
                });
                localRunsByCase[caseKey] = { testCase, runnableSets, localRuns };
                if (!runnableSets.length) return;
                const split = splitPomsByScope(getSelectedPomsForCaseKey(caseKey));
                runs.push({
                    test_case_id: testCaseId,
                    base_url: processingResult?.start_url || processingResult?.application_url || '',
                    playwright_script: script,
                    pom_names: split.localNames,
                    global_pom_names: split.globalNames,
                    application_pom_names: split.applicationNames,
                    action_delay_ms: actionDelayEnabled ? actionDelayMs : 0,
                    input_sets: runnableSets,
                });
            });
            if (!runs.length) {
                Object.entries(localRunsByCase).forEach(([caseKey, value]) => {
                    setRunResults(prev => ({ ...prev, [caseKey]: buildRunOutputFromRuns(value.localRuns) }));
                });
                setRunAllStatus('error');
                return;
            }
            if (isFeatureMode && !featureSessionId) throw new Error('Missing feature capture session. Load or create a captured flow first.');
            setRunningCases(new Set(runnableCases.map(testCase => testCase._case_key)));
            const endpoint = isFeatureMode
                ? API_ENDPOINTS.FEATURE_CAPTURE_RUN_SCRIPT_BATCH_QUEUE(featureSessionId)
                : API_ENDPOINTS.RUN_PLAYWRIGHT_BATCH_QUEUE;
            const body = isFeatureMode
                ? { runs }
                : { pdf_id: pdfId, section_number: processingResult?.section_number, runs };
            const submitted = await submitJobOperation(endpoint, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
            }, { errorMessage: 'Failed to queue selected scripts' });
            setExecutionJob(submitted.job);
            const snapshot = await waitForJobResult(submitted.job, {
                onUpdate: ({ job, items }) => { setExecutionJob(job); setExecutionItems(items || []); },
                acceptFailedExecution: true,
            });
            setExecutionJob(snapshot.job);
            setExecutionItems(snapshot.items || []);
            const resultPayload = await loadJobResult(snapshot.job);
            const completedRuns = Array.isArray(resultPayload?.items) ? resultPayload.items : [resultPayload];
            Object.entries(localRunsByCase).forEach(([caseKey, value]) => {
                const matched = completedRuns.filter(run => String(run?.test_case_id || '') === String(getResolvedTestCaseId(value.testCase)));
                const merged = [...value.localRuns, ...matched.map((run, resultIndex) => ({
                    ...(value.runnableSets.find(setItem => String(setItem.input_set_id) === String(run?.input_set_id)) || value.runnableSets[resultIndex] || {}),
                    ...run,
                }))];
                setRunResults(prev => ({ ...prev, [caseKey]: buildRunOutputFromRuns(merged) || { runs: merged, exit_code: 1 } }));
            });
            setRunAllStatus(snapshot.job.status === 'failed' ? 'error' : snapshot.job.status === 'partial_completed' ? 'partial' : 'success');
        } catch (err) {
            setRunAllStatus('error');
            setErrors(prev => ({
                ...prev,
                bulkRun: resolveCaughtErrorMessage(err, 'Failed to run selected scripts'),
            }));
        } finally {
            setRunningCases(new Set());
        }
    };

    const cancelExecution = async () => {
        if (!executionJob) return;
        setExecutionJob(await cancelJob(executionJob));
    };

    const retryFailedExecution = async () => {
        if (!executionJob) return;
        const retried = await retryJob(executionJob);
        setExecutionJob(retried);
        setRunAllStatus('loading');
        const snapshot = await waitForJobResult(retried, {
            onUpdate: ({ job, items }) => { setExecutionJob(job); setExecutionItems(items || []); },
        });
        setExecutionJob(snapshot.job);
        setExecutionItems(snapshot.items || []);
        setRunAllStatus(snapshot.job.status === 'partial_completed' ? 'partial' : 'success');
    };

    // Copy script to clipboard
    const copyToClipboard = (script, caseId) => {
        navigator.clipboard.writeText(script);
        setCopiedId(caseId);
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Download script as file
    const downloadScript = (script, caseId) => {
        const element = document.createElement('a');
        const file = new Blob([script], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = `test_script_${caseId}.${getFileExtension(scriptType)}`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const addProjectFile = (files, path, content) => {
        files.push({
            path: `${PLAYWRIGHT_EXPORT_ROOT}/${path}`.replace(/\/{2,}/g, '/'),
            content,
        });
    };

    const loadAssociatedPomsForCase = async (testCase, options = {}) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        const testCaseId = getResolvedTestCaseId(testCase);
        const sectionNumber = Number(processingResult?.section_number || 0);
        if (!caseKey || !testCaseId || !pdfId || !sectionNumber) return null;
        if (associatedPomsByCaseKey[caseKey] && !options.force) return associatedPomsByCaseKey[caseKey];

        const activeController = associatedPomRequestControllersRef.current.get(caseKey);
        if (activeController) {
            if (!options.force) return null;
            activeController.abort();
        }

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), ASSOCIATED_POMS_REQUEST_TIMEOUT_MS);
        associatedPomRequestControllersRef.current.set(caseKey, controller);
        setLoadingAssociatedPoms((prev) => new Set([...prev, caseKey]));
        setAssociatedPomErrors((prev) => ({ ...prev, [caseKey]: null }));
        try {
            const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.PLAYWRIGHT_ASSOCIATED_POMS(pdfId, sectionNumber, testCaseId)}`, {
                method: 'GET',
                headers: { Accept: 'application/json' },
                signal: controller.signal,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(resolveApiErrorMessage(errorData, 'Failed to load associated POM files'));
            }
            const responseText = await response.text();
            if (!responseText.trim()) {
                throw new Error('Associated POM API returned an empty response body');
            }
            const payload = JSON.parse(responseText);
            const normalizedPayload = normalizeAssociatedPomsPayload(payload);
            if (associatedPomRequestControllersRef.current.get(caseKey) !== controller) {
                return null;
            }
            setAssociatedPomsByCaseKey((prev) => ({ ...prev, [caseKey]: normalizedPayload }));
            return normalizedPayload;
        } catch (err) {
            if (associatedPomRequestControllersRef.current.get(caseKey) !== controller) {
                return null;
            }
            const fallbackMessage = err?.name === 'AbortError'
                ? 'Timed out while loading associated POM files. Please try again.'
                : 'Failed to load associated POM files';
            setAssociatedPomErrors((prev) => ({
                ...prev,
                [caseKey]: resolveCaughtErrorMessage(err, fallbackMessage),
            }));
            return null;
        } finally {
            window.clearTimeout(timeoutId);
            if (associatedPomRequestControllersRef.current.get(caseKey) === controller) {
                associatedPomRequestControllersRef.current.delete(caseKey);
                setLoadingAssociatedPoms((prev) => {
                    const next = new Set(prev);
                    next.delete(caseKey);
                    return next;
                });
            }
        }
    };

    const canEditAssociatedPomFile = (pom = {}) => (
        !isFeatureMode
        && !isApiScriptFlow
        && Boolean(pdfId)
        // Only a POM this source owns is editable here. Application and global
        // POMs are shared, so they are edited in the catalog that owns them.
        && (pom?.scope || 'local') === 'local'
        && Boolean(String(pom?.pageName || '').trim())
    );

    const loadAssociatedPomFilesForEdit = async (testCase, pom, pomIndex = 0, options = {}) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        const pageName = String(pom?.pageName || '').trim();
        const editKey = buildAssociatedPomFileEditorKey(caseKey, pom, pomIndex);
        if (!caseKey || !pageName) return null;
        if (!canEditAssociatedPomFile(pom)) {
            setAssociatedPomEditErrors((prev) => ({
                ...prev,
                [editKey]: 'Only local PDF-scoped POM files can be edited here.',
            }));
            return null;
        }
        if (associatedPomEditDrafts[editKey]?.loaded && !options.force) {
            return associatedPomEditDrafts[editKey];
        }

        setLoadingAssociatedPomFileKeys((prev) => new Set([...prev, editKey]));
        setAssociatedPomEditErrors((prev) => ({ ...prev, [editKey]: null }));
        setAssociatedPomEditStatus((prev) => ({ ...prev, [editKey]: null }));
        try {
            const response = await apiFetch(
                `${API_ENDPOINTS.POM_FILES(pageName)}?pdf_id=${encodeURIComponent(pdfId)}`,
                { method: 'GET', headers: { Accept: 'application/json' } }
            );
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(resolveApiErrorMessage(errorData, 'Failed to load POM files'));
            }
            const payload = normalizeAssociatedPomFilesPayload(await response.json(), pageName);
            const draft = {
                ...payload,
                loaded: true,
                dirty: false,
                selectedFileName: payload.selectedFileName,
            };
            setAssociatedPomEditDrafts((prev) => ({ ...prev, [editKey]: draft }));
            return draft;
        } catch (err) {
            setAssociatedPomEditErrors((prev) => ({
                ...prev,
                [editKey]: resolveCaughtErrorMessage(err, 'Failed to load POM files'),
            }));
            return null;
        } finally {
            setLoadingAssociatedPomFileKeys((prev) => {
                const next = new Set(prev);
                next.delete(editKey);
                return next;
            });
        }
    };

    const beginAssociatedPomFileEdit = async (testCase, pom, pomIndex = 0) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        const editKey = buildAssociatedPomFileEditorKey(caseKey, pom, pomIndex);
        setEditingAssociatedPomKeys((prev) => new Set([...prev, editKey]));
        await loadAssociatedPomFilesForEdit(testCase, pom, pomIndex);
    };

    const cancelAssociatedPomFileEdit = (testCase, pom, pomIndex = 0) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        const editKey = buildAssociatedPomFileEditorKey(caseKey, pom, pomIndex);
        if (associatedPomEditDrafts[editKey]?.dirty && !window.confirm('Discard unsaved POM file changes?')) return;
        setEditingAssociatedPomKeys((prev) => {
            const next = new Set(prev);
            next.delete(editKey);
            return next;
        });
        setAssociatedPomEditErrors((prev) => ({ ...prev, [editKey]: null }));
        setAssociatedPomEditStatus((prev) => ({ ...prev, [editKey]: null }));
    };

    const selectAssociatedPomDraftFile = (editKey, fileName) => {
        setAssociatedPomEditDrafts((prev) => {
            const draft = prev[editKey];
            if (!draft) return prev;
            return {
                ...prev,
                [editKey]: {
                    ...draft,
                    selectedFileName: fileName,
                },
            };
        });
    };

    const updateAssociatedPomDraftFile = (editKey, fileName, value) => {
        setAssociatedPomEditDrafts((prev) => {
            const draft = prev[editKey];
            if (!draft) return prev;
            return {
                ...prev,
                [editKey]: {
                    ...draft,
                    dirty: true,
                    files: (draft.files || []).map((file) => (
                        file.name === fileName ? { ...file, content: value } : file
                    )),
                },
            };
        });
        setAssociatedPomEditErrors((prev) => ({ ...prev, [editKey]: null }));
        setAssociatedPomEditStatus((prev) => ({ ...prev, [editKey]: null }));
    };

    const refreshAssociatedPomFileEdit = async (testCase, pom, pomIndex = 0) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        const editKey = buildAssociatedPomFileEditorKey(caseKey, pom, pomIndex);
        if (associatedPomEditDrafts[editKey]?.dirty && !window.confirm('Discard unsaved POM file changes and reload from disk?')) return;
        await loadAssociatedPomFilesForEdit(testCase, pom, pomIndex, { force: true });
    };

    const saveAssociatedPomFileEdit = async (testCase, pom, pomIndex = 0) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        const pageName = String(pom?.pageName || '').trim();
        const editKey = buildAssociatedPomFileEditorKey(caseKey, pom, pomIndex);
        const draft = associatedPomEditDrafts[editKey];
        const files = Array.isArray(draft?.files) ? draft.files : [];
        const tsFile = files.find((file) => file.language === 'typescript') || files.find((file) => /\.ts$/i.test(file.name));
        const configFile = files.find((file) => file.language === 'json') || files.find((file) => /\.json$/i.test(file.name));

        if (!canEditAssociatedPomFile(pom)) {
            setAssociatedPomEditErrors((prev) => ({
                ...prev,
                [editKey]: 'Only local PDF-scoped POM files can be edited here.',
            }));
            return;
        }
        if (!tsFile || !configFile) {
            setAssociatedPomEditErrors((prev) => ({
                ...prev,
                [editKey]: 'Both the TypeScript file and config JSON must be present before saving.',
            }));
            return;
        }

        setSavingAssociatedPomKeys((prev) => new Set([...prev, editKey]));
        setAssociatedPomEditErrors((prev) => ({ ...prev, [editKey]: null }));
        setAssociatedPomEditStatus((prev) => ({ ...prev, [editKey]: null }));
        try {
            const response = await apiFetch(API_ENDPOINTS.POM_FILES(pageName), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pdf_id: pdfId,
                    typescriptCode: tsFile.content,
                    configText: configFile.content,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(resolveApiErrorMessage(errorData, 'Failed to save POM files'));
            }
            const payload = normalizeAssociatedPomFilesPayload(await response.json(), pageName);
            setAssociatedPomEditDrafts((prev) => ({
                ...prev,
                [editKey]: {
                    ...payload,
                    loaded: true,
                    dirty: false,
                    selectedFileName: payload.selectedFileName,
                },
            }));
            setAssociatedPomEditStatus((prev) => ({
                ...prev,
                [editKey]: payload.validation_warnings.length
                    ? `Saved with ${payload.validation_warnings.length} validation warning${payload.validation_warnings.length === 1 ? '' : 's'}.`
                    : 'POM files saved.',
            }));
            await loadAssociatedPomsForCase(testCase, { force: true });
            fetchPOMsList();
            setOpenAssociatedPomTypeScriptPanels((prev) => new Set([...prev, buildAssociatedPomViewerKey(caseKey, pom, pomIndex)]));
        } catch (err) {
            setAssociatedPomEditErrors((prev) => ({
                ...prev,
                [editKey]: resolveCaughtErrorMessage(err, 'Failed to save POM files'),
            }));
        } finally {
            setSavingAssociatedPomKeys((prev) => {
                const next = new Set(prev);
                next.delete(editKey);
                return next;
            });
        }
    };

    const renderAssociatedPomFileEditor = (testCase, pom, pomIndex = 0, sizing = {}) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        const editKey = buildAssociatedPomFileEditorKey(caseKey, pom, pomIndex);
        const draft = associatedPomEditDrafts[editKey];
        const files = Array.isArray(draft?.files) ? draft.files : [];
        const selectedFileName = draft?.selectedFileName || files[0]?.name || '';
        const selectedFile = files.find((file) => file.name === selectedFileName) || files[0];
        const isEditing = editingAssociatedPomKeys.has(editKey);
        const isLoadingFiles = loadingAssociatedPomFileKeys.has(editKey);
        const isSaving = savingAssociatedPomKeys.has(editKey);
        const error = associatedPomEditErrors[editKey];
        const status = associatedPomEditStatus[editKey];
        const validationWarnings = Array.isArray(draft?.validation_warnings) ? draft.validation_warnings : [];

        if (!isEditing) return null;

        return (
            <div className="rounded-lg border border-blue-200 bg-white p-3 shadow-sm">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-2">
                        {files.map((file) => (
                            <button
                                key={`${editKey}-${file.name}`}
                                type="button"
                                onClick={() => selectAssociatedPomDraftFile(editKey, file.name)}
                                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${file.name === selectedFileName
                                    ? 'border-blue-600 bg-blue-600 text-white'
                                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}`}
                            >
                                {file.name}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => refreshAssociatedPomFileEdit(testCase, pom, pomIndex)}
                            disabled={isLoadingFiles || isSaving}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${isLoadingFiles ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                        <button
                            type="button"
                            onClick={() => saveAssociatedPomFileEdit(testCase, pom, pomIndex)}
                            disabled={isLoadingFiles || isSaving || !draft?.dirty}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isSaving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            Save Files
                        </button>
                        <button
                            type="button"
                            onClick={() => cancelAssociatedPomFileEdit(testCase, pom, pomIndex)}
                            disabled={isSaving}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <X className="h-3.5 w-3.5" />
                            Close
                        </button>
                    </div>
                </div>
                {isLoadingFiles && !selectedFile ? (
                    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                        <Loader className="h-4 w-4 animate-spin" />
                        Loading POM files...
                    </div>
                ) : selectedFile ? (
                    <CodeEditorPreview
                        value={selectedFile.content}
                        language={selectedFile.language === 'json' ? 'json' : 'typescript'}
                        editable
                        onChange={(value) => updateAssociatedPomDraftFile(editKey, selectedFile.name, value)}
                        minHeight={sizing.minHeight || 260}
                        maxHeight={sizing.maxHeight || 560}
                        placeholder={selectedFile.language === 'json' ? 'Edit POM config JSON' : 'Edit POM TypeScript'}
                    />
                ) : (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
                        POM files are not loaded yet.
                    </div>
                )}
                {draft?.dirty && (
                    <p className="mt-2 text-xs font-semibold text-amber-700">Unsaved POM file changes.</p>
                )}
                {status && (
                    <p className="mt-2 text-xs font-semibold text-emerald-700">{status}</p>
                )}
                {validationWarnings.length > 0 && (
                    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        {validationWarnings.join(' ')}
                    </div>
                )}
                {error && (
                    <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                        {error}
                    </div>
                )}
            </div>
        );
    };

    const buildPlaywrightProjectExportFiles = async (generatedCases) => {
        const files = [];
        const usedScriptNames = new Set();
        const exportedPomFiles = new Map();
        const warnings = [];
        const testData = {};
        const exportedPomsByCaseKey = {};
        const manifest = {
            exported_at: new Date().toISOString(),
            source: {
                pdf_id: pdfId || null,
                section_number: processingResult?.section_number || null,
                section_title: processingResult?.section_title || null,
                script_flow: isApiScriptFlow ? 'api' : 'ui',
            },
            scripts: [],
            poms: [],
            warnings,
        };

        addProjectFile(files, 'README.md', PLAYWRIGHT_EXPORT_README);
        addProjectFile(files, '.gitignore', PLAYWRIGHT_EXPORT_GITIGNORE);
        addProjectFile(files, 'package.json', PLAYWRIGHT_EXPORT_PACKAGE_JSON);
        addProjectFile(files, 'playwright.config.ts', PLAYWRIGHT_EXPORT_CONFIG_TS);
        addProjectFile(files, 'tests/ui/testsuiteHooks.ts', TESTSUITE_HOOKS_TS);
        addProjectFile(files, 'tests/api/testsuiteHooks.ts', TESTSUITE_HOOKS_TS);

        generatedCases.forEach((testCase, index) => {
            const caseKey = testCase._case_key;
            const script = String(generatedScripts[caseKey] || '').trim();
            const baseName = buildCaseExportName(testCase, index);
            let fileName = `${baseName}.spec.ts`;
            let duplicateIndex = 2;
            while (usedScriptNames.has(fileName)) {
                fileName = `${baseName}-${duplicateIndex}.spec.ts`;
                duplicateIndex += 1;
            }
            usedScriptNames.add(fileName);

            const scriptPath = `tests/${isApiScriptFlow ? 'api' : 'ui'}/${fileName}`;
            addProjectFile(files, scriptPath, `${script}\n`);

            const resolvedInputSets = resolveUserInputSets(testCase);
            testData[fileName.replace(/\.spec\.ts$/, '')] = {
                test_case_no: getResolvedTestCaseId(testCase) || null,
                category: testCase._category || null,
                title: testCase.test_description || testCase.test_name || testCase.scenario || testCase.title || null,
                required_inputs: getRequiredInputsFromScript(script),
                input_sets: resolvedInputSets.length > 0
                    ? resolvedInputSets
                    : [{
                        set_name: 'Set 1',
                        set_type: 'primary',
                        user_inputs: normalizeUserInputList(testCase?.user_inputs || []),
                    }],
            };
            manifest.scripts.push({
                test_case_no: getResolvedTestCaseId(testCase) || null,
                case_key: caseKey,
                path: scriptPath,
                title: testCase.test_description || testCase.test_name || testCase.scenario || testCase.title || null,
            });
        });

        if (!isApiScriptFlow) {
            for (const testCase of generatedCases) {
                const caseKey = testCase._case_key;
                const payload = associatedPomsByCaseKey[caseKey]
                    || (isFeatureMode ? { poms: featurePomSelections, warnings: [] } : await loadAssociatedPomsForCase(testCase));
                const poms = Array.isArray(payload?.poms) ? payload.poms : [];
                exportedPomsByCaseKey[caseKey] = poms;
                const payloadWarnings = Array.isArray(payload?.warnings) ? payload.warnings : [];
                if (!payload && getPomSelectionForCaseKey(caseKey).poms.length > 0) {
                    warnings.push(`POM files could not be loaded for ${getResolvedTestCaseId(testCase) || caseKey}.`);
                }
                payloadWarnings.forEach((warning) => warnings.push(warning));

                poms.forEach((pom, pomIndex) => {
                    const pageName = String(pom.pageName || `pom-${pomIndex + 1}`).trim();
                    const pomBaseName = normalizeExportFileSegment(pageName, `pom-${pomIndex + 1}`);
                    const pomScopePrefix = pom.scope && pom.scope !== 'local' ? `${pom.scope}-` : '';
                    const pomPath = `pages/${pomScopePrefix}${pomBaseName}.ts`;
                    const configPath = `data/poms/${pomScopePrefix}${pomBaseName}.json`;
                    const code = String(pom.typescriptCode || '').trim();
                    const configText = typeof pom.configText === 'string'
                        ? pom.configText
                        : stringifyAssociatedPomConfig(pom.config);
                    const pomKey = `${pom.scope || 'local'}::${pageName}`;

                    if (code && !exportedPomFiles.has(pomPath)) {
                        addProjectFile(files, pomPath, `${code}\n`);
                        exportedPomFiles.set(pomPath, pomKey);
                    }
                    if (!exportedPomFiles.has(configPath)) {
                        addProjectFile(files, configPath, `${configText}\n`);
                        exportedPomFiles.set(configPath, pomKey);
                    }
                    if (!manifest.poms.some((item) => item.key === pomKey)) {
                        manifest.poms.push({
                            key: pomKey,
                            page_name: pageName,
                            scope: pom.scope || 'local',
                            page_url: pom.pageUrl || null,
                            path: code ? pomPath : null,
                            config_path: configPath,
                        });
                    }
                });
            }

            manifest.scripts.forEach((scriptEntry) => {
                const exportPath = `${PLAYWRIGHT_EXPORT_ROOT}/${scriptEntry.path}`.replace(/\/{2,}/g, '/');
                const file = files.find((candidate) => candidate.path === exportPath);
                if (!file) return;
                const rewritten = rewriteExportedPomImports(
                    file.content,
                    exportedPomsByCaseKey[scriptEntry.case_key] || []
                );
                file.content = `${rewritten.trimEnd()}\n`;
            });
        }

        addProjectFile(files, 'data/test-data.json', `${JSON.stringify(testData, null, 2)}\n`);
        addProjectFile(files, 'data/export-manifest.json', `${JSON.stringify(manifest, null, 2)}\n`);

        return {
            files,
            manifest,
        };
    };

    const downloadPlaywrightProject = async () => {
        const generatedCases = getNormalizedTestCases()
            .filter((testCase) => String(generatedScripts[testCase._case_key] || '').trim());

        if (generatedCases.length === 0) {
            setProjectExportStatus(null);
            setProjectExportError('Generate at least one script before downloading a Playwright project.');
            return;
        }

        setProjectExportLoading(true);
        setProjectExportStatus(null);
        setProjectExportError(null);

        try {
            const { files, manifest } = await buildPlaywrightProjectExportFiles(generatedCases);
            const blob = createZipBlob(files);
            const sourceName = normalizeExportFileSegment(
                processingResult?.section_title || pdfId || 'playwright-automation',
                'playwright-automation'
            );
            downloadBlob(blob, `${sourceName}-playwright-project.zip`);
            setProjectExportStatus({
                scripts: manifest.scripts.length,
                poms: manifest.poms.length,
                files: files.length,
                warnings: manifest.warnings.length,
            });
            window.setTimeout(() => setProjectExportStatus(null), 5000);
        } catch (err) {
            setProjectExportError(resolveCaughtErrorMessage(err, 'Failed to download Playwright project'));
        } finally {
            setProjectExportLoading(false);
        }
    };

    const toggleAssociatedPoms = async (testCase) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        if (!caseKey) return;

        if (associatedPomPanels.has(caseKey)) {
            setAssociatedPomPanels((prev) => {
                const next = new Set(prev);
                next.delete(caseKey);
                return next;
            });
            return;
        }

        setAssociatedPomPanels((prev) => new Set([...prev, caseKey]));
    };

    const toggleAssociatedPomTypeScript = (testCase, pom, pomIndex = 0) => {
        const caseKey = testCase?._case_key || testCase?.test_case_no;
        if (!caseKey) return;

        const viewerKey = buildAssociatedPomViewerKey(caseKey, pom, pomIndex);
        const willOpen = !openAssociatedPomTypeScriptPanels.has(viewerKey);
        setOpenAssociatedPomTypeScriptPanels((prev) => {
            const next = new Set(prev);
            if (next.has(viewerKey)) {
                next.delete(viewerKey);
            } else {
                next.add(viewerKey);
            }
            return next;
        });

        if (willOpen && !associatedPomsByCaseKey[caseKey]) {
            loadAssociatedPomsForCase(testCase);
        }
    };

    const getFileExtension = (type) => {
        const extensions = {
            selenium: 'java',
            cypress: 'js',
            playwright: 'js',
            testng: 'xml',
        };
        return extensions[type] || 'txt';
    };

    const getScriptTypeLabel = (type) => {
        const labels = {
            selenium: 'Selenium (Java)',
            cypress: 'Cypress (JavaScript)',
            playwright: 'Playwright (JavaScript)',
            testng: 'TestNG (XML)',
        };
        return labels[type] || type;
    };

    const testCases = getFilteredTestCases(categoryFilter);
    const allTests = getNormalizedTestCases();
    const generatedExportCount = allTests.filter((testCase) => String(generatedScripts[testCase._case_key] || '').trim()).length;
    const positiveCount = allTests.filter(tc => tc._category === 'positive').length;
    const negativeCount = allTests.filter(tc => tc._category === 'negative').length;
    const manualPositiveCount = allTests.filter(
        tc => tc._category === 'positive' && tc.source_type === 'manual_upload'
    ).length;
    const manualNegativeCount = allTests.filter(
        tc => tc._category === 'negative' && tc.source_type === 'manual_upload'
    ).length;
    const allFilteredSelected = testCases.length > 0 && testCases.every(tc => selectedCases.has(tc._case_key));
    const selectedCount = selectedCases.size;
    const isSelectionAtLimit = selectedCount >= MAX_SCRIPT_GENERATION_CASES;
    const selectedHaveGenerated = selectedCount > 0 && Array.from(selectedCases).every(
        id => Boolean(generatedScripts[id]) && !getScriptStaleMeta(id).isStale
    );
    const pomSelectedCaseCount = isApiScriptFlow
        ? allTests.length
        : allTests.filter((tc) => getSelectedPomsForCaseKey(tc._case_key).length > 0).length;
    const pomMissingCaseCount = isApiScriptFlow ? 0 : Math.max(allTests.length - pomSelectedCaseCount, 0);
    const hasAnyCaseSpecificPomSelection = allTests.some(
        (tc) => getCaseSpecificPomSelectionForCaseKey(tc._case_key).poms.length > 0
    );
    const pomRecommendationBusy = loadingPomRecommendations.size > 0;
    const pomRecommendationOverlayTitle = pomRecommendationCancelling
        ? 'Cancelling POM selection'
        : hasAnyCaseSpecificPomSelection
            ? 'Refreshing POM selections'
            : 'Auto-selecting POMs';
    const pomRecommendationOverlayText = pomRecommendationCancelling
        ? 'Stopping in-flight requests and keeping existing selections unchanged.'
        : loadingPomRecommendations.size > 1
            ? `Matching POMs for ${loadingPomRecommendations.size} test cases.`
            : 'Matching POMs for this test case.';
    const isPomRequiredBulkError = !isApiScriptFlow && typeof errors.bulk === 'string'
        && (
            errors.bulk.toLowerCase().includes('page object model')
            || errors.bulk.toLowerCase().includes('pom selection')
            || errors.bulk.toLowerCase().includes('poms')
        );

    useEffect(() => {
        if (!isFeatureMode || pdfId) return;
        const normalizedCases = getNormalizedTestCases();
        const payload = {
            poms: featurePomSelections,
            warnings: [],
        };
        setAssociatedPomsByCaseKey((prev) => {
            const next = { ...prev };
            normalizedCases.forEach((testCase) => {
                if (!testCase?._case_key) return;
                next[testCase._case_key] = payload;
            });
            return next;
        });
    }, [featurePomSelections, getNormalizedTestCases, isFeatureMode, pdfId]);

    useEffect(() => {
        if (isFeatureMode || isApiScriptFlow) return;
        if (!pomSelectionsLoaded || pomModePromptSeen || allTests.length === 0) return;
        if (pomSelectedCaseCount > 0) return;
        setShowPomModeModal(true);
        setPomModePromptSeen(true);
    }, [allTests.length, isApiScriptFlow, isFeatureMode, pomModePromptSeen, pomSelectedCaseCount, pomSelectionsLoaded]);

    useEffect(() => {
        const normalizedCases = getNormalizedTestCases();

        if (!normalizedCases.length) return;
        setScriptInputValues(prev => {
            const next = { ...prev };
            normalizedCases.forEach((testCase) => {
                const caseKey = testCase._case_key;
                const defaults = getUserInputsMapFromTestCase(testCase);
                next[caseKey] = {
                    ...defaults,
                    ...(next[caseKey] || {})
                };
            });
            return next;
        });
    }, [getNormalizedTestCases]);

    useEffect(() => {
        if (!prefilledScriptsByCaseId || typeof prefilledScriptsByCaseId !== 'object') return;

        const normalizedCases = getNormalizedTestCases();
        if (!normalizedCases.length) return;

        const hydratedScripts = {};
        const hydratedAssumptions = {};
        const hydratedWarnings = {};
        const hydratedModels = {};
        const hydratedSaveMeta = {};

        normalizedCases.forEach((testCase) => {
            const caseId = String(testCase?.test_case_no || '').trim();
            if (!caseId) return;
            const saved = prefilledScriptsByCaseId[caseId];
            if (!saved || typeof saved !== 'object') return;

            const script = String(saved.playwright_script || saved.script || '').trim();
            if (!script) return;

            hydratedScripts[testCase._case_key] = script;
            hydratedAssumptions[testCase._case_key] = Array.isArray(saved.assumptions)
                ? saved.assumptions
                : [];
            hydratedWarnings[testCase._case_key] = Array.isArray(saved.warnings)
                ? saved.warnings
                : [];
            hydratedModels[testCase._case_key] = saved.model || null;
            const editorMeta = resolveScriptEditorMeta(saved);
            hydratedSaveMeta[testCase._case_key] = {
                version: saved.version ?? null,
                status: saved.status || null,
                updated_at: saved.updated_at || null,
                save_mode: 'history',
                draft_updated_at: saved.draft_updated_at || null,
                requires_regeneration: Boolean(saved.requires_regeneration || saved.status === 'stale'),
                stale_reason: saved.stale_reason || null,
                missing_poms: Array.isArray(saved.missing_poms) ? saved.missing_poms : [],
                edited_by: editorMeta.edited_by,
                edited_at: editorMeta.edited_at || saved.updated_at || null,
            };
        });

        if (!Object.keys(hydratedScripts).length) return;

        setGeneratedScripts((prev) => {
            const merged = { ...prev };
            Object.entries(hydratedScripts).forEach(([caseKey, script]) => {
                if (!merged[caseKey]) {
                    merged[caseKey] = script;
                }
            });
            return merged;
        });

        setScriptAssumptions((prev) => {
            const merged = { ...prev };
            Object.entries(hydratedAssumptions).forEach(([caseKey, assumptions]) => {
                if (!merged[caseKey]) {
                    merged[caseKey] = assumptions;
                }
            });
            return merged;
        });

        setScriptWarnings((prev) => {
            const merged = { ...prev };
            Object.entries(hydratedWarnings).forEach(([caseKey, warnings]) => {
                if (!merged[caseKey]) {
                    merged[caseKey] = warnings;
                }
            });
            return merged;
        });
        setScriptModels((prev) => {
            const merged = { ...prev };
            Object.entries(hydratedModels).forEach(([caseKey, model]) => {
                if (!merged[caseKey]) {
                    merged[caseKey] = model;
                }
            });
            return merged;
        });
        setScriptSaveMeta((prev) => {
            const merged = { ...prev };
            Object.entries(hydratedSaveMeta).forEach(([caseKey, meta]) => {
                if (!merged[caseKey]) {
                    merged[caseKey] = meta;
                }
            });
            return merged;
        });
    }, [prefilledScriptsByCaseId, getNormalizedTestCases]);

    useEffect(() => {
        if (!isFeatureMode || !featureScripts || typeof featureScripts !== 'object') return;
        const scriptItems = Array.isArray(featureScripts.items) ? featureScripts.items : [];
        if (!scriptItems.length) return;

        const normalizedCases = getNormalizedTestCases();
        const casesById = normalizedCases.reduce((acc, testCase) => {
            const testCaseId = getResolvedTestCaseId(testCase);
            if (testCaseId) acc[testCaseId] = testCase;
            return acc;
        }, {});
        const hydratedScripts = {};
        const hydratedAssumptions = {};
        const hydratedWarnings = {};
        const hydratedModels = {};
        const hydratedSaveMeta = {};

        scriptItems.forEach((item) => {
            const testCaseId = String(item?.test_case_id || '').trim();
            const testCase = casesById[testCaseId];
            const caseKey = testCase?._case_key;
            const script = String(item?.playwright_script || '').trim();
            if (!caseKey || !script) return;
            hydratedScripts[caseKey] = script;
            hydratedAssumptions[caseKey] = Array.isArray(item.assumptions) ? item.assumptions : [];
            hydratedWarnings[caseKey] = Array.isArray(item.warnings) ? item.warnings : [];
            hydratedModels[caseKey] = item.model || null;
            hydratedSaveMeta[caseKey] = {
                version: null,
                updated_at: item.updated_at || item.generated_at || null,
                save_mode: 'feature_capture',
                draft_updated_at: null,
                edited_by: resolveActorLabel(
                    item.edited_by_name,
                    item.edited_by_email,
                    item.edited_by
                ),
            };
        });

        if (!Object.keys(hydratedScripts).length) return;
        setGeneratedScripts((prev) => ({ ...prev, ...hydratedScripts }));
        setScriptAssumptions((prev) => ({ ...prev, ...hydratedAssumptions }));
        setScriptWarnings((prev) => ({ ...prev, ...hydratedWarnings }));
        setScriptModels((prev) => ({ ...prev, ...hydratedModels }));
        setScriptSaveMeta((prev) => ({ ...prev, ...hydratedSaveMeta }));
    }, [featureScripts, getNormalizedTestCases, isFeatureMode]);

    const inspectorCase = (
        testCases.find((testCase) => testCase._case_key === expandedCase)
        || testCases[0]
        || null
    );
    return (
        <div className="space-y-6" aria-busy={pomRecommendationBusy}>
            <div className={`rounded-lg border border-slate-200 border-l-4 ${scriptTheme.cardAccentBorder} bg-white p-5`}>
                <div className="flex items-start gap-3">
                    <span className={`mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg ${scriptTheme.iconBadge}`}>
                        <scriptTheme.Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                        <h3 className="text-base font-semibold text-slate-900">
                            {isApiScriptFlow ? 'Generate API Test Scripts' : 'Generate Test Scripts'}
                        </h3>
                        <p className="mt-1 text-sm text-slate-600">
                            Generate automation scripts for your test cases in various frameworks
                        </p>
                        {processingResult?.section_title && (
                            <p className="mt-2 text-xs text-slate-500">
                                Module<span className="mx-1.5 text-slate-300">·</span>
                                <span className="font-medium text-slate-700">{processingResult.section_title}</span>
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {isFeatureMode && (
                <section className="rounded-xl border border-indigo-200 bg-indigo-50 p-4" aria-label="Test notes for script generation">
                    <label htmlFor="feature-script-notes" className="text-sm font-semibold text-indigo-950">Test notes for script generation</label>
                    <p className="mt-1 text-sm text-indigo-800">Describe any checks, conditions, data changes, or retries in plain language. The saved recordings stay reusable. These notes are applied when you generate selected scripts.</p>
                    <textarea
                        id="feature-script-notes"
                        className="text-input mt-3 min-h-28 w-full resize-y bg-white"
                        maxLength={4000}
                        value={featureScriptNotes}
                        onChange={(event) => onFeatureScriptNotesChange?.(event.target.value)}
                        placeholder="Example: After the shared login, verify the dashboard title. If an optional dialog appears, close it before continuing."
                    />
                </section>
            )}

            {!isApiScriptFlow && showPomModeModal && ReactDOM.createPortal(
                <div className="modal-overlay">
                    <div className="modal-shell max-w-xl overflow-hidden">
                        <div className="border-b border-slate-700 bg-slate-900 px-5 py-4 text-white">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${scriptTheme.onDark}`}>
                                        POM Selection
                                    </p>
                                    <h3 className="mt-1 text-lg font-bold">Choose POM Selection Mode</h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowPomModeModal(false)}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/40 bg-white/10 text-white transition hover:bg-white/20"
                                    aria-label="Close POM selection mode"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                        <div className="space-y-3 bg-white p-5">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowPomModeModal(false);
                                    autoSelectPomsForCases(allTests, { preserveManual: false });
                                }}
                                disabled={pomRecommendationBusy}
                                className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <span>
                                    <span className="block text-sm font-semibold text-slate-900">Auto-select POMs</span>
                                    <span className="block text-xs text-slate-600">Match available page objects to each test case.</span>
                                </span>
                                {pomRecommendationBusy ? (
                                    <Loader className={`h-5 w-5 animate-spin ${scriptTheme.accentText}`} />
                                ) : (
                                    <Wand2 className={`h-5 w-5 ${scriptTheme.accentText}`} />
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowPomModeModal(false)}
                                className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left transition hover:bg-slate-50"
                            >
                                <span>
                                    <span className="block text-sm font-semibold text-slate-900">Choose Manually Per Test Case</span>
                                    <span className="block text-xs text-slate-600">Review each test case and update its POMs yourself.</span>
                                </span>
                                <Pencil className="h-5 w-5 text-slate-600" />
                            </button>
                        </div>
                        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-4">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowPomModeModal(false);
                                    if (onBack) onBack();
                                }}
                                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowPomModeModal(false)}
                                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${scriptTheme.primaryButton}`}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showGithubExportModal && (
                <GithubExportModal
                    allTests={allTests}
                    selectedCases={selectedCases}
                    pdfId={pdfId}
                    sectionNumber={processingResult?.section_number}
                    onClose={() => setShowGithubExportModal(false)}
                />
            )}

            {/* POM Selection Card */}
            {isApiScriptFlow ? (
                <div className={`rounded-lg border border-slate-200 border-l-4 ${scriptTheme.cardAccentBorder} bg-white p-4`}>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-start gap-3">
                            <div className={`p-2.5 rounded-lg ${scriptTheme.iconBadge}`}>
                                <Link2 className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="font-semibold text-slate-900 text-sm">API Contract Scripts</p>
                                <p className="text-xs text-slate-600 mt-1">
                                    POM generation is skipped for Swagger/OpenAPI sources. Scripts are generated from API operations, curl data, and expected API responses.
                                </p>
                            </div>
                        </div>
                        <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${scriptTheme.chip}`}>
                            <CheckCircle className="h-3.5 w-3.5" />
                            Ready for API generation
                        </span>
                    </div>
                </div>
            ) : !pomSelectionsLoaded ? (
                <div className={`rounded-lg border border-slate-200 border-l-4 ${scriptTheme.cardAccentBorder} bg-white p-4`}>
                    <div className="flex items-center gap-3">
                        <Loader className={`h-5 w-5 animate-spin ${scriptTheme.accentText}`} />
                        <div>
                            <p className="text-sm font-semibold text-slate-900">Loading automation setup</p>
                            <p className="text-xs text-slate-600">Restoring saved POM selections for these test cases.</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className={`rounded-lg border border-slate-200 border-l-4 ${scriptTheme.cardAccentBorder} bg-white p-4`}>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-start gap-3">
                            <span className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg ${scriptTheme.iconBadge}`}>
                                <BookOpen className="h-4 w-4" />
                            </span>
                            <div>
                                <p className="text-sm font-semibold text-slate-900">Page Object Models</p>
                                <p className="mt-1 text-xs text-slate-600">
                                    {isFeatureMode
                                        ? `${featurePomSelections.length} captured page object${featurePomSelections.length === 1 ? '' : 's'} attached automatically to ${allTests.length} test case${allTests.length === 1 ? '' : 's'}.`
                                        : `${pomSelectedCaseCount} of ${allTests.length} test case${allTests.length === 1 ? '' : 's'} have POMs selected.`}
                                    {!isFeatureMode && pomMissingCaseCount > 0 ? ` ${pomMissingCaseCount} still need selection.` : ''}
                                </p>
                                {pomSelectionStatus && (
                                    <p className="mt-2 text-xs font-medium text-slate-700">{pomSelectionStatus}</p>
                                )}
                                {pomSelectionError && (
                                    <p className="mt-2 text-xs font-medium text-red-700">{pomSelectionError}</p>
                                )}
                                {!isFeatureMode && !isApiScriptFlow && (
                                    <label className="mt-3 flex items-start gap-2 text-xs text-slate-700">
                                        <input
                                            type="checkbox"
                                            checked={strictPomMode}
                                            onChange={(event) => setStrictPomMode(event.target.checked)}
                                            className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300"
                                        />
                                        <span>
                                            <span className="font-semibold text-slate-900">POM-only generation</span>
                                            {' — '}
                                            never write a selector that is not in a selected POM. A step with no POM
                                            element is reported instead, so it can be scanned or added first.
                                        </span>
                                    </label>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {isFeatureMode ? (
                                <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm font-semibold text-slate-700">
                                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                                    Auto-attached
                                </span>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => autoSelectPomsForCases(allTests, { preserveManual: hasAnyCaseSpecificPomSelection })}
                                        disabled={allTests.length === 0 || pomRecommendationBusy}
                                        className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${scriptTheme.primaryButton}`}
                                    >
                                        {pomRecommendationBusy ? (
                                            <Loader className="w-4 h-4 animate-spin" />
                                        ) : hasAnyCaseSpecificPomSelection ? (
                                            <RefreshCw className="w-4 h-4" />
                                        ) : (
                                            <Wand2 className="w-4 h-4" />
                                        )}
                                        {pomRecommendationBusy
                                            ? (hasAnyCaseSpecificPomSelection ? 'Refreshing...' : 'Auto-selecting...')
                                            : (hasAnyCaseSpecificPomSelection ? 'Refresh Selection' : 'Auto-select POMs')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleOpenPomModal({ type: 'all' })}
                                        disabled={allTests.length === 0}
                                        className={secondaryButtonClass}
                                    >
                                        <BookOpen className="w-4 h-4" />
                                        Select Common POMs
                                    </button>
                                    <button
                                        type="button"
                                        onClick={resetAllPomSelections}
                                        disabled={pomSelectedCaseCount === 0}
                                        className={secondaryButtonClass}
                                    >
                                        <RotateCcw className="w-4 h-4" />
                                        Reset
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* POM Modal */}
            {!isApiScriptFlow && showPomModal && ReactDOM.createPortal(
                <div className="modal-overlay">
                    <div className="modal-shell max-w-2xl flex max-h-[78vh] flex-col">
                        {/* Modal Header */}
                        <div className="border-b border-slate-700 bg-slate-900 px-5 py-4 text-white">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${scriptTheme.onDark}`}>
                                        Automation Setup
                                    </p>
                                    <h3 className="mt-1 text-lg font-bold">Select Page Object Models</h3>
                                    <p className={`mt-1 text-sm ${scriptTheme.onDark}`}>
                                        {pomModalTarget?.type === 'case'
                                            ? `Choose POMs for ${pomModalTarget.testCaseId || 'this test case'}.`
                                            : 'Choose common POMs for all test cases.'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowPomModal(false)}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/40 bg-white/10 text-white transition hover:bg-white/20"
                                    aria-label="Close POM modal"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3">
                            <input
                                type="text"
                                placeholder="Search by name or URL..."
                                value={pomSearchTerm}
                                onChange={(e) => setPomSearchTerm(e.target.value)}
                                className="text-input"
                            />
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto bg-white">
                            {loadingPoms ? (
                                <div className="flex h-36 items-center justify-center">
                                    <Loader className={`w-6 h-6 animate-spin ${scriptTheme.accentText}`} />
                                </div>
                            ) : pomError ? (
                                <div className="m-4 rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-red-900 text-sm">Error Loading POMs</p>
                                        <p className="text-red-700 text-sm mt-1">{pomError}</p>
                                    </div>
                                </div>
                            ) : filteredPoms.length === 0 ? (
                                <div className="p-8 text-center">
                                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                    <p className="text-slate-700 font-medium">
                                        {pomSearchTerm ? 'No POMs found matching your search' : 'No POMs available'}
                                    </p>
                                    {!pomSearchTerm && (
                                        <p className="text-slate-500 text-sm mt-2">
                                            {normalizedApplicationId
                                                ? 'Create page objects in this application, then refresh this step.'
                                                : 'Create a POM for this source first.'}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-200">
                                    {filteredPoms.map((pom) => {
                                        const hasFullPageUrl = isAbsoluteHttpUrl(pom.pageUrl);
                                        const currentKey = buildPomSelectionKey(pom);
                                        const isSelected = modalSelectedPomKeySet.has(currentKey);
                                        const selectionDisabled = !hasFullPageUrl && !isSelected;
                                        return (
                                        <button
                                            key={currentKey}
                                            type="button"
                                            disabled={selectionDisabled}
                                            onClick={() => {
                                                setModalSelectedPoms((prev) => {
                                                    if (isSelected) {
                                                        return prev.filter(item => buildPomSelectionKey(item) !== currentKey);
                                                    }
                                                    return [...prev, pom];
                                                });
                                                setErrors(prev => ({ ...prev, bulk: null }));
                                            }}
                                            className={`w-full border-l-4 p-4 text-left transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-amber-50/50 ${isSelected ? `${scriptTheme.selectedBorder} ${scriptTheme.selectedTint}` : 'border-l-transparent'}`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <p className="font-semibold text-slate-900">{pom.pageName}</p>
                                                    <p className={`text-xs mt-1 ${hasFullPageUrl ? 'text-slate-600' : 'text-amber-700'}`}>
                                                        <span className="font-medium">URL:</span> {hasFullPageUrl ? pom.pageUrl : 'Full page URL required'}
                                                    </p>
                                                    {!hasFullPageUrl && (
                                                        <p className="mt-1 text-xs text-amber-700">
                                                            Edit this POM and add a full http(s) URL before selecting it.
                                                        </p>
                                                    )}
                                                    <p className="text-xs text-slate-600 mt-1">
                                                        <span className="font-medium">Scope:</span> {POM_SCOPE_LABELS[pom.scope] || 'This source'}
                                                    </p>
                                                    <div className="flex gap-2 mt-2 text-xs">
                                                        <span className="rounded-md bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">
                                                            {pom.locators} locator{Number(pom.locators) === 1 ? '' : 's'}
                                                        </span>
                                                        <span className="rounded-md bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">
                                                            {pom.functions} method{Number(pom.functions) === 1 ? '' : 's'}
                                                        </span>
                                                    </div>
                                                </div>
                                                {isSelected && (
                                                    <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-1 ${scriptTheme.accentText}`} />
                                                )}
                                            </div>
                                        </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="border-t border-slate-200 bg-white px-4 py-3 flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    setShowPomModal(false);
                                    setPomSearchTerm('');
                                }}
                                className="secondary-btn px-4 py-2 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmPomSelection}
                                disabled={loadingPoms || Boolean(pomError) || (pomModalTarget?.type === 'case' && modalSelectedPoms.length === 0)}
                                className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${scriptTheme.primaryButton}`}
                            >
                                {pomModalTarget?.type === 'case' ? 'Save Selection' : 'Save Common POMs'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {VERSIONING_ENABLED && pendingScriptSave && ReactDOM.createPortal(
                <div
                    className="modal-overlay"
                    role="presentation"
                    onClick={() => {
                        if (savingScriptKeys.has(pendingScriptSave.caseKey)) return;
                        setPendingScriptSave(null);
                    }}
                >
                    <div
                        className="modal-shell flex max-w-md flex-col"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="save-script-version-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="border-b border-slate-700 bg-slate-900 p-5 text-white">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <h4
                                        id="save-script-version-title"
                                        className="text-lg font-semibold flex items-center gap-2"
                                    >
                                        <Save className="h-5 w-5" />
                                        Save Script
                                    </h4>
                                    <p className="mt-2 text-sm text-blue-100">
                                        Choose whether to update the current script version or create a new one.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setPendingScriptSave(null)}
                                    disabled={savingScriptKeys.has(pendingScriptSave.caseKey)}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/40 bg-white/10 text-white transition hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                    aria-label="Close save script dialog"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <div className="space-y-3 bg-white p-5 text-sm text-slate-700">
                            <p>
                                Current script version: <span className="font-semibold">v{pendingScriptSave.currentVersion}</span>
                            </p>
                            <p>
                                Saved versions: <span className="font-semibold">{pendingScriptSave.versionCount ?? 0}</span> / {MAX_SAVED_VERSIONS}
                            </p>
                            {(pendingScriptSave.versionCount ?? 0) >= MAX_SAVED_VERSIONS && (
                                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                    Max {MAX_SAVED_VERSIONS} saved versions reached. Update the current version or delete an older one to create another.
                                </p>
                            )}
                            <p className="text-slate-500">
                                Cancel closes this dialog without saving anything.
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
                            <button
                                type="button"
                                onClick={() => setPendingScriptSave(null)}
                                disabled={savingScriptKeys.has(pendingScriptSave.caseKey)}
                                className="secondary-btn px-4 py-2"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const next = pendingScriptSave;
                                    setPendingScriptSave(null);
                                    saveScriptVersion(next.testCase, {
                                        autosave: false,
                                        saveAsNewVersion: false,
                                        targetVersion: next.currentVersion,
                                    }).catch(() => {});
                                }}
                                disabled={savingScriptKeys.has(pendingScriptSave.caseKey)}
                                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Update Current Version
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const next = pendingScriptSave;
                                    setPendingScriptSave(null);
                                    saveScriptVersion(next.testCase, {
                                        autosave: false,
                                        saveAsNewVersion: true,
                                        targetVersion: null,
                                    }).catch(() => {});
                                }}
                                disabled={savingScriptKeys.has(pendingScriptSave.caseKey) || (pendingScriptSave.versionCount ?? 0) >= MAX_SAVED_VERSIONS}
                                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <Save className="w-4 h-4" />
                                {(pendingScriptSave.versionCount ?? 0) >= MAX_SAVED_VERSIONS ? 'Version Limit Reached' : 'Save as New Version'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Bulk actions and category filter */}
            <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="mb-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                        <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${actionDelayEnabled ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-500'}`}>
                            <Clock3 className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-slate-800">Action delay</p>
                            <p className="mt-0.5 text-xs text-slate-500">
                                Pause after each browser action so animations and page updates can settle.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                        <button
                            type="button"
                            role="switch"
                            aria-checked={actionDelayEnabled}
                            aria-label="Enable action delay"
                            onClick={() => setActionDelayEnabled((enabled) => !enabled)}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${actionDelayEnabled ? 'bg-indigo-600' : 'bg-slate-300'}`}
                        >
                            <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${actionDelayEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <span className="w-14 text-sm font-medium text-slate-700">
                            {actionDelayEnabled ? 'On' : 'Off'}
                        </span>
                        <label className={`flex items-center gap-2 text-sm font-medium ${actionDelayEnabled ? 'text-slate-700' : 'text-slate-400'}`}>
                            Delay
                            <input
                                type="number"
                                min="0"
                                max="10000"
                                step="100"
                                value={actionDelayMs}
                                disabled={!actionDelayEnabled}
                                onChange={(event) => {
                                    const nextValue = Number(event.target.value);
                                    setActionDelayMs(Number.isFinite(nextValue) ? Math.min(10000, Math.max(0, nextValue)) : 0);
                                }}
                                className="h-9 w-24 rounded-lg border border-slate-300 bg-white px-3 text-right text-sm font-semibold text-slate-800 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                                aria-label="Action delay in milliseconds"
                            />
                            <span>ms</span>
                        </label>
                    </div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                        {selectedCount > 0 && (
                            <span className="mr-auto text-xs font-medium text-slate-500">
                                {selectedCount} of {MAX_SCRIPT_GENERATION_CASES} selected
                            </span>
                        )}
                        <button
                            onClick={() => {
                                const next = new Set(selectedCases);
                                if (allFilteredSelected) {
                                    testCases.forEach(tc => next.delete(tc._case_key));
                                } else {
                                    let skippedCount = 0;
                                    testCases.forEach(tc => {
                                        if (next.has(tc._case_key)) return;
                                        if (next.size >= MAX_SCRIPT_GENERATION_CASES) {
                                            skippedCount += 1;
                                            return;
                                        }
                                        next.add(tc._case_key);
                                    });
                                    if (skippedCount > 0) {
                                        setErrors(prev => ({
                                            ...prev,
                                            bulk: `You can select up to ${MAX_SCRIPT_GENERATION_CASES} test cases for script generation.`
                                        }));
                                    } else {
                                        setErrors(prev => ({ ...prev, bulk: null }));
                                    }
                                }
                                setSelectedCases(next);
                            }}
                            disabled={testCases.length === 0}
                            className={`${secondaryButtonClass} whitespace-nowrap`}
                        >
                            {allFilteredSelected ? 'Clear Selection' : 'Select All'}
                        </button>
                        <button
                            onClick={generateSelectedScripts}
                            disabled={!pomSelectionsLoaded || selectedCases.size === 0 || selectedCases.size > MAX_SCRIPT_GENERATION_CASES || generateAllStatus === 'loading'}
                            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap ${scriptTheme.primaryButton}`}
                        >
                            {generateAllStatus === 'loading' ? (
                                <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Zap className="w-4 h-4" />
                                    Generate Selected
                                </>
                            )}
                        </button>
                        <button
                            onClick={runSelectedScripts}
                            disabled={!selectedHaveGenerated || runAllStatus === 'loading'}
                            className={`${secondaryButtonClass} whitespace-nowrap`}
                        >
                            {runAllStatus === 'loading' ? (
                                <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    Running...
                                </>
                            ) : (
                                <>
                                    <Play className="w-4 h-4" />
                                    Run Selected
                                </>
                            )}
                        </button>
                        <button
                            onClick={downloadPlaywrightProject}
                            disabled={projectExportLoading || generatedExportCount === 0}
                            className={`${secondaryButtonClass} whitespace-nowrap`}
                        >
                            {projectExportLoading ? (
                                <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4" />
                                    Download Project
                                </>
                            )}
                        </button>
                        <button
                            onClick={() => setShowGithubExportModal(true)}
                            disabled={!pdfId || !processingResult?.section_number}
                            className={`${secondaryButtonClass} whitespace-nowrap`}
                        >
                            <Github className="w-4 h-4" />
                            Push to GitHub
                        </button>
                    </div>

                    {scriptBatchJob && (
                        <div className="mt-4 rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-white p-4 shadow-sm" aria-live="polite">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm font-bold text-slate-900">Script generation</p>
                                    <p className="mt-0.5 text-xs text-slate-600">
                                        {scriptBatchJob.progress?.message || `${scriptBatchJob.progress?.current || 0} of ${scriptBatchJob.progress?.total || scriptBatchItems.length} processed`}
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    {['queued', 'running', 'cancelling'].includes(scriptBatchJob.status) && (
                                        <button type="button" onClick={cancelScriptBatch} disabled={scriptBatchJob.status === 'cancelling'} className={secondaryButtonClass}>Cancel pending</button>
                                    )}
                                    {['failed', 'partial_completed', 'cancelled'].includes(scriptBatchJob.status) && scriptBatchItems.some(item => ['failed', 'cancelled'].includes(item.status)) && (
                                        <button type="button" onClick={retryFailedScripts} className={secondaryButtonClass}><RefreshCw className="h-4 w-4" /> Retry failed</button>
                                    )}
                                </div>
                            </div>
                            <div className="mt-3 h-2 overflow-hidden rounded-full bg-indigo-100" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={scriptBatchJob.progress?.percent || 0}>
                                <div className="h-full rounded-full bg-indigo-600 transition-all" style={{ width: `${scriptBatchJob.progress?.percent || 0}%` }} />
                            </div>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {scriptBatchItems.map(item => (
                                    <div key={item.id || item.item_key} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs">
                                        {item.status === 'completed' ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : item.status === 'failed' ? <AlertCircle className="h-4 w-4 text-red-600" /> : <Loader className={`h-4 w-4 text-indigo-600 ${item.status === 'running' ? 'animate-spin' : ''}`} />}
                                        <span className="min-w-0 flex-1 truncate font-semibold text-slate-700">{item.test_case_id || item.item_key}</span>
                                        <span className="capitalize text-slate-500">{item.status === 'completed' ? 'Generated' : item.status}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {executionJob && (
                        <div className="mt-4 rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 to-white p-4 shadow-sm" aria-live="polite">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <p className="text-sm font-bold text-slate-900">Browser run</p>
                                    <p className="mt-0.5 text-xs text-slate-600">
                                        {executionJob.status === 'queued'
                                            ? `Waiting for a browser worker${executionJob.created_at ? ` since ${new Date(executionJob.created_at).toLocaleTimeString()}` : ''}`
                                            : executionJob.progress?.message || `${executionJob.progress?.current || 0} of ${executionJob.progress?.total || executionItems.length} input sets processed`}
                                    </p>
                                    {executionJob.started_at && (
                                        <p className="mt-1 text-[11px] text-slate-500">
                                            Queue wait: {Math.max(0, Math.round((new Date(executionJob.started_at) - new Date(executionJob.created_at)) / 1000))}s
                                            {executionJob.completed_at ? ` · Execution: ${Math.max(0, Math.round((new Date(executionJob.completed_at) - new Date(executionJob.started_at)) / 1000))}s` : ''}
                                        </p>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    {['queued', 'running', 'cancelling'].includes(executionJob.status) && (
                                        <button type="button" onClick={cancelExecution} disabled={executionJob.status === 'cancelling'} className={secondaryButtonClass}>Cancel run</button>
                                    )}
                                    {['failed', 'partial_completed', 'cancelled'].includes(executionJob.status) && executionItems.some(item => ['failed', 'cancelled'].includes(item.status)) && (
                                        <button type="button" onClick={retryFailedExecution} className={secondaryButtonClass}><RefreshCw className="h-4 w-4" /> Retry failed sets</button>
                                    )}
                                </div>
                            </div>
                            <div className="mt-3 h-2 overflow-hidden rounded-full bg-sky-100" role="progressbar" aria-label="Browser run progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow={executionJob.progress?.percent || 0}>
                                <div className="h-full rounded-full bg-sky-600 transition-all" style={{ width: `${executionJob.progress?.percent || 0}%` }} />
                            </div>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {executionItems.map(item => (
                                    <div key={item.id || item.item_key} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs">
                                        <div className="flex items-center gap-2">
                                            {item.status === 'completed' ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : item.status === 'failed' ? <AlertCircle className="h-4 w-4 text-red-600" /> : <Loader className={`h-4 w-4 text-sky-600 ${item.status === 'running' ? 'animate-spin' : ''}`} />}
                                            <span className="min-w-0 flex-1 truncate font-semibold text-slate-700">{item.test_case_id} · {item.set_name || item.input_set_id}</span>
                                            <span className="capitalize text-slate-500">{item.summary?.status || item.status}</span>
                                        </div>
                                        {item.summary?.report_url && (
                                            <a href={item.summary.report_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 font-semibold text-sky-700 hover:text-sky-900"><ExternalLink className="h-3 w-3" /> View report</a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                {/* Category Filter */}
                {allTests.length > 0 && (
                    <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3">
                        <Filter className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Filter</span>
                        <button
                            onClick={() => setCategoryFilter('all')}
                            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${categoryFilter === 'all'
                                ? scriptTheme.primaryButton
                                : `border border-slate-300 bg-white text-slate-700 ${scriptTheme.secondaryHover}`
                                }`}
                        >
                            All ({allTests.length})
                        </button>
                        {positiveCount > 0 && (
                            <button
                                onClick={() => setCategoryFilter('positive')}
                                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${categoryFilter === 'positive'
                                    ? scriptTheme.primaryButton
                                    : `border border-slate-300 bg-white text-slate-700 ${scriptTheme.secondaryHover}`
                                    }`}
                            >
                                Positive ({positiveCount})
                            </button>
                        )}
                        {negativeCount > 0 && (
                            <button
                                onClick={() => setCategoryFilter('negative')}
                                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${categoryFilter === 'negative'
                                    ? scriptTheme.primaryButton
                                    : `border border-slate-300 bg-white text-slate-700 ${scriptTheme.secondaryHover}`
                                    }`}
                            >
                                Negative ({negativeCount})
                            </button>
                        )}
                        {manualPositiveCount > 0 && (
                            <button
                                onClick={() => setCategoryFilter('manual_positive')}
                                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${categoryFilter === 'manual_positive'
                                    ? scriptTheme.primaryButton
                                    : `border border-slate-300 bg-white text-slate-700 ${scriptTheme.secondaryHover}`
                                    }`}
                            >
                                Manual Positive ({manualPositiveCount})
                            </button>
                        )}
                        {manualNegativeCount > 0 && (
                            <button
                                onClick={() => setCategoryFilter('manual_negative')}
                                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${categoryFilter === 'manual_negative'
                                    ? scriptTheme.primaryButton
                                    : `border border-slate-300 bg-white text-slate-700 ${scriptTheme.secondaryHover}`
                                    }`}
                            >
                                Manual Negative ({manualNegativeCount})
                            </button>
                        )}
                    </div>
                )}

                {/* Bulk Error */}
                {errors.bulk && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded flex gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                            {isPomRequiredBulkError ? (
                                <>
                                    <p className="text-sm text-red-800 font-medium">Page Object Model Required</p>
                                    <p className="text-sm text-red-600 mt-1">
                                        You must select a Page Object Model before generating test scripts.
                                            <button
                                                onClick={() => handleOpenPomModal({ type: 'all' })}
                                                className="ml-1 font-semibold text-red-700 hover:text-red-900 underline"
                                            >
                                                Select common POMs
                                            </button>
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm text-red-600">{errors.bulk}</p>
                            )}
                        </div>
                    </div>
                )}
                {errors.bulkRun && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded flex gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600">{errors.bulkRun}</p>
                    </div>
                )}
                {projectExportError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded flex gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600">{projectExportError}</p>
                    </div>
                )}

                {/* Bulk Success */}
                {generateAllStatus === 'success' && (
                    <div className="flex gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                        <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                        <p className="text-sm text-emerald-800">Selected scripts generated successfully.</p>
                    </div>
                )}
                {runAllStatus === 'success' && (
                    <div className="flex gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                        <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                        <p className="text-sm text-emerald-800">✓ Selected scripts executed successfully!</p>
                    </div>
                )}
                {runAllStatus === 'partial' && (
                    <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
                        <p className="text-sm text-amber-900">Completed input sets are available. Retry only the failed sets from the browser run card.</p>
                    </div>
                )}
                {projectExportStatus && (
                    <div className="flex gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                        <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                        <p className="text-sm text-emerald-800">
                            Downloaded Playwright project with {projectExportStatus.scripts} script{projectExportStatus.scripts === 1 ? '' : 's'}, {projectExportStatus.poms} POM file{projectExportStatus.poms === 1 ? '' : 's'}, and {projectExportStatus.files} total file{projectExportStatus.files === 1 ? '' : 's'}.
                            {projectExportStatus.warnings > 0 ? ` ${projectExportStatus.warnings} warning${projectExportStatus.warnings === 1 ? '' : 's'} added to the manifest.` : ''}
                        </p>
                    </div>
                )}
            </div>

            {/* Test Cases and Scripts */}
            {testCases.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm text-amber-800 font-medium">No test cases available</p>
                        <p className="text-sm text-amber-600 mt-1">
                            {categoryFilter === 'all'
                                ? 'Generate test cases first to create automation scripts.'
                                : `No ${getFilterDisplayLabel(categoryFilter)} test cases available. Try selecting a different category.`}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="grid gap-5 2xl:grid-cols-[320px_minmax(0,1fr)]">
                    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden 2xl:sticky 2xl:top-24">
                        <div className="border-b border-slate-200 px-3 py-3">
                            <p className="text-sm font-semibold text-slate-900">Test Cases</p>
                            <p className="mt-1 text-xs text-slate-500">Pick one case to inspect.</p>
                        </div>
                        <div className="max-h-[calc(100vh-220px)] overflow-y-auto">
                            {testCases.map((testCase) => {
                                const isActive = inspectorCase?._case_key === testCase._case_key;
                                const caseSelection = getPomSelectionForCaseKey(testCase._case_key);
                                const casePoms = caseSelection.poms;
                                const hasScript = Boolean(generatedScripts[testCase._case_key]);
                                const hasRun = Boolean(runResults[testCase._case_key]);
                                const runOutput = runResults[testCase._case_key] || {};
                                const runPassed = hasRun && resolveExitCode(runOutput.exit_code) === 0;
                                const staleMeta = getScriptStaleMeta(testCase._case_key);
                                return (
                                    <button
                                        key={testCase._case_key}
                                        type="button"
                                        onClick={() => {
                                            setExpandedCase(testCase._case_key);
                                            setErrors((prev) => ({ ...prev, bulk: null }));
                                        }}
                                        className={`w-full border-b border-l-4 border-slate-100 px-3 py-3 text-left transition ${isActive ? `${scriptTheme.selectedBorder} ${scriptTheme.selectedTint}` : 'border-l-transparent bg-white hover:bg-slate-50/70'}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedCases.has(testCase._case_key)}
                                                disabled={!selectedCases.has(testCase._case_key) && isSelectionAtLimit}
                                                title={
                                                    !selectedCases.has(testCase._case_key) && isSelectionAtLimit
                                                        ? `Select up to ${MAX_SCRIPT_GENERATION_CASES} test cases for script generation.`
                                                        : 'Select this test case'
                                                }
                                                onChange={(event) => {
                                                    event.stopPropagation();
                                                    setSelectedCases((prev) => {
                                                        const next = new Set(prev);
                                                        if (next.has(testCase._case_key)) {
                                                            next.delete(testCase._case_key);
                                                            setErrors((current) => ({ ...current, bulk: null }));
                                                        } else {
                                                            if (next.size >= MAX_SCRIPT_GENERATION_CASES) {
                                                                setErrors((current) => ({
                                                                    ...current,
                                                                    bulk: `You can select up to ${MAX_SCRIPT_GENERATION_CASES} test cases for script generation.`
                                                                }));
                                                                return next;
                                                            }
                                                            next.add(testCase._case_key);
                                                            setErrors((current) => ({ ...current, bulk: null }));
                                                        }
                                                        return next;
                                                    });
                                                }}
                                                onClick={(event) => event.stopPropagation()}
                                                className={`mt-1 h-4 w-4 ${scriptTheme.accentControl} disabled:cursor-not-allowed disabled:opacity-50`}
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <span className="text-sm font-semibold text-slate-900">{testCase.test_case_no || 'Test Case'}</span>
                                                    {testCase._category && (
                                                        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${getCategoryBadgeClass(testCase._category)}`}>
                                                            {getCategoryLabel(testCase._category)}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="mt-1 line-clamp-2 text-xs text-slate-600">
                                                    {testCase.test_description || testCase.test_name || 'Test Case'}
                                                </p>
                                                <div className="mt-2 flex flex-wrap gap-1.5">
                                                    <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                                                        <span className={`h-1.5 w-1.5 rounded-full ${staleMeta.isStale ? 'bg-amber-500' : hasScript ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                                        {staleMeta.isStale ? 'Regenerate' : hasScript ? 'Script ready' : 'No script'}
                                                    </span>
                                                    <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${isApiScriptFlow || casePoms.length ? 'bg-slate-100 text-slate-600' : 'bg-amber-50 text-amber-700'}`}>
                                                        {isApiScriptFlow ? 'API' : casePoms.length ? `${casePoms.length} POM${casePoms.length === 1 ? '' : 's'}` : 'No POM'}
                                                    </span>
                                                    {hasRun && (
                                                        <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${runPassed ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                                                            {runPassed ? 'Run passed' : 'Run failed'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="min-w-0 rounded-lg border border-slate-200 bg-white overflow-hidden">
                        {inspectorCase ? (
                            <>
                                <div className="border-b border-slate-200 bg-white px-5 py-4">
                                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="text-base font-semibold text-slate-900">{inspectorCase.test_case_no || 'Test Case'}</p>
                                                {inspectorCase._category && (
                                                    <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${getCategoryBadgeClass(inspectorCase._category)}`}>
                                                        {getCategoryLabel(inspectorCase._category)}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-1 text-sm text-slate-600">
                                                {inspectorCase.test_description || inspectorCase.test_name || 'Test Case'}
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {!generatedScripts[inspectorCase._case_key] ? (
                                                <button
                                                    onClick={() => generateScriptForCase(inspectorCase)}
                                                    disabled={!pomSelectionsLoaded || loadingCases.has(inspectorCase._case_key)}
                                                    className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${scriptTheme.primaryButton}`}
                                                >
                                                    {loadingCases.has(inspectorCase._case_key) ? <Loader className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                                                    Generate Script
                                                </button>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => {
                                                            if (editModes.has(inspectorCase._case_key)) {
                                                                requestScriptSave(inspectorCase);
                                                                return;
                                                            }
                                                            setEditModes((prev) => {
                                                                const next = new Set(prev);
                                                                next.add(inspectorCase._case_key);
                                                                return next;
                                                            });
                                                        }}
                                                        disabled={!generatedScripts[inspectorCase._case_key] || savingScriptKeys.has(inspectorCase._case_key)}
                                                        className={`inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition ${scriptTheme.secondaryHover} disabled:cursor-not-allowed disabled:opacity-50`}
                                                    >
                                                        {savingScriptKeys.has(inspectorCase._case_key) ? (
                                                            <Loader className="h-4 w-4 animate-spin" />
                                                        ) : editModes.has(inspectorCase._case_key) ? (
                                                            <Save className="h-4 w-4" />
                                                        ) : (
                                                            <Pencil className="h-4 w-4" />
                                                        )}
                                                        {editModes.has(inspectorCase._case_key) ? 'Save' : 'Edit'}
                                                    </button>
                                                    <button
                                                        onClick={() => runScriptForCase(inspectorCase)}
                                                        disabled={runningCases.has(inspectorCase._case_key) || getScriptStaleMeta(inspectorCase._case_key).isStale}
                                                        title={getScriptStaleMeta(inspectorCase._case_key).isStale ? 'Regenerate this script before running.' : ''}
                                                        className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${scriptTheme.primaryButton}`}
                                                    >
                                                        {runningCases.has(inspectorCase._case_key) ? <Loader className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                                                        Run
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-200 pt-3">
                                        {[
                                            ['script', 'Script', FileCode],
                                            ['inputs', 'Inputs', Pencil],
                                            ['poms', 'POMs', BookOpen],
                                            ['run', 'Run Output', Play],
                                            ['metadata', 'Metadata', List],
                                        ].filter(([tabId]) => !isApiScriptFlow || tabId !== 'poms').map(([tabId, label, Icon]) => (
                                            <button
                                                key={tabId}
                                                type="button"
                                                onClick={() => setActiveInspectorTab(tabId)}
                                                className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] font-semibold transition ${activeInspectorTab === tabId ? scriptTheme.primaryButton : `border border-slate-200 bg-white text-slate-600 ${scriptTheme.secondaryHover}`}`}
                                            >
                                                <Icon className="h-4 w-4" />
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {getScriptStaleMeta(inspectorCase._case_key).isStale && (
                                    <div className="border-b border-amber-200 bg-amber-50 px-5 py-3">
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex gap-2 text-sm text-amber-800">
                                                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                                <p>{getScriptStaleMeta(inspectorCase._case_key).message}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    setRunResults((prev) => {
                                                        const next = { ...prev };
                                                        delete next[inspectorCase._case_key];
                                                        return next;
                                                    });
                                                    setRunErrors((prev) => {
                                                        const next = { ...prev };
                                                        delete next[inspectorCase._case_key];
                                                        return next;
                                                    });
                                                    await generateScriptForCase(inspectorCase);
                                                }}
                                                disabled={!pomSelectionsLoaded || loadingCases.has(inspectorCase._case_key)}
                                                className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                {loadingCases.has(inspectorCase._case_key) ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                                                Regenerate
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="bg-slate-50/60 p-5">
                                    {activeInspectorTab === 'script' && (
                                        <div className="space-y-3">
                                            {generatedScripts[inspectorCase._case_key] ? (
                                                <>
                                                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                                                        <div>
                                                            {savingScriptKeys.has(inspectorCase._case_key) ? (
                                                                <span className="font-semibold text-slate-700">
                                                                    Saving script...
                                                                </span>
                                                            ) : scriptSaveErrors[inspectorCase._case_key] ? (
                                                                <span className="font-semibold text-red-700">
                                                                    {resolveCaughtErrorMessage(scriptSaveErrors[inspectorCase._case_key], 'Failed to save script')}
                                                                </span>
                                                            ) : isFeatureMode ? (
                                                                <span className="font-semibold text-slate-700">Saved to application</span>
                                                            ) : scriptSaveMeta[inspectorCase._case_key]?.version ? (
                                                                <span className="font-semibold text-slate-700">Saved script</span>
                                                            ) : (
                                                                <span>No saved version yet.</span>
                                                            )}
                                                            {scriptSaveMeta[inspectorCase._case_key]?.updated_at && (
                                                                <span className="ml-2 text-slate-500">
                                                                    Saved {new Date(scriptSaveMeta[inspectorCase._case_key].updated_at).toLocaleString()}
                                                                </span>
                                                            )}
                                                            {dirtyScriptKeys.has(inspectorCase._case_key) && (
                                                                <span className="ml-2 font-semibold text-amber-700">Unsaved local edits</span>
                                                            )}
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => copyToClipboard(generatedScripts[inspectorCase._case_key], inspectorCase._case_key)}
                                                                className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-700 hover:bg-slate-100"
                                                            >
                                                                <Copy className="h-3.5 w-3.5" />
                                                                {copiedId === inspectorCase._case_key ? 'Copied!' : 'Copy'}
                                                            </button>
                                                            <button
                                                                onClick={() => downloadScript(generatedScripts[inspectorCase._case_key], inspectorCase.test_case_no || inspectorCase._case_key)}
                                                                className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-700 hover:bg-slate-100"
                                                            >
                                                                <Download className="h-3.5 w-3.5" />
                                                                Download
                                                            </button>
                                                            <button
                                                                onClick={async () => {
                                                                    setRunResults((prev) => {
                                                                        const next = { ...prev };
                                                                        delete next[inspectorCase._case_key];
                                                                        return next;
                                                                    });
                                                                    setRunErrors((prev) => {
                                                                        const next = { ...prev };
                                                                        delete next[inspectorCase._case_key];
                                                                        return next;
                                                                    });
                                                                    await generateScriptForCase(inspectorCase);
                                                                }}
                                                                disabled={!pomSelectionsLoaded || loadingCases.has(inspectorCase._case_key)}
                                                                className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                            >
                                                                {loadingCases.has(inspectorCase._case_key) ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                                                                Regenerate
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {editModes.has(inspectorCase._case_key) ? (
                                                        <CodeEditorPreview
                                                            value={generatedScripts[inspectorCase._case_key]}
                                                            editable
                                                            minHeight={520}
                                                            maxHeight={620}
                                                            onChange={(value) => {
                                                                setGeneratedScripts((prev) => ({ ...prev, [inspectorCase._case_key]: value }));
                                                                setDirtyScriptKeys((prev) => new Set([...prev, inspectorCase._case_key]));
                                                            }}
                                                        />
                                                    ) : (
                                                        <CodeEditorPreview
                                                            value={generatedScripts[inspectorCase._case_key]}
                                                            minHeight={520}
                                                            maxHeight={620}
                                                        />
                                                    )}
                                                </>
                                            ) : (
                                                <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                                                    <FileCode className="mx-auto h-8 w-8 text-slate-400" />
                                                    <p className="mt-3 text-sm font-semibold text-slate-900">No script generated yet</p>
                                                    <p className="mt-1 text-sm text-slate-600">
                                                        {isApiScriptFlow
                                                            ? 'Generate the Playwright API script directly from this contract test case.'
                                                            : isFeatureMode
                                                                ? 'Captured page objects are already attached. Generate the Playwright script for this test case.'
                                                            : 'Select POMs, then generate the Playwright script for this test case.'}
                                                    </p>
                                                </div>
                                            )}
                                            {(scriptAssumptions[inspectorCase._case_key]?.length > 0 || scriptWarnings[inspectorCase._case_key]?.length > 0) && (
                                                <div className="grid gap-3 lg:grid-cols-2">
                                                    {scriptAssumptions[inspectorCase._case_key]?.length > 0 && (
                                                        <div className="rounded-lg border border-slate-200 bg-white p-4">
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Assumptions</p>
                                                            <ul className="mt-2.5 space-y-1.5 text-[13px] leading-relaxed text-slate-700">
                                                                {scriptAssumptions[inspectorCase._case_key].map((item, index) => <li key={index}>{normalizeErrorDetail(item)}</li>)}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    {scriptWarnings[inspectorCase._case_key]?.length > 0 && (
                                                        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                                                            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Warnings</p>
                                                            <ul className="mt-2.5 space-y-1.5 text-[13px] leading-relaxed text-amber-900">
                                                                {scriptWarnings[inspectorCase._case_key].map((item, index) => <li key={index}>{normalizeErrorDetail(item)}</li>)}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeInspectorTab === 'inputs' && (() => {
                                        const caseKey = inspectorCase._case_key;
                                        const draftSets = getCaseInputDraft(inspectorCase);
                                        const isDirty = caseInputDirtyKeys.has(caseKey);
                                        const isSavingInputs = savingCaseInputKeys.has(caseKey);
                                        const savedAt = caseInputSaveMeta[caseKey]?.saved_at;
                                        const canAddSet = draftSets.length < MAX_INPUT_SETS_PER_CASE;
                                        return (
                                            <div className="space-y-4">
                                                <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm lg:flex-row lg:items-center lg:justify-between">
                                                    <div>
                                                        <p className="text-sm font-semibold text-slate-900">Test Inputs</p>
                                                        <div className="mt-1 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
                                                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5">
                                                                {draftSets.length || 0} set{draftSets.length === 1 ? '' : 's'}
                                                            </span>
                                                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5">
                                                                {draftSets.reduce((total, setItem) => total + Number(setItem?.user_inputs?.length || 0), 0)} field{draftSets.reduce((total, setItem) => total + Number(setItem?.user_inputs?.length || 0), 0) === 1 ? '' : 's'}
                                                            </span>
                                                            {isDirty && (
                                                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700">
                                                                    Unsaved edits
                                                                </span>
                                                            )}
                                                            {savedAt && !isDirty && (
                                                                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700">
                                                                    Saved {new Date(savedAt).toLocaleTimeString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => addInputSetForCase(inspectorCase)}
                                                            disabled={!canAddSet || isSavingInputs}
                                                            title={canAddSet ? 'Add a new input set' : `Up to ${MAX_INPUT_SETS_PER_CASE} sets per case`}
                                                            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            <Plus className="h-4 w-4" />
                                                            Add Set
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => resetCaseInputDraft(inspectorCase)}
                                                            disabled={!isDirty || isSavingInputs}
                                                            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            <RotateCcw className="h-4 w-4" />
                                                            Reset
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => saveCaseInputValues(inspectorCase)}
                                                            disabled={!isDirty || isSavingInputs}
                                                            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${scriptTheme.primaryButton}`}
                                                        >
                                                            {isSavingInputs ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                                            Save Inputs
                                                        </button>
                                                    </div>
                                                </div>

                                                {caseInputSaveErrors[caseKey] && (
                                                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                                                        {caseInputSaveErrors[caseKey]}
                                                    </div>
                                                )}

                                                {draftSets.length === 0 ? (
                                                    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                                                        <Pencil className="mx-auto h-8 w-8 text-slate-400" />
                                                        <p className="mt-3 text-sm font-semibold text-slate-900">No input sets configured yet</p>
                                                        <button
                                                            type="button"
                                                            onClick={() => addInputSetForCase(inspectorCase)}
                                                            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                                                        >
                                                            <Plus className="h-3.5 w-3.5" />
                                                            Add Set
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-3">
                                                        {draftSets.map((setItem, setIndex) => {
                                                            const setLabel = String(setItem?.set_name || `Set ${setIndex + 1}`).trim() || `Set ${setIndex + 1}`;
                                                            const setType = String(setItem?.set_type || '').trim();
                                                            const inputs = normalizeUserInputList(setItem?.user_inputs || []);
                                                            return (
                                                                <div key={`${caseKey}-editor-input-set-${setIndex}`} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                                                                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                            <p className="text-sm font-semibold text-slate-900">{setLabel}</p>
                                                                            {setType && (
                                                                                <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${scriptTheme.chip}`}>
                                                                                    {setType}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => addInputRowToDraft(inspectorCase, setIndex)}
                                                                                disabled={isSavingInputs}
                                                                                className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                                                                            >
                                                                                <Plus className="h-3.5 w-3.5" />
                                                                                Add Field
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => removeInputSetForCase(inspectorCase, setIndex)}
                                                                                disabled={draftSets.length <= 1 || isSavingInputs}
                                                                                title={draftSets.length <= 1 ? 'At least one set is required' : 'Remove this set'}
                                                                                className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                                                                            >
                                                                                <Trash2 className="h-3.5 w-3.5" />
                                                                                Remove Set
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    {inputs.length === 0 ? (
                                                                        <p className="rounded border border-dashed border-slate-300 px-3 py-3 text-center text-xs text-slate-500">
                                                                            No fields in this set yet.
                                                                        </p>
                                                                    ) : (
                                                                        <div className="grid gap-3 md:grid-cols-2">
                                                                            {inputs.map((inputItem, inputIndex) => (
                                                                                <div key={`${caseKey}-editor-input-${setIndex}-${inputIndex}`} className="rounded-lg border border-slate-200 p-2">
                                                                                    <div className="flex items-center justify-between gap-2">
                                                                                        <input
                                                                                            type="text"
                                                                                            value={inputItem.field || ''}
                                                                                            onChange={(event) => updateCaseInputDraftField(
                                                                                                inspectorCase,
                                                                                                setIndex,
                                                                                                inputIndex,
                                                                                                event.target.value
                                                                                            )}
                                                                                            placeholder="Field name"
                                                                                            className="min-w-0 flex-1 border-b border-transparent bg-transparent text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 focus:border-slate-300 focus:outline-none"
                                                                                        />
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => removeInputRowFromDraft(inspectorCase, setIndex, inputIndex)}
                                                                                            disabled={isSavingInputs}
                                                                                            aria-label="Remove field"
                                                                                            className="inline-flex items-center rounded p-1 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                                                                                        >
                                                                                            <Trash2 className="h-3.5 w-3.5" />
                                                                                        </button>
                                                                                    </div>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={inputItem.value || ''}
                                                                                        onChange={(event) => updateCaseInputDraftValue(
                                                                                            inspectorCase,
                                                                                            setIndex,
                                                                                            inputIndex,
                                                                                            event.target.value
                                                                                        )}
                                                                                        className="text-input mt-1 bg-white text-sm"
                                                                                        placeholder="Value"
                                                                                    />
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {activeInspectorTab === 'poms' && (() => {
                                        const caseSelection = getPomSelectionForCaseKey(inspectorCase._case_key);
                                        const pomsPayload = associatedPomsByCaseKey[inspectorCase._case_key] || {};
                                        const poms = Array.isArray(pomsPayload.poms) ? pomsPayload.poms : [];
                                        const warnings = Array.isArray(pomsPayload.warnings) ? pomsPayload.warnings : [];
                                        const hasAssociatedPomPayload = Boolean(associatedPomsByCaseKey[inspectorCase._case_key]);
                                        const visiblePoms = hasAssociatedPomPayload ? poms : caseSelection.poms;
                                        return (
                                            <div className="space-y-5">
                                                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
                                                    <div>
                                                        <p className="text-sm font-semibold text-slate-900">
                                                            {isFeatureMode ? 'Attached POMs' : 'Selected POMs'}
                                                        </p>
                                                        <div className="mt-1 flex flex-wrap gap-1.5">
                                                            {caseSelection.poms.length ? caseSelection.poms.map((pom) => (
                                                                <span key={`${inspectorCase._case_key}-inspector-pom-${buildPomSelectionKey(pom)}`} className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${scriptTheme.chip}`}>
                                                                    {pomScopePrefixLabel(pom)}{pom.pageName}
                                                                </span>
                                                            )) : <span className="text-xs font-semibold text-amber-700">No POM selected</span>}
                                                        </div>
                                                    </div>
                                                    {!isFeatureMode && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenPomModal({ type: 'case', caseKey: inspectorCase._case_key, testCaseId: getResolvedTestCaseId(inspectorCase) })}
                                                            className={`rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition ${scriptTheme.secondaryHover}`}
                                                        >
                                                            Change POMs
                                                        </button>
                                                    )}
                                                </div>
                                                {associatedPomErrors[inspectorCase._case_key] && !loadingAssociatedPoms.has(inspectorCase._case_key) && (
                                                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                                                        {associatedPomErrors[inspectorCase._case_key]}
                                                    </div>
                                                )}
                                                {warnings.length > 0 && (
                                                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                                        {warnings.join(' ')}
                                                    </div>
                                                )}
                                                {visiblePoms.length === 0 ? (
                                                    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                                                        <BookOpen className="mx-auto h-8 w-8 text-slate-400" />
                                                        <p className="mt-3 text-sm font-semibold text-slate-900">No POM selected</p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-5">
                                                        {visiblePoms.map((pom, pomIndex) => {
                                                            const code = String(pom.typescriptCode || '');
                                                            const configText = typeof pom.configText === 'string'
                                                                ? pom.configText
                                                                : stringifyAssociatedPomConfig(pom.config);
                                                            const viewerKey = buildAssociatedPomViewerKey(inspectorCase._case_key, pom, pomIndex);
                                                            const isTypeScriptOpen = openAssociatedPomTypeScriptPanels.has(viewerKey);
                                                            return (
                                                                <div key={`${inspectorCase._case_key}-pom-inspector-${pom.scope || 'local'}-${pom.pageName || pomIndex}`} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                                                                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3">
                                                                        <div>
                                                                            <p className="text-sm font-semibold text-slate-900">{pomScopePrefixLabel(pom)}{pom.pageName || 'POM'}</p>
                                                                            <p className="text-xs text-slate-500 break-all">{pom.pageUrl || 'No page URL saved'}</p>
                                                                        </div>
                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                            {canEditAssociatedPomFile(pom) ? (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => beginAssociatedPomFileEdit(inspectorCase, pom, pomIndex)}
                                                                                    className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                                                                >
                                                                                    <Pencil className="h-3.5 w-3.5" />
                                                                                    Edit Files
                                                                                </button>
                                                                            ) : pom.scope === 'application' ? (
                                                                                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-500" title="Edit this in Application &gt; Manage POMs">Shared, read-only here</span>
                                                                            ) : pom.scope === 'global' ? (
                                                                                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-500">Global read-only</span>
                                                                            ) : null}
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => copyToClipboard(code || configText, `${inspectorCase._case_key}-pom-${pomIndex}`)}
                                                                                disabled={!hasAssociatedPomPayload}
                                                                                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                                            >
                                                                                {copiedId === `${inspectorCase._case_key}-pom-${pomIndex}` ? 'Copied!' : 'Copy POM'}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <div className="space-y-3 bg-slate-50 p-4">
                                                                        {renderAssociatedPomFileEditor(inspectorCase, pom, pomIndex, { minHeight: 300, maxHeight: 620 })}
                                                                        <CollapsibleAssociatedPomTypeScript
                                                                            isOpen={isTypeScriptOpen}
                                                                            isLoading={loadingAssociatedPoms.has(inspectorCase._case_key)}
                                                                            error={associatedPomErrors[inspectorCase._case_key]}
                                                                            code={hasAssociatedPomPayload ? code : ''}
                                                                            minHeight={260}
                                                                            maxHeight={560}
                                                                            onToggle={() => toggleAssociatedPomTypeScript(inspectorCase, pom, pomIndex)}
                                                                        />
                                                                        {hasAssociatedPomPayload && (
                                                                            <details className="rounded-lg border border-slate-200 bg-white">
                                                                                <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                                                                                    Config JSON
                                                                                </summary>
                                                                                <div className="border-t border-slate-200 p-3">
                                                                                    <AssociatedPomCodePreview
                                                                                        value={configText}
                                                                                        language="json"
                                                                                        minHeight={140}
                                                                                        maxHeight={280}
                                                                                    />
                                                                                </div>
                                                                            </details>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {activeInspectorTab === 'run' && (() => {
                                        const runOutput = runResults[inspectorCase._case_key];
                                        const runError = runErrors[inspectorCase._case_key];
                                        if (!runOutput && !runError) {
                                            return (
                                                <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                                                    <Play className="mx-auto h-8 w-8 text-slate-400" />
                                                    <p className="mt-3 text-sm font-semibold text-slate-900">No run output yet</p>
                                                    <p className="mt-1 text-sm text-slate-600">Run the script to view execution status and logs.</p>
                                                </div>
                                            );
                                        }
                                        if (runError && !runOutput) {
                                            const runErrorMessage = resolveCaughtErrorMessage(runError, 'Failed to run script');
                                            return (
                                                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                                                    <p className="text-sm font-semibold text-red-800">Run Error</p>
                                                    <p className="mt-1 text-sm text-red-700">{runErrorMessage}</p>
                                                </div>
                                            );
                                        }
                                        const runEntries = Array.isArray(runOutput?.runs) && runOutput.runs.length > 0 ? runOutput.runs : [runOutput];
                                        const totalSets = Number(runOutput?.total_sets || runEntries.length || 0);
                                        const passedSets = Number(runOutput?.passed_sets ?? runEntries.filter((entry) => resolveExitCode(entry?.exit_code) === 0).length);
                                        const failedSets = Number(runOutput?.failed_sets ?? Math.max(0, totalSets - passedSets));
                                        const htmlReports = Array.isArray(runOutput?.html_reports)
                                            ? runOutput.html_reports
                                            : runEntries
                                                .map((entry) => resolveRunHtmlReport(entry))
                                                .filter((item) => item && getHtmlReportUrl(item));
                                        return (
                                            <div className="space-y-4">
                                                <div className={`rounded-lg border px-4 py-3 ${failedSets > 0 ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
                                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                                        <div>
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <p className={`text-sm font-semibold ${failedSets > 0 ? 'text-red-800' : 'text-emerald-800'}`}>
                                                                    {failedSets > 0 ? 'Run Failed' : 'Run Passed'}
                                                                </p>
                                                            </div>
                                                            <p className="mt-1 text-xs text-slate-700">Sets: {passedSets}/{totalSets} passed · Duration: {Number(runOutput.duration_ms || 0)} ms</p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const reportUrls = htmlReports.map((item) => getHtmlReportUrl(item)).filter(Boolean);
                                                                const screenshotUrls = Array.isArray(runOutput?.screenshots)
                                                                    ? runOutput.screenshots.map((item) => String(item?.viewer_url || item?.presigned_url || '').trim()).filter(Boolean)
                                                                    : [];
                                                                const message = [
                                                                    `Status: ${failedSets > 0 ? 'Failed' : 'Passed'}`,
                                                                    `Sets: ${passedSets}/${totalSets} passed`,
                                                                    `Duration: ${Number(runOutput.duration_ms || 0)} ms`,
                                                                    runOutput?.stdout ? `Stdout:\n${runOutput.stdout}` : '',
                                                                    runOutput?.stderr ? `Stderr:\n${runOutput.stderr}` : '',
                                                                    reportUrls.length ? `HTML Reports:\n${reportUrls.join('\n')}` : '',
                                                                    screenshotUrls.length ? `Screenshots:\n${screenshotUrls.join('\n')}` : '',
                                                                ].filter(Boolean).join('\n\n');
                                                                copyToClipboard(message, `${inspectorCase._case_key}-run-output`);
                                                            }}
                                                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                                                        >
                                                            <Copy className="h-3.5 w-3.5" />
                                                            {copiedId === `${inspectorCase._case_key}-run-output` ? 'Copied' : 'Copy Output'}
                                                        </button>
                                                    </div>
                                                </div>

                                                {htmlReports.length > 0 && (
                                                    <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
                                                        <p className="text-sm font-semibold text-violet-900">Playwright Reports</p>
                                                        <div className="mt-2 flex flex-wrap gap-2">
                                                            {htmlReports.map((report, reportIndex) => {
                                                                const reportUrl = getHtmlReportUrl(report);
                                                                const expiryLabel = report?.viewer_url
                                                                    ? ''
                                                                    : formatExpiryDuration(Number(report?.presigned_expires_in_seconds || 0));
                                                                return reportUrl ? (
                                                                    <a
                                                                        key={`${inspectorCase._case_key}-html-report-${reportIndex}`}
                                                                        href={reportUrl}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700"
                                                                    >
                                                                        <ExternalLink className="h-3.5 w-3.5" />
                                                                        Report {reportIndex + 1}{expiryLabel ? ` · ${expiryLabel}` : ''}
                                                                    </a>
                                                                ) : null;
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {runEntries.map((entry, entryIndex) => {
                                                    const entryReport = resolveRunHtmlReport(entry);
                                                    const entryReportUrl = getHtmlReportUrl(entryReport);
                                                    const entryReportExpiryLabel = entryReport?.viewer_url
                                                        ? ''
                                                        : formatExpiryDuration(Number(entryReport?.presigned_expires_in_seconds || 0));
                                                    const reportRefreshKey = `${inspectorCase._case_key}-${entry?.run_id || 'missing'}-${entryIndex}`;
                                                    const isRefreshingReport = refreshingReportKeys.has(reportRefreshKey);
                                                    const isPassed = resolveExitCode(entry?.exit_code) === 0;
                                                    return (
                                                        <div key={`${inspectorCase._case_key}-run-entry-${entryIndex}`} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                                                <div>
                                                                    <p className="text-sm font-semibold text-slate-900">{entry?.set_label || `Set ${entryIndex + 1}`}</p>
                                                                    <p className={`mt-1 text-xs font-semibold ${isPassed ? 'text-emerald-700' : 'text-red-700'}`}>
                                                                        {isPassed ? 'Passed' : 'Failed'} · {Number(entry?.duration_ms || 0)} ms
                                                                    </p>
                                                                </div>
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {entryReportUrl && (
                                                                        <>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => copyToClipboard(entryReportUrl, `${inspectorCase._case_key}-html-report-url-${entryIndex}`)}
                                                                                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                                                                            >
                                                                                <Copy className="h-3 w-3" />
                                                                                {copiedId === `${inspectorCase._case_key}-html-report-url-${entryIndex}` ? 'Copied' : 'Copy Link'}
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => refreshHtmlReportLink(inspectorCase._case_key, entry.run_id, entryIndex)}
                                                                                disabled={!entry?.run_id || isRefreshingReport}
                                                                                className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-[11px] font-semibold text-violet-700 hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
                                                                            >
                                                                                {isRefreshingReport ? <Loader className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
                                                                                Refresh
                                                                            </button>
                                                                            <a href={entryReportUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-700">
                                                                                <ExternalLink className="h-3 w-3" />
                                                                                Open Report{entryReportExpiryLabel ? ` · ${entryReportExpiryLabel}` : ''}
                                                                            </a>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {entry?.stdout && (
                                                                <pre className="mt-3 max-h-48 overflow-auto rounded-lg border border-slate-200 bg-slate-900 p-3 text-xs text-slate-100 whitespace-pre-wrap">
                                                                    {entry.stdout}
                                                                </pre>
                                                            )}
                                                            {entry?.stderr && (
                                                                <pre className="mt-3 max-h-48 overflow-auto rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 whitespace-pre-wrap">
                                                                    {entry.stderr}
                                                                </pre>
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                {failedSets > 0 && runOutput?.stderr && (
                                                    <div className="rounded-lg border border-red-200 bg-white p-3">
                                                        <div className="mb-2 flex items-center justify-between gap-2">
                                                            <p className="text-sm font-semibold text-red-800">Combined Error Log</p>
                                                            <button
                                                                type="button"
                                                                onClick={() => copyToClipboard(runOutput.stderr, `${inspectorCase._case_key}-stderr`)}
                                                                className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-200"
                                                            >
                                                                <Copy className="h-3 w-3" />
                                                                {copiedId === `${inspectorCase._case_key}-stderr` ? 'Copied' : 'Copy Error'}
                                                            </button>
                                                        </div>
                                                        <pre className="max-h-64 overflow-auto rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 whitespace-pre-wrap">
                                                            {runOutput.stderr}
                                                        </pre>
                                                    </div>
                                                )}

                                                {Array.isArray(runOutput?.screenshots) && runOutput.screenshots.length > 0 && (
                                                    <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                                                        <p className="text-sm font-semibold text-sky-900">Screenshots ({runOutput.screenshots.length})</p>
                                                        <div className="mt-2 grid gap-3 sm:grid-cols-2">
                                                            {runOutput.screenshots.map((shot, index) => (
                                                                <RunScreenshotCard
                                                                    key={`${inspectorCase._case_key}-shot-${index}`}
                                                                    shot={shot}
                                                                    index={index}
                                                                    idPrefix={inspectorCase._case_key}
                                                                    copiedId={copiedId}
                                                                    copyToClipboard={copyToClipboard}
                                                                    compact
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {Array.isArray(runOutput?.videos) && runOutput.videos.length > 0 && (
                                                    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                                                        <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-900">
                                                            <Video className="h-4 w-4" />
                                                            Execution recordings ({runOutput.videos.length})
                                                        </p>
                                                        <div className="mt-2 grid gap-3 sm:grid-cols-2">
                                                            {runOutput.videos.map((video, index) => (
                                                                <RunVideoCard key={`${inspectorCase._case_key}-video-${index}`} video={video} index={index} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {activeInspectorTab === 'metadata' && (
                                        <div className="space-y-3">
                                            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                                {[
                                                    ['Role', inspectorCase.User_Role],
                                                    ['Priority', inspectorCase.priority],
                                                    ['Category', inspectorCase._category],
                                                    ['Module', inspectorCase.module],
                                                ].filter(([, value]) => value).map(([label, value]) => (
                                                    <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
                                                        <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
                                                    </div>
                                                ))}
                                            </div>
                                            {(inspectorCase.test_steps || inspectorCase.tab_flows) && (
                                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                                    <p className="text-sm font-semibold text-slate-900">Test Steps</p>
                                                    <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                                                        {Array.isArray(inspectorCase.test_steps) ? inspectorCase.test_steps.join('\n') : inspectorCase.test_steps || inspectorCase.tab_flows}
                                                    </p>
                                                </div>
                                            )}
                                            {inspectorCase.expected_result && (
                                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                                    <p className="text-sm font-semibold text-slate-900">Expected Result</p>
                                                    <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{inspectorCase.expected_result}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {errors[inspectorCase._case_key] && (
                                        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
                                            <p className="text-sm font-semibold text-red-800">Test case could not be processed</p>
                                            <p className="mt-1 text-sm text-red-700">{resolveCaughtErrorMessage(
                                                errors[inspectorCase._case_key],
                                                'This test case could not be processed. Review its script and page-object selections, then try again.'
                                            )}</p>
                                        </div>
                                    )}

                                    <MissingPomElementsPanel
                                        report={pomCoverageByCaseKey[inspectorCase._case_key]}
                                        isChecking={checkingPomCoverage.has(inspectorCase._case_key)}
                                        onRecheck={() => {
                                            checkPomCoverageForCase(inspectorCase).catch((err) => {
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    [inspectorCase._case_key]: resolveCaughtErrorMessage(
                                                        err,
                                                        'Page-object coverage could not be checked. Confirm the selected POM files are available, then try again.'
                                                    ),
                                                }));
                                            });
                                        }}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="p-8 text-center">
                                <FileCode className="mx-auto h-8 w-8 text-slate-400" />
                                <p className="mt-3 text-sm font-semibold text-slate-900">Select a test case</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {false && (
            <div className="hidden">
                {testCases.length === 0 ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
                        <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm text-yellow-800 font-medium">No test cases available</p>
                            <p className="text-sm text-yellow-600 mt-1">
                                {categoryFilter === 'all'
                                    ? 'Generate test cases first to create automation scripts.'
                                    : `No ${getFilterDisplayLabel(categoryFilter)} test cases available. Try selecting a different category.`}
                            </p>
                        </div>
                    </div>
                ) : (
                    testCases.map((testCase) => (
                        <div
                            key={testCase._case_key}
                            className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                        >
                            {/* Case Header */}
                            <button
                                onClick={() => setExpandedCase(
                                    expandedCase === testCase._case_key ? null : testCase._case_key
                                )}
                                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-center gap-3 flex-1 text-left">
                                    <input
                                        type="checkbox"
                                        checked={selectedCases.has(testCase._case_key)}
                                        disabled={!selectedCases.has(testCase._case_key) && isSelectionAtLimit}
                                        title={
                                            !selectedCases.has(testCase._case_key) && isSelectionAtLimit
                                                ? `Select up to ${MAX_SCRIPT_GENERATION_CASES} test cases for script generation.`
                                                : 'Select this test case'
                                        }
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            setSelectedCases(prev => {
                                                const next = new Set(prev);
                                                if (next.has(testCase._case_key)) {
                                                    next.delete(testCase._case_key);
                                                    setErrors(current => ({ ...current, bulk: null }));
                                                } else {
                                                    if (next.size >= MAX_SCRIPT_GENERATION_CASES) {
                                                        setErrors(current => ({
                                                            ...current,
                                                            bulk: `You can select up to ${MAX_SCRIPT_GENERATION_CASES} test cases for script generation.`
                                                        }));
                                                        return next;
                                                    }
                                                    next.add(testCase._case_key);
                                                    setErrors(current => ({ ...current, bulk: null }));
                                                }
                                                return next;
                                            });
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-4 w-4 accent-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                                    />
                                    <FileCode className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-semibold text-gray-900">
                                                {testCase.test_case_no}
                                            </p>
                                            {testCase._category && (
                                                <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${getCategoryBadgeClass(testCase._category)}`}>
                                                    {getCategoryLabel(testCase._category)}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {testCase.test_description || testCase.test_name || 'Test Case'}
                                        </p>
                                        {(() => {
                                            const caseSelection = getPomSelectionForCaseKey(testCase._case_key);
                                            const casePoms = caseSelection.poms;
                                            if (!pomSelectionsLoaded) {
                                                return (
                                                    <div className="mt-2 flex items-center gap-2 text-xs font-medium text-slate-500">
                                                        <Loader className="h-3.5 w-3.5 animate-spin text-amber-700" />
                                                        Loading POMs...
                                                    </div>
                                                );
                                            }
                                            if (!casePoms.length) {
                                                return (
                                                    <p className="mt-2 text-xs font-medium text-amber-700">
                                                        No POM selected
                                                    </p>
                                                );
                                            }
                                            return (
                                                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                                    <span className="text-xs font-medium text-slate-500">
                                                        {caseSelection.mode === 'auto' ? 'Auto POMs:' : 'POMs:'}
                                                    </span>
                                                    {casePoms.slice(0, 3).map((pom) => (
                                                        <span
                                                            key={`${testCase._case_key}-header-${buildPomSelectionKey(pom)}`}
                                                            className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-700"
                                                        >
                                                            {pomScopePrefixLabel(pom)}{pom.pageName}
                                                        </span>
                                                    ))}
                                                    {casePoms.length > 3 && (
                                                        <span className="text-xs text-slate-500">+{casePoms.length - 3}</span>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                                {expandedCase === testCase._case_key ? (
                                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                ) : (
                                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                )}
                            </button>

                            {/* Expanded Content */}
                            {expandedCase === testCase._case_key && (
                                <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-4">
                                    {/* Test Case Details */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-white rounded p-3 border border-gray-200">
                                        {testCase.User_Role && (
                                            <div>
                                                <span className="text-gray-600 font-medium">Role:</span>
                                                <p className="text-gray-900 font-semibold text-xs mt-1">{testCase.User_Role}</p>
                                            </div>
                                        )}
                                        {testCase.priority && (
                                            <div>
                                                <span className="text-gray-600 font-medium">Priority:</span>
                                                <p className="text-gray-900 font-semibold text-xs mt-1">{testCase.priority}</p>
                                            </div>
                                        )}
                                        {testCase._category && (
                                            <div>
                                                <span className="text-gray-600 font-medium">Category:</span>
                                                <p className="text-gray-900 font-semibold text-xs mt-1 capitalize">{testCase._category}</p>
                                            </div>
                                        )}
                                        {testCase.module && (
                                            <div>
                                                <span className="text-gray-600 font-medium">Module:</span>
                                                <p className="text-gray-900 font-semibold text-xs mt-1">{testCase.module}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Test Steps and Expected Result */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        {(testCase.test_steps || testCase.tab_flows) && (
                                            <div className="bg-white rounded p-3 border border-gray-200">
                                                <p className="font-semibold text-gray-900 mb-2">Test Steps:</p>
                                                <p className="text-gray-700 whitespace-pre-wrap text-xs">
                                                    {Array.isArray(testCase.test_steps)
                                                        ? testCase.test_steps.join('\n')
                                                        : testCase.test_steps || testCase.tab_flows}
                                                </p>
                                            </div>
                                        )}
                                        {testCase.expected_result && (
                                            <div className="bg-white rounded p-3 border border-gray-200">
                                                <p className="font-semibold text-gray-900 mb-2">Expected Result:</p>
                                                <p className="text-gray-700 whitespace-pre-wrap text-xs">
                                                    {testCase.expected_result}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    {(() => {
                                        const resolvedSets = resolveUserInputSets(testCase);
                                        if (!Array.isArray(resolvedSets) || resolvedSets.length === 0) {
                                            return null;
                                        }
                                        const hasAnyInputs = resolvedSets.some(
                                            (setItem) => Array.isArray(setItem?.user_inputs) && setItem.user_inputs.length > 0
                                        );
                                        if (!hasAnyInputs) {
                                            return null;
                                        }
                                        return (
                                            <div className="bg-white rounded p-3 border border-gray-200">
                                                <p className="font-semibold text-gray-900 mb-2">Test Input Sets ({resolvedSets.length})</p>
                                                <div className="space-y-2">
                                                    {resolvedSets.map((setItem, setIndex) => {
                                                        const setLabel = String(setItem?.set_name || `Set ${setIndex + 1}`).trim() || `Set ${setIndex + 1}`;
                                                        const setType = String(setItem?.set_type || '').trim();
                                                        const setInputs = normalizeUserInputList(setItem?.user_inputs || []);
                                                        return (
                                                            <div
                                                                key={`${testCase._case_key}-input-set-${setIndex}`}
                                                                className="rounded border border-gray-200 bg-slate-50 p-2"
                                                            >
                                                                <p className="text-xs font-semibold text-slate-800 mb-1">
                                                                    {setLabel}
                                                                    {setType ? ` (${setType})` : ''}
                                                                </p>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                    {setInputs.map((item, idx) => (
                                                                        <div key={`${testCase._case_key}-input-${setIndex}-${idx}`} className="text-xs text-gray-700">
                                                                            <span className="font-semibold text-gray-900">{item?.field || 'Field'}:</span>{' '}
                                                                            {item?.value || ''}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {(() => {
                                        const resolvedSets = resolveUserInputSets(testCase);
                                        const setCount = resolvedSets.length || 1;
                                        return (
                                            <div className="text-xs text-slate-600">
                                                Script run will execute once per input set ({setCount} run{setCount === 1 ? '' : 's'}).
                                            </div>
                                        );
                                    })()}

                                    {!isApiScriptFlow && (() => {
                                        const caseSpecificSelection = getCaseSpecificPomSelectionForCaseKey(testCase._case_key);
                                        const caseSpecificPoms = caseSpecificSelection.poms;
                                        const caseSelection = getPomSelectionForCaseKey(testCase._case_key);
                                        const casePoms = caseSelection.poms;
                                        const isAutoLoading = loadingPomRecommendations.has(testCase._case_key);
                                        return (
                                            <div className="rounded-lg border border-amber-200 bg-white p-3">
                                                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <BookOpen className="h-4 w-4 text-amber-700" />
                                                            <p className="text-sm font-semibold text-slate-900">
                                                                {isFeatureMode ? 'Attached POMs' : 'POM Selection'}
                                                            </p>
                                                            {caseSpecificSelection.mode === 'auto' && (
                                                                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                                                                    Auto
                                                                </span>
                                                            )}
                                                            {caseSpecificSelection.mode === 'manual_all' && (
                                                                <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                                                                    Applied to all
                                                                </span>
                                                            )}
                                                            {commonPomSelections.length > 0 && (
                                                                <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-xs font-semibold text-cyan-700">
                                                                    Common
                                                                </span>
                                                            )}
                                                        </div>
                                                        {!pomSelectionsLoaded ? (
                                                            <div className="mt-2 flex items-center gap-2 text-xs font-medium text-slate-500">
                                                                <Loader className="h-3.5 w-3.5 animate-spin text-amber-700" />
                                                                Restoring saved POM selection...
                                                            </div>
                                                        ) : casePoms.length === 0 ? (
                                                            <p className="mt-2 text-xs text-amber-700">
                                                                {isFeatureMode
                                                                    ? 'Captured page objects will be attached automatically.'
                                                                    : 'Select POMs before generating this script.'}
                                                            </p>
                                                        ) : (
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {casePoms.map((pom) => (
                                                                    <span
                                                                        key={`${testCase._case_key}-pom-${buildPomSelectionKey(pom)}`}
                                                                        className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700"
                                                                        title={pom.reason || ''}
                                                                    >
                                                                        {pomScopePrefixLabel(pom)}
                                                                        {pom.pageName}
                                                                        {Number.isFinite(Number(pom.score)) ? ` · ${Math.round(Number(pom.score) * 100)}%` : ''}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {!isFeatureMode && (
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => autoSelectPomsForCase(testCase)}
                                                                disabled={!pomSelectionsLoaded || isAutoLoading}
                                                                className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                            >
                                                                {isAutoLoading ? (
                                                                    <Loader className="h-3.5 w-3.5 animate-spin" />
                                                                ) : (
                                                                    <Wand2 className="h-3.5 w-3.5" />
                                                                )}
                                                                {caseSpecificPoms.length ? 'Refresh' : 'Auto'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleOpenPomModal({
                                                                    type: 'case',
                                                                    caseKey: testCase._case_key,
                                                                    testCaseId: getResolvedTestCaseId(testCase),
                                                                })}
                                                                disabled={!pomSelectionsLoaded}
                                                                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                            >
                                                                <Pencil className="h-3.5 w-3.5" />
                                                                Change
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => resetPomSelectionForCase(testCase)}
                                                                disabled={!pomSelectionsLoaded || caseSpecificPoms.length === 0}
                                                                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                            >
                                                                <RotateCcw className="h-3.5 w-3.5" />
                                                                Reset
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Generate Script Button */}
                                    {!generatedScripts[testCase._case_key] ? (
                                        <button
                                            onClick={() => generateScriptForCase(testCase)}
                                            disabled={!pomSelectionsLoaded || loadingCases.has(testCase._case_key)}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {loadingCases.has(testCase._case_key) ? (
                                                <>
                                                    <Loader className="w-4 h-4 animate-spin" />
                                                    Generating Script...
                                                </>
                                            ) : (
                                                <>
                                                    <Play className="w-4 h-4" />
                                                    Generate {getScriptTypeLabel(scriptType).split('(')[0].trim()} Script
                                                </>
                                            )}
                                        </button>
                                    ) : (
                                        <div className="space-y-3">
                                            {getScriptStaleMeta(testCase._case_key).isStale && (
                                                <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                                                    <div className="flex gap-2 text-sm text-amber-800">
                                                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                                        <p>{getScriptStaleMeta(testCase._case_key).message}</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={async () => {
                                                            setEditModes(prev => {
                                                                const next = new Set(prev);
                                                                next.delete(testCase._case_key);
                                                                return next;
                                                            });
                                                            setRunResults(prev => {
                                                                const next = { ...prev };
                                                                delete next[testCase._case_key];
                                                                return next;
                                                            });
                                                            setRunErrors(prev => {
                                                                const next = { ...prev };
                                                                delete next[testCase._case_key];
                                                                return next;
                                                            });
                                                            await generateScriptForCase(testCase);
                                                        }}
                                                        disabled={!pomSelectionsLoaded || loadingCases.has(testCase._case_key)}
                                                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        {loadingCases.has(testCase._case_key) ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                                                        Regenerate
                                                    </button>
                                                </div>
                                            )}
                                            {/* Script Display First */}
                                            <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-slate-500 text-xs font-semibold tracking-wider">PLAYWRIGHT SCRIPT</span>
                                                </div>
                                                {editModes.has(testCase._case_key) ? (
                                                    <CodeEditorPreview
                                                        value={generatedScripts[testCase._case_key]}
                                                        editable
                                                        minHeight={240}
                                                        maxHeight={384}
                                                        onChange={(value) => {
                                                            setGeneratedScripts(prev => ({
                                                                ...prev,
                                                                [testCase._case_key]: value
                                                            }));
                                                            setDirtyScriptKeys(prev => new Set([...prev, testCase._case_key]));
                                                        }}
                                                    />
                                                ) : (
                                                    <CodeEditorPreview
                                                        value={generatedScripts[testCase._case_key]}
                                                        minHeight={240}
                                                        maxHeight={384}
                                                    />
                                                )}
                                            </div>

                                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex flex-wrap items-center justify-between gap-3 text-xs">
                                                <div className="text-slate-600">
                                                    {savingScriptKeys.has(testCase._case_key) ? (
                                                        <span className="font-medium text-blue-700">
                                                            Saving script...
                                                        </span>
                                                    ) : scriptSaveErrors[testCase._case_key] ? (
                                                        <span className="font-medium text-red-700">
                                                            {resolveCaughtErrorMessage(scriptSaveErrors[testCase._case_key], 'Failed to save script')}
                                                        </span>
                                                    ) : scriptSaveMeta[testCase._case_key]?.version ? (
                                                        <span className="font-medium text-emerald-700">
                                                            Saved script
                                                        </span>
                                                    ) : (
                                                        <span>No script yet.</span>
                                                    )}
                                                    {scriptSaveMeta[testCase._case_key]?.updated_at && (
                                                        <span className="ml-2 text-slate-500">
                                                            Saved {new Date(scriptSaveMeta[testCase._case_key].updated_at).toLocaleString()}
                                                        </span>
                                                    )}
                                                    {scriptSaveMeta[testCase._case_key]?.draft_updated_at && (
                                                        <span className="ml-2 text-slate-500">
                                                            Draft {new Date(scriptSaveMeta[testCase._case_key].draft_updated_at).toLocaleString()}
                                                        </span>
                                                    )}
                                                    {scriptSaveMeta[testCase._case_key]?.edited_by && (
                                                        <span className="ml-2 text-slate-500">
                                                            Edited by {
                                                                resolveActorLabel(scriptSaveMeta[testCase._case_key].edited_by)
                                                                || 'Unknown'
                                                            }
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {dirtyScriptKeys.has(testCase._case_key) && !savingScriptKeys.has(testCase._case_key) && (
                                                        <span className="text-amber-700 font-medium">Unsaved local edits</span>
                                                    )}
                                                    {!isFeatureMode && VERSIONING_ENABLED && (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleVersionPanel(testCase)}
                                                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-700 hover:bg-slate-100"
                                                    >
                                                        <History className="w-3.5 h-3.5" />
                                                        Versions
                                                    </button>
                                                    )}
                                                </div>
                                            </div>

                                            {!isFeatureMode && VERSIONING_ENABLED && versionPanels.has(testCase._case_key) && (
                                                <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
                                                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                                                        Saved Versions
                                                    </p>
                                                    {(scriptVersionsByCaseId[getResolvedTestCaseId(testCase)] || []).length === 0 ? (
                                                        <p className="text-sm text-slate-500">No saved versions yet for this script.</p>
                                                    ) : (
                                                        (scriptVersionsByCaseId[getResolvedTestCaseId(testCase)] || []).map((artifact) => {
                                                            const artifactEditorMeta = resolveScriptEditorMeta(artifact);
                                                            return (
                                                                <div
                                                                    key={artifact.id || artifact.version}
                                                                    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 flex flex-wrap items-center justify-between gap-3"
                                                                >
                                                                    <div>
                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                            <p className="text-sm font-semibold text-slate-900">v{artifact.version}</p>
                                                                            {String(artifact?.status || '').toLowerCase() === 'stale' && (
                                                                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                                                                                    Needs regeneration
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <p className="mt-1 text-xs text-slate-500">
                                                                            {artifact.updated_at ? new Date(artifact.updated_at).toLocaleString() : 'Unknown time'}
                                                                            {artifact?.request_payload?.save_mode
                                                                                ? ` · ${String(artifact.request_payload.save_mode).replace(/_/g, ' ')}`
                                                                                : ''}
                                                                            {artifactEditorMeta.edited_by
                                                                                ? ` · Edited by ${artifactEditorMeta.edited_by}`
                                                                                : ''}
                                                                        </p>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const nextScript = String(artifact?.payload?.playwright_script || '').trim();
                                                                            setGeneratedScripts((prev) => ({
                                                                                ...prev,
                                                                                [testCase._case_key]: nextScript,
                                                                            }));
                                                                            setScriptAssumptions((prev) => ({
                                                                                ...prev,
                                                                                [testCase._case_key]: Array.isArray(artifact?.payload?.assumptions) ? artifact.payload.assumptions : [],
                                                                            }));
                                                                            setScriptWarnings((prev) => ({
                                                                                ...prev,
                                                                                [testCase._case_key]: Array.isArray(artifact?.payload?.warnings) ? artifact.payload.warnings : [],
                                                                            }));
                                                                            setScriptModels((prev) => ({
                                                                                ...prev,
                                                                                [testCase._case_key]: artifact?.payload?.model || null,
                                                                            }));
                                                                            setScriptSaveMeta((prev) => ({
                                                                                ...prev,
                                                                                [testCase._case_key]: {
                                                                                    version: artifact?.version ?? null,
                                                                                    status: artifact?.status || null,
                                                                                    updated_at: artifact?.updated_at || null,
                                                                                    save_mode: artifact?.request_payload?.save_mode || 'history',
                                                                                    draft_updated_at: null,
                                                                                    requires_regeneration: Boolean(artifact?.request_payload?.requires_regeneration || artifact?.status === 'stale'),
                                                                                    stale_reason: artifact?.request_payload?.stale_reason || null,
                                                                                    missing_poms: Array.isArray(artifact?.request_payload?.missing_poms)
                                                                                        ? artifact.request_payload.missing_poms
                                                                                        : [],
                                                                                    edited_by: artifactEditorMeta.edited_by,
                                                                                    edited_at: artifactEditorMeta.edited_at || artifact?.updated_at || null,
                                                                                },
                                                                            }));
                                                                            setDirtyScriptKeys((prev) => {
                                                                                const next = new Set(prev);
                                                                                next.delete(testCase._case_key);
                                                                                return next;
                                                                            });
                                                                        }}
                                                                        className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                                                                    >
                                                                        Load
                                                                    </button>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            )}

                                            {/* Assumptions & Warnings Collapsible Section */}
                                            {(scriptAssumptions[testCase._case_key]?.length > 0 || scriptWarnings[testCase._case_key]?.length > 0) && (
                                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                    <button
                                                        onClick={() => setExpandedDetails(
                                                            expandedDetails === testCase._case_key ? null : testCase._case_key
                                                        )}
                                                        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors bg-gray-50"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <FileCode className="w-5 h-5 text-gray-600" />
                                                            <div className="text-left">
                                                                <p className="font-semibold text-gray-900 text-sm">Script Details & Analysis</p>
                                                                <p className="text-xs text-gray-600 mt-0.5">
                                                                    {(scriptAssumptions[testCase._case_key]?.length || 0) + (scriptWarnings[testCase._case_key]?.length || 0)} items
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {expandedDetails === testCase._case_key ? (
                                                            <ChevronUp className="w-5 h-5 text-gray-400" />
                                                        ) : (
                                                            <ChevronDown className="w-5 h-5 text-gray-400" />
                                                        )}
                                                    </button>

                                                    {expandedDetails === testCase._case_key && (
                                                        <div className="border-t border-gray-200 p-4 space-y-4 bg-white">
                                                            {/* Assumptions Section */}
                                                            {scriptAssumptions[testCase._case_key] && scriptAssumptions[testCase._case_key].length > 0 && (
                                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                    <div className="flex items-start gap-3">
                                                                        <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                                                                            <Lightbulb className="w-5 h-5 text-blue-600" />
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="font-semibold text-blue-900 text-sm">Assumptions ({scriptAssumptions[testCase._case_key].length})</p>
                                                                            <ul className="mt-3 space-y-2.5">
                                                                                {scriptAssumptions[testCase._case_key].map((assumption, idx) => (
                                                                                    <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                                                                                        <span className="text-blue-600 font-bold mt-1">•</span>
                                                                                        <span className="leading-relaxed">{normalizeErrorDetail(assumption)}</span>
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Warnings Section */}
                                                            {scriptWarnings[testCase._case_key] && scriptWarnings[testCase._case_key].length > 0 && (
                                                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                                                    <div className="flex items-start gap-3">
                                                                        <div className="bg-amber-100 p-2 rounded-lg flex-shrink-0">
                                                                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="font-semibold text-amber-900 text-sm">Warnings ({scriptWarnings[testCase._case_key].length})</p>
                                                                            <ul className="mt-3 space-y-2.5">
                                                                                {scriptWarnings[testCase._case_key].map((warning, idx) => (
                                                                                    <li key={idx} className="text-sm text-amber-800 flex items-start gap-2">
                                                                                        <span className="text-amber-600 text-lg">⚠️</span>
                                                                                        <span className="leading-relaxed">{normalizeErrorDetail(warning)}</span>
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Script Actions */}
                                            <div className="flex gap-2 flex-wrap">
                                                <button
                                                    onClick={() => {
                                                        if (editModes.has(testCase._case_key)) {
                                                            requestScriptSave(testCase);
                                                            return;
                                                        }
                                                        setEditModes(prev => {
                                                            const next = new Set(prev);
                                                            next.add(testCase._case_key);
                                                            return next;
                                                        });
                                                    }}
                                                    disabled={!generatedScripts[testCase._case_key] || savingScriptKeys.has(testCase._case_key)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors font-medium text-sm disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    {savingScriptKeys.has(testCase._case_key) ? (
                                                        <Loader className="w-4 h-4 animate-spin" />
                                                    ) : editModes.has(testCase._case_key) ? (
                                                        <Save className="w-4 h-4" />
                                                    ) : (
                                                        <Pencil className="w-4 h-4" />
                                                    )}
                                                    {editModes.has(testCase._case_key) ? 'Save' : 'Edit'}
                                                </button>

                                                <button
                                                    onClick={() => copyToClipboard(
                                                        generatedScripts[testCase._case_key],
                                                        testCase._case_key
                                                    )}
                                                    className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-medium text-sm"
                                                >
                                                    <Copy className="w-4 h-4" />
                                                    {copiedId === testCase._case_key ? 'Copied!' : 'Copy'}
                                                </button>

                                                <button
                                                    onClick={() => downloadScript(
                                                        generatedScripts[testCase._case_key],
                                                        testCase.test_case_no || testCase._case_key
                                                    )}
                                                    className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium text-sm"
                                                >
                                                    <Download className="w-4 h-4" />
                                                    Download
                                                </button>

                                                {!isApiScriptFlow && (
                                                    <button
                                                        onClick={() => toggleAssociatedPoms(testCase)}
                                                        disabled={loadingAssociatedPoms.has(testCase._case_key)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {loadingAssociatedPoms.has(testCase._case_key) ? (
                                                            <Loader className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <BookOpen className="w-4 h-4" />
                                                        )}
                                                        POM Files
                                                    </button>
                                                )}

                                                <button
                                                    onClick={() => runScriptForCase(testCase)}
                                                    disabled={runningCases.has(testCase._case_key) || getScriptStaleMeta(testCase._case_key).isStale}
                                                    title={getScriptStaleMeta(testCase._case_key).isStale ? 'Regenerate this script before running.' : ''}
                                                    className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {runningCases.has(testCase._case_key) ? (
                                                        <>
                                                            <Loader className="w-4 h-4 animate-spin" />
                                                            Running Sets...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Play className="w-4 h-4" />
                                                            Run All Sets
                                                        </>
                                                    )}
                                                </button>

                                                <button
                                                    onClick={async () => {
                                                        setEditModes(prev => {
                                                            const next = new Set(prev);
                                                            next.delete(testCase._case_key);
                                                            return next;
                                                        });
                                                        setRunResults(prev => {
                                                            const next = { ...prev };
                                                            delete next[testCase._case_key];
                                                            return next;
                                                        });
                                                        setRunErrors(prev => {
                                                            const next = { ...prev };
                                                            delete next[testCase._case_key];
                                                            return next;
                                                        });
                                                        await generateScriptForCase(testCase);
                                                    }}
                                                    disabled={!pomSelectionsLoaded || loadingCases.has(testCase._case_key)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {loadingCases.has(testCase._case_key) ? (
                                                        <>
                                                            <Loader className="w-4 h-4 animate-spin" />
                                                            Regenerating...
                                                        </>
                                                    ) : (
                                                        'Regenerate'
                                                    )}
                                                </button>
                                            </div>

                                            {associatedPomPanels.has(testCase._case_key) && (
                                                <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div>
                                                            <p className="text-sm font-semibold text-slate-900">Associated POM Files</p>
                                                            <p className="text-xs text-slate-500 mt-0.5">
                                                                Files used when this script was generated.
                                                            </p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleAssociatedPoms(testCase)}
                                                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                                                        >
                                                            Hide
                                                        </button>
                                                    </div>
                                                    {associatedPomErrors[testCase._case_key] && !loadingAssociatedPoms.has(testCase._case_key) && (
                                                        <p className="text-sm font-medium text-red-700">
                                                            {associatedPomErrors[testCase._case_key]}
                                                        </p>
                                                    )}
                                                    {(() => {
                                                        const caseSelection = getPomSelectionForCaseKey(testCase._case_key);
                                                        const payload = associatedPomsByCaseKey[testCase._case_key] || {};
                                                        const poms = Array.isArray(payload.poms) ? payload.poms : [];
                                                        const warnings = Array.isArray(payload.warnings) ? payload.warnings : [];
                                                        const hasAssociatedPomPayload = Boolean(associatedPomsByCaseKey[testCase._case_key]);
                                                        const visiblePoms = hasAssociatedPomPayload ? poms : caseSelection.poms;
                                                        if (!visiblePoms.length && !warnings.length) {
                                                            return <p className="text-sm text-slate-500">No associated POM files found for this saved script.</p>;
                                                        }
                                                        return (
                                                            <div className="space-y-4">
                                                                {warnings.length > 0 && (
                                                                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                                                        {warnings.join(' ')}
                                                                    </div>
                                                                )}
                                                                {visiblePoms.map((pom, pomIndex) => {
                                                                    const code = String(pom.typescriptCode || '');
                                                                    const configText = typeof pom.configText === 'string'
                                                                        ? pom.configText
                                                                        : stringifyAssociatedPomConfig(pom.config);
                                                                    const viewerKey = buildAssociatedPomViewerKey(testCase._case_key, pom, pomIndex);
                                                                    const isTypeScriptOpen = openAssociatedPomTypeScriptPanels.has(viewerKey);
                                                                    return (
                                                                        <div
                                                                            key={`${testCase._case_key}-associated-pom-${pom.scope || 'local'}-${pom.pageName || pomIndex}`}
                                                                            className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden"
                                                                        >
                                                                            <div className="px-3 py-2 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
                                                                                <div>
                                                                                    <p className="text-sm font-semibold text-slate-900">
                                                                                        {pomScopePrefixLabel(pom)}{pom.pageName || 'POM'}
                                                                                    </p>
                                                                                    <p className="text-xs text-slate-500 break-all">
                                                                                        {pom.pageUrl || 'No page URL saved'}
                                                                                    </p>
                                                                                </div>
                                                                                <div className="flex flex-wrap items-center gap-2">
                                                                                    {canEditAssociatedPomFile(pom) ? (
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => beginAssociatedPomFileEdit(testCase, pom, pomIndex)}
                                                                                            className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                                                                        >
                                                                                            <Pencil className="h-3.5 w-3.5" />
                                                                                            Edit Files
                                                                                        </button>
                                                                                    ) : pom.scope === 'application' ? (
                                                                                        <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-500" title="Edit this in Application &gt; Manage POMs">Shared, read-only here</span>
                                                                                    ) : pom.scope === 'global' ? (
                                                                                        <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-500">Global read-only</span>
                                                                                    ) : null}
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => copyToClipboard(code || configText, `${testCase._case_key}-pom-${pomIndex}`)}
                                                                                        disabled={!hasAssociatedPomPayload}
                                                                                        className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                                                    >
                                                                                        {copiedId === `${testCase._case_key}-pom-${pomIndex}` ? 'Copied!' : 'Copy POM'}
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                            <div className="space-y-3 p-3">
                                                                                {renderAssociatedPomFileEditor(testCase, pom, pomIndex, { minHeight: 220, maxHeight: 360 })}
                                                                                <CollapsibleAssociatedPomTypeScript
                                                                                    isOpen={isTypeScriptOpen}
                                                                                    isLoading={loadingAssociatedPoms.has(testCase._case_key)}
                                                                                    error={associatedPomErrors[testCase._case_key]}
                                                                                    code={hasAssociatedPomPayload ? code : ''}
                                                                                    minHeight={180}
                                                                                    maxHeight={288}
                                                                                    onToggle={() => toggleAssociatedPomTypeScript(testCase, pom, pomIndex)}
                                                                                />
                                                                                {hasAssociatedPomPayload && (
                                                                                    <details className="rounded-lg border border-slate-200 bg-white">
                                                                                        <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
                                                                                            Config JSON
                                                                                        </summary>
                                                                                        <div className="border-t border-slate-200 p-3">
                                                                                            <AssociatedPomCodePreview
                                                                                                value={configText}
                                                                                                language="json"
                                                                                                minHeight={180}
                                                                                                maxHeight={288}
                                                                                            />
                                                                                        </div>
                                                                                    </details>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            )}

                                            {/* Run Result */}
                                            {runResults[testCase._case_key] && (() => {
                                                const runOutput = runResults[testCase._case_key];
                                                const runEntries = Array.isArray(runOutput?.runs) && runOutput.runs.length > 0
                                                    ? runOutput.runs
                                                    : [runOutput];
                                                const totalSets = Number(runOutput?.total_sets || runEntries.length || 0);
                                                const passedSets = Number(
                                                    runOutput?.passed_sets ?? runEntries.filter((entry) => resolveExitCode(entry?.exit_code) === 0).length
                                                );
                                                const failedSets = Number(
                                                    runOutput?.failed_sets ?? Math.max(0, totalSets - passedSets)
                                                );
                                                const htmlReports = Array.isArray(runOutput?.html_reports)
                                                    ? runOutput.html_reports
                                                    : runEntries
                                                        .map((entry) => resolveRunHtmlReport(entry))
                                                        .filter((item) => item && getHtmlReportUrl(item));

                                                return (
                                                    <div className="bg-white rounded-lg border border-gray-200 p-3 text-sm space-y-2">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <div className="flex items-center gap-2">
                                                                {runOutput.exit_code === 0 ? (
                                                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                                                ) : (
                                                                    <AlertCircle className="w-4 h-4 text-red-600" />
                                                                )}
                                                                <span className="font-semibold text-gray-900">
                                                                    {runOutput.exit_code === 0 ? 'Run Passed' : 'Run Failed'}
                                                                </span>
                                                                <span className="text-gray-500">
                                                                    ({runOutput.duration_ms} ms)
                                                                </span>
                                                                <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-700">
                                                                    Sets: {passedSets}/{totalSets} passed
                                                                </span>
                                                            </div>
                                                            <button
                                                                onClick={() => {
                                                                    const screenshotUrls = Array.isArray(runOutput?.screenshots)
                                                                        ? runOutput.screenshots
                                                                            .map((item) => String(item?.viewer_url || item?.presigned_url || '').trim())
                                                                            .filter(Boolean)
                                                                        : [];
                                                                    const reportUrls = htmlReports
                                                                        .map((item) => getHtmlReportUrl(item))
                                                                        .filter(Boolean);
                                                                    const message = [
                                                                        `Status: ${runOutput.exit_code === 0 ? 'Passed' : 'Failed'}`,
                                                                        `Sets: ${passedSets}/${totalSets} passed`,
                                                                        `Duration: ${runOutput.duration_ms} ms`,
                                                                        ...runEntries.map((entry) => {
                                                                            const parts = [
                                                                                '',
                                                                                `[${entry?.set_label || `Set ${(entry?.set_index ?? 0) + 1}`}]`,
                                                                                `Status: ${(resolveExitCode(entry?.exit_code) === 0) ? 'Passed' : 'Failed'}`,
                                                                                `Duration: ${Number(entry?.duration_ms || 0)} ms`,
                                                                                entry?.stdout ? `Stdout:\n${entry.stdout}` : '',
                                                                                entry?.stderr ? `Stderr:\n${entry.stderr}` : '',
                                                                            ];
                                                                            return parts.filter(Boolean).join('\n');
                                                                        }),
                                                                        screenshotUrls.length
                                                                            ? `\nScreenshot URLs:\n${screenshotUrls.join('\n')}`
                                                                            : '',
                                                                        reportUrls.length
                                                                            ? `\nHTML Report URLs:\n${reportUrls.join('\n')}`
                                                                            : '',
                                                                    ].filter(Boolean).join('\n');
                                                                    copyToClipboard(message, `${testCase._case_key}-run-output`);
                                                                }}
                                                                className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200"
                                                            >
                                                                <Copy className="h-3 w-3" />
                                                                {copiedId === `${testCase._case_key}-run-output` ? 'Copied!' : 'Copy Output'}
                                                            </button>
                                                        </div>

                                                        <div className="space-y-2">
                                                            {runEntries.map((entry, entryIndex) => {
                                                                const setLabel = entry?.set_label || `Set ${(entry?.set_index ?? entryIndex) + 1}`;
                                                                const isPassed = resolveExitCode(entry?.exit_code) === 0;
                                                                const entryReport = resolveRunHtmlReport(entry);
                                                                const entryReportUrl = getHtmlReportUrl(entryReport);
                                                                const entryReportExpiryLabel = entryReport?.viewer_url
                                                                    ? ''
                                                                    : formatExpiryDuration(Number(entryReport?.presigned_expires_in_seconds || 0));
                                                                const reportRefreshKey = `${testCase._case_key}-${entry?.run_id || 'missing'}-${entryIndex}`;
                                                                const isRefreshingReport = refreshingReportKeys.has(reportRefreshKey);
                                                                return (
                                                                    <div
                                                                        key={`${testCase._case_key}-run-set-${entry?.set_index ?? entryIndex}`}
                                                                        className="rounded border border-slate-200 bg-slate-50 p-2"
                                                                    >
                                                                        <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-xs">
                                                                            <div className="flex items-center gap-2">
                                                                                {isPassed ? (
                                                                                    <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                                                                ) : (
                                                                                    <AlertCircle className="w-3.5 h-3.5 text-red-600" />
                                                                                )}
                                                                                <span className="font-semibold text-slate-800">{setLabel}</span>
                                                                                <span className={isPassed ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                                                                                    {isPassed ? 'Passed' : 'Failed'}
                                                                                </span>
                                                                                <span className="text-slate-500">({Number(entry?.duration_ms || 0)} ms)</span>
                                                                            </div>
                                                                            {entryReportUrl && (
                                                                                <div className="flex flex-wrap items-center justify-end gap-1.5 rounded-lg border border-slate-200 bg-white px-1.5 py-1 shadow-sm">
                                                                                    {entryReportExpiryLabel && (
                                                                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                                                                                            <Clock3 className="h-3 w-3" />
                                                                                            {entryReportExpiryLabel}
                                                                                        </span>
                                                                                    )}
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => copyToClipboard(entryReportUrl, `${testCase._case_key}-html-report-url-${entryIndex}`)}
                                                                                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-100"
                                                                                    >
                                                                                        <Copy className="h-3 w-3" />
                                                                                        {copiedId === `${testCase._case_key}-html-report-url-${entryIndex}` ? 'Copied' : 'Copy Link'}
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => refreshHtmlReportLink(testCase._case_key, entry.run_id, entryIndex)}
                                                                                        disabled={!entry?.run_id || isRefreshingReport}
                                                                                        className="inline-flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-[11px] font-semibold text-violet-700 transition-colors hover:border-violet-300 hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
                                                                                    >
                                                                                        {isRefreshingReport ? (
                                                                                            <Loader className="h-3 w-3 animate-spin" />
                                                                                        ) : (
                                                                                            <Link2 className="h-3 w-3" />
                                                                                        )}
                                                                                        Refresh
                                                                                    </button>
                                                                                    <a
                                                                                        href={entryReportUrl}
                                                                                        target="_blank"
                                                                                        rel="noreferrer"
                                                                                        className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-violet-700"
                                                                                    >
                                                                                        <ExternalLink className="h-3 w-3" />
                                                                                        Open Report
                                                                                    </a>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {entry?.stdout && (
                                                                            <pre className="text-xs whitespace-pre-wrap text-gray-700 bg-white border border-gray-200 rounded p-2 max-h-32 overflow-y-auto">
                                                                                {entry.stdout}
                                                                            </pre>
                                                                        )}
                                                                        {entry?.stderr && (
                                                                            <pre className="text-xs whitespace-pre-wrap text-red-700 bg-red-50 border border-red-200 rounded p-2 max-h-32 overflow-y-auto mt-1">
                                                                                {entry.stderr}
                                                                            </pre>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        {failedSets > 0 && runOutput?.stderr && (
                                                            <div>
                                                                <div className="mb-1 flex items-center justify-between">
                                                                    <p className="text-xs font-semibold text-gray-600">Combined Error Log</p>
                                                                    <button
                                                                        onClick={() =>
                                                                            copyToClipboard(
                                                                                runOutput.stderr,
                                                                                `${testCase._case_key}-stderr`
                                                                            )
                                                                        }
                                                                        className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700 hover:bg-red-200"
                                                                    >
                                                                        <Copy className="h-3 w-3" />
                                                                        {copiedId === `${testCase._case_key}-stderr` ? 'Copied!' : 'Copy Error'}
                                                                    </button>
                                                                </div>
                                                                <pre className="text-xs whitespace-pre-wrap text-red-700 bg-red-50 border border-red-200 rounded p-2 max-h-40 overflow-y-auto">
                                                                    {runOutput.stderr}
                                                                </pre>
                                                            </div>
                                                        )}

                                                        {Array.isArray(runOutput?.screenshots) && runOutput.screenshots.length > 0 && (
                                                            <div className="rounded-lg border border-sky-200 bg-gradient-to-b from-sky-50 to-white p-3 space-y-3">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleScreenshotPanel(testCase._case_key)}
                                                                    className="w-full flex items-center justify-between gap-2 text-left"
                                                                >
                                                                    <div className="flex flex-wrap items-center gap-2">
                                                                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-900">
                                                                            <ImageIcon className="h-3.5 w-3.5" />
                                                                            Screenshots
                                                                        </span>
                                                                        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">
                                                                            {runOutput.screenshots.length}
                                                                        </span>
                                                                    </div>
                                                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700">
                                                                        {expandedScreenshotPanels.has(testCase._case_key) ? 'Collapse' : 'Expand'}
                                                                        {expandedScreenshotPanels.has(testCase._case_key)
                                                                            ? <ChevronUp className="h-3.5 w-3.5" />
                                                                            : <ChevronDown className="h-3.5 w-3.5" />}
                                                                    </span>
                                                                </button>
                                                                {expandedScreenshotPanels.has(testCase._case_key) && (
                                                                    <div className="grid gap-3 border-t border-sky-100 pt-3 lg:grid-cols-2">
                                                                        {runOutput.screenshots.map((shot, index) => (
                                                                            <RunScreenshotCard
                                                                                key={`${testCase._case_key}-shot-${index}`}
                                                                                shot={shot}
                                                                                index={index}
                                                                                idPrefix={testCase._case_key}
                                                                                copiedId={copiedId}
                                                                                copyToClipboard={copyToClipboard}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {Array.isArray(runOutput?.videos) && runOutput.videos.length > 0 && (
                                                            <div className="rounded-lg border border-indigo-200 bg-gradient-to-b from-indigo-50 to-white p-3">
                                                                <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-900">
                                                                    <Video className="h-3.5 w-3.5" />
                                                                    Execution recordings ({runOutput.videos.length})
                                                                </p>
                                                                <div className="mt-3 grid gap-3 lg:grid-cols-2">
                                                                    {runOutput.videos.map((video, index) => (
                                                                        <RunVideoCard key={`${testCase._case_key}-video-${index}`} video={video} index={index} />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            {runErrors[testCase._case_key] && (() => {
                                                const runErrorMessage = resolveCaughtErrorMessage(
                                                    runErrors[testCase._case_key],
                                                    'Failed to run script'
                                                );
                                                return (
                                                    <div className="p-3 bg-red-50 border border-red-200 rounded flex gap-2">
                                                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                                                        <div className="w-full">
                                                            <div className="flex items-center justify-between gap-2">
                                                                <p className="text-sm text-red-800 font-medium">Run Error</p>
                                                                <button
                                                                    onClick={() =>
                                                                        copyToClipboard(
                                                                            runErrorMessage,
                                                                            `${testCase._case_key}-run-error`
                                                                        )
                                                                    }
                                                                    className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700 hover:bg-red-200"
                                                                >
                                                                    <Copy className="h-3 w-3" />
                                                                    {copiedId === `${testCase._case_key}-run-error` ? 'Copied!' : 'Copy Error'}
                                                                </button>
                                                            </div>
                                                            <p className="text-sm text-red-600 mt-1">{runErrorMessage}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}

                                    {/* Error Display */}
                                    {errors[testCase._case_key] && (
                                        <div className="p-3 bg-red-50 border border-red-200 rounded flex gap-2">
                                            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm text-red-800 font-medium">Script action could not be completed</p>
                                                <p className="text-sm text-red-600 mt-1">
                                                    {resolveCaughtErrorMessage(
                                                        errors[testCase._case_key],
                                                        'Review this test case’s script and page-object selections, then try again.'
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-6 border-t border-slate-200">
                {showBackButton && onBack ? (
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 px-6 py-2 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                    >
                        ← Back
                    </button>
                ) : <span />}

                <button
                    onClick={onComplete}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg hover:shadow-lg transition-all font-semibold ${scriptTheme.primaryButton}`}
                >
                    <CheckCircle className="w-4 h-4" />
                    Complete
                </button>
            </div>

            {pomRecommendationBusy && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/45 backdrop-blur-sm">
                    <div
                        className="w-[min(90vw,360px)] rounded-xl border border-white/30 bg-white p-5 text-center shadow-2xl"
                        role="status"
                        aria-live="polite"
                    >
                        <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${scriptTheme.iconBadge}`}>
                            <Loader className="h-6 w-6 animate-spin" />
                        </div>
                        <p className="mt-4 text-sm font-semibold text-slate-900">
                            {pomRecommendationOverlayTitle}
                        </p>
                        <p className="mt-1 text-xs text-slate-600">
                            {pomRecommendationOverlayText} Please wait until this finishes.
                        </p>
                        <button
                            type="button"
                            onClick={cancelPomRecommendation}
                            disabled={pomRecommendationCancelling}
                            className="mt-4 inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {pomRecommendationCancelling ? 'Cancelling...' : 'Cancel'}
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
